import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { Kanban, dropColumnHandler, dropHandler } from 'react-kanban-kit';
import { writeCsvObjects } from 'hucre/csv';
import './tasks-kanban.css';

const emptyBoard = {
  root: {
    id: 'root',
    title: 'Root',
    children: [],
    totalChildrenCount: 0,
    parentId: null
  }
};

const TASK_KANBAN_COLUMN_LIMIT = 80;
const TASK_TABLE_DEFAULT_PAGE_SIZE = 200;
const TASK_TABLE_PAGE_SIZE_OPTIONS = [100, 200, 500];
const TASK_CHART_EXPORT_LIMIT = 50000;
const TASK_TEXT_PIE_MAX_GROUPS = 5;
const TASK_TEXT_BAR_MAX_GROUPS = 20;
const TASK_TEXT_BUBBLE_MAX_GROUPS = 200;
const TASK_FILTER_STORAGE_KEY = 't-demand-task-table-filters:v1';
const TASK_FILTER_BATCH_LIMIT = 5000;
const TASK_VALUE_VIEWER_CONFIG_KEY = 't-demand-value-viewer-config:v1';
const SESSION_EXPIRED_EVENT = 'tdemand:session-expired';
const AUTH_API_PATH_RE = /^\/api\/(?:login\/|logout(?:\/|$))/i;

const apiPathFromUrl = (url) => {
  try {
    return new URL(String(url || ''), window.location.origin).pathname;
  } catch (error) {
    return String(url || '').split('?')[0];
  }
};

const shouldDispatchSessionExpired = (status, url) => {
  if (Number(status) !== 401) return false;
  const path = apiPathFromUrl(url);
  return !AUTH_API_PATH_RE.test(path);
};

const dispatchSessionExpiredEvent = (url, status) => {
  if (!window?.dispatchEvent) return;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, {
    detail: {
      status: Number(status) || 0,
      url: String(url || '')
    }
  }));
};

const positiveInt = (value, fallback, min = 1, max = Number.MAX_SAFE_INTEGER) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(numeric)));
};

const readTaskValueViewerConfig = () => {
  const defaults = {
    charLimit: 120,
    initialItems: 10,
    loadStep: 20,
    maxColumns: 8
  };
  if (typeof window === 'undefined') return defaults;

  const globalConfig = window.TDEMAND_VALUE_VIEWER_CONFIG
    && typeof window.TDEMAND_VALUE_VIEWER_CONFIG === 'object'
    && !Array.isArray(window.TDEMAND_VALUE_VIEWER_CONFIG)
    ? window.TDEMAND_VALUE_VIEWER_CONFIG
    : {};

  let persistedConfig = {};
  try {
    const parsed = JSON.parse(localStorage.getItem(TASK_VALUE_VIEWER_CONFIG_KEY) || '{}');
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      persistedConfig = parsed;
    }
  } catch (error) {
    persistedConfig = {};
  }

  const merged = {
    ...defaults,
    ...persistedConfig,
    ...globalConfig
  };

  return {
    charLimit: positiveInt(merged.charLimit, defaults.charLimit, 24, 1000),
    initialItems: positiveInt(merged.initialItems, defaults.initialItems, 3, 200),
    loadStep: positiveInt(merged.loadStep, defaults.loadStep, 1, 500),
    maxColumns: positiveInt(merged.maxColumns, defaults.maxColumns, 3, 20)
  };
};

const TASK_VALUE_VIEWER_CONFIG = readTaskValueViewerConfig();

const emptyTaskFilters = () => ({
  logic: 'and',
  conditions: []
});

const taskFilterConditionId = () => (
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
);

const taskFilterOperators = [
  { value: 'contains', label: 'contem', needsValue: true },
  { value: 'not_contains', label: 'não contém', needsValue: true },
  { value: 'equals', label: 'igual a', needsValue: true },
  { value: 'not_equals', label: 'diferente de', needsValue: true },
  { value: 'starts_with', label: 'comeca com', needsValue: true },
  { value: 'ends_with', label: 'termina com', needsValue: true },
  { value: 'greater_than', label: 'maior que', needsValue: true },
  { value: 'greater_or_equal', label: 'maior ou igual', needsValue: true },
  { value: 'less_than', label: 'menor que', needsValue: true },
  { value: 'less_or_equal', label: 'menor ou igual', needsValue: true },
  { value: 'between', label: 'entre (a..b)', needsValue: true },
  { value: 'empty', label: 'está vazio', needsValue: false },
  { value: 'not_empty', label: 'não está vazio', needsValue: false }
];

const viewerInitials = (viewer = {}) => {
  const name = String(viewer.name || viewer.email || 'U').trim();
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  return (parts.map((part) => part[0]).join('') || 'U').toUpperCase();
};

const roleTone = {
  pending: 'pending',
  waiting: 'waiting',
  success: 'success',
  error: 'error',
  custom: 'custom'
};

const api = async (url, options = {}) => {
  const method = String(options.method || 'GET').toUpperCase();
  const headers = {
    'content-type': 'application/json',
    ...(options.headers || {})
  };
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && window.TDEMAND_CSRF_TOKEN && !headers['X-TDemand-CSRF']) {
    headers['X-TDemand-CSRF'] = window.TDEMAND_CSRF_TOKEN;
  }
  const response = await fetch(url, {
    headers,
    ...options
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (shouldDispatchSessionExpired(response.status, url)) {
      dispatchSessionExpiredEvent(url, response.status);
    }
    const error = new Error(data.message || 'Falha na requisicao');
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

const slug = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '') || 'status';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const taskTitle = (task) => task.task_key || `Task #${task.id}`;

const demandTitle = (item = {}) => String(item.name || item.process || item.protocol || `Demanda ${item.id || ''}`).trim();

const compactTaskTitle = (value) => {
  const text = String(value || '');
  if (text.length <= 14) return text;
  return `${text.slice(0, 6)}...${text.slice(-4)}`;
};

const payloadValueLabel = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') {
    return value
      .replace(/<br\s*\/?>(\s*)/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(payloadValueLabel).filter(Boolean).join(' | ');
  if (typeof value === 'object') {
    const fileName = value.name || value.file_name || value.filename;
    if (fileName) return String(fileName);
    return JSON.stringify(value);
  }
  return String(value);
};

const isPlainObjectValue = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isFileLikePayloadValue = (value) => (
  isPlainObjectValue(value)
  && (
    value.storage
    || value.storage_path
    || value.content_base64
    || value.base64
    || value.mime_type
    || value.mimeType
    || value.file_name
    || value.filename
  )
);

const isPrimitiveStructuredValue = (value) => (
  value == null || ['string', 'number', 'boolean'].includes(typeof value)
);

const structuredValueTypeLabel = (value) => {
  if (value == null) return 'Vazio';
  if (Array.isArray(value)) return 'Lista';
  if (isPlainObjectValue(value)) return 'Objeto';
  if (typeof value === 'string') return 'Texto';
  if (typeof value === 'number') return 'Numero';
  if (typeof value === 'boolean') return 'Booleano';
  return 'Valor';
};

const friendlyScalarValue = (value) => {
  if (value == null || value === '') return 'Vazio';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Nao';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'Vazio';
  if (typeof value === 'string') {
    return value
      .replace(/<br\s*\/?>(\s*)/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'Vazio';
  }
  return String(value);
};

const truncateVisualText = (value, limit = TASK_VALUE_VIEWER_CONFIG.charLimit) => {
  const text = String(value ?? '');
  if (!text) return '';
  const max = positiveInt(limit, TASK_VALUE_VIEWER_CONFIG.charLimit, 12, 1000);
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 3))}...`;
};

const stringifyStructuredValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const describeNestedStructuredValue = (value) => {
  if (Array.isArray(value)) return `Lista com ${value.length} item${value.length === 1 ? '' : 's'}`;
  if (isPlainObjectValue(value)) {
    const totalFields = Object.keys(value).length;
    return `Objeto com ${totalFields} campo${totalFields === 1 ? '' : 's'}`;
  }
  return friendlyScalarValue(value);
};

const detectStructuredListKind = (list = []) => {
  if (!Array.isArray(list) || !list.length) return 'empty';
  const primitiveCount = list.filter((item) => isPrimitiveStructuredValue(item)).length;
  const objectCount = list.filter((item) => isPlainObjectValue(item)).length;
  if (primitiveCount === list.length) return 'simple';
  if (objectCount === list.length) return 'objects';
  return 'mixed';
};

const structuredListKindLabel = (kind) => {
  if (kind === 'simple') return 'lista de valores simples';
  if (kind === 'objects') return 'lista de objetos';
  if (kind === 'mixed') return 'lista mista';
  return 'lista vazia';
};

const structuredValueInlineSummary = (value, options = {}) => {
  const charLimit = positiveInt(options.charLimit, TASK_VALUE_VIEWER_CONFIG.charLimit, 12, 1000);
  if (Array.isArray(value)) {
    if (!value.length) return 'Lista vazia';
    const kind = detectStructuredListKind(value);
    if (kind === 'simple') {
      const sample = value.slice(0, 3).map((item) => friendlyScalarValue(item)).join(', ');
      return truncateVisualText(`Lista simples (${value.length}): ${sample}`, charLimit);
    }
    if (kind === 'objects') return `Lista de objetos (${value.length} itens)`;
    return `Lista mista (${value.length} itens)`;
  }
  if (isPlainObjectValue(value)) {
    if (isFileLikePayloadValue(value)) {
      const fileName = value.name || value.file_name || value.filename || 'Arquivo';
      return truncateVisualText(String(fileName), charLimit);
    }
    const keys = Object.keys(value);
    if (!keys.length) return 'Objeto vazio';
    const sample = keys.slice(0, 2).map((key) => `${key}: ${describeNestedStructuredValue(value[key])}`).join(' | ');
    return truncateVisualText(`Objeto (${keys.length} campos): ${sample}`, charLimit);
  }
  return truncateVisualText(friendlyScalarValue(value), charLimit);
};

const normalizeSearchQuery = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const taskPayloadKeys = (tasks) => Array.from(new Set(
  tasks.flatMap((task) => Object.keys(task.payload || {}))
)).sort((a, b) => a.localeCompare(b, 'pt-BR'));

const demandSchemaPayloadKeys = (demand) => {
  const schema = demand?.task_schema || demand?.args || {};
  if (!schema || typeof schema !== 'object' || Array.isArray(schema)) return [];
  return Object.keys(schema);
};

const TASK_CHART_TECHNICAL_PAYLOAD_KEYS = new Set([
  'id',
  'key',
  'task_key',
  'sensitive',
  'sensitive_data',
  'sensitiveData',
  'sensivel'
]);
const TASK_CHART_MAX_AGGREGATED_FIELDS = 120;
const TASK_CHART_MAX_AGGREGATED_DEPTH = 5;

const taskPayloadPathValue = (task, path) => {
  const payload = task?.payload && typeof task.payload === 'object' && !Array.isArray(task.payload)
    ? task.payload
    : {};
  if (Object.prototype.hasOwnProperty.call(payload, path)) return payload[path];
  return String(path || '').split('.').filter(Boolean).reduce((current, key) => (
    current != null && typeof current === 'object' ? current[key] : undefined
  ), payload);
};

const collectAggregatedChartPaths = (value, path, paths, depth = 0) => {
  if (!path || paths.size >= TASK_CHART_MAX_AGGREGATED_FIELDS) return;
  if (
    isPlainObjectValue(value)
    && !isFileLikePayloadValue(value)
    && depth < TASK_CHART_MAX_AGGREGATED_DEPTH
  ) {
    const entries = Object.entries(value);
    if (entries.length) {
      entries.forEach(([key, child]) => {
        if (paths.size >= TASK_CHART_MAX_AGGREGATED_FIELDS) return;
        collectAggregatedChartPaths(child, `${path}.${key}`, paths, depth + 1);
      });
      return;
    }
  }
  paths.add(path);
};

const aggregatedTaskPayloadPaths = (demand, tasks) => {
  const schemaKeys = new Set(demandSchemaPayloadKeys(demand));
  const paths = new Set();
  for (const task of tasks || []) {
    const payload = task?.payload && typeof task.payload === 'object' && !Array.isArray(task.payload)
      ? task.payload
      : {};
    for (const [key, value] of Object.entries(payload)) {
      if (paths.size >= TASK_CHART_MAX_AGGREGATED_FIELDS) break;
      if (schemaKeys.has(key) || TASK_CHART_TECHNICAL_PAYLOAD_KEYS.has(key)) continue;
      collectAggregatedChartPaths(value, key, paths);
    }
    if (paths.size >= TASK_CHART_MAX_AGGREGATED_FIELDS) break;
  }
  return Array.from(paths).sort((a, b) => a.localeCompare(b, 'pt-BR'));
};

const inferAggregatedChartFieldType = (tasks, path) => {
  let sawNumber = false;
  let sawBoolean = false;
  let sawOther = false;
  for (const task of tasks || []) {
    const value = taskPayloadPathValue(task, path);
    if (value == null || value === '') continue;
    if (typeof value === 'number' && Number.isFinite(value)) sawNumber = true;
    else if (typeof value === 'boolean') sawBoolean = true;
    else sawOther = true;
    if (sawOther || (sawNumber && sawBoolean)) return 'string';
  }
  if (sawNumber) return 'number';
  if (sawBoolean) return 'boolean';
  return 'string';
};

const taskSchemaDefinition = (demand, fieldKey) => {
  const schema = demand?.task_schema || demand?.args || {};
  const definition = schema?.[fieldKey];
  if (definition && typeof definition === 'object' && !Array.isArray(definition)) return definition;
  if (typeof definition === 'string') return { type: definition };
  return { type: 'string' };
};

const taskFieldLabel = (demand, fieldKey) => {
  const definition = taskSchemaDefinition(demand, fieldKey);
  return String(definition.alias || definition.label || definition.title || '').trim() || fieldKey;
};

const statusRoleForTask = (demand, statusName) => (
  normalizeStatuses(demand).find((status) => status.name === statusName)?.role || 'custom'
);

const statusToneForTask = (demand, task) => (
  roleTone[statusRoleForTask(demand, task.status)] || 'custom'
);

const statusToneForName = (demand, statusName) => (
  roleTone[statusRoleForTask(demand, statusName)] || 'custom'
);

const normalizeListOptions = (definition) => (
  Array.isArray(definition?.options) ? definition.options : []
).map((option, index) => {
  if (typeof option === 'string') {
    return {
      name: option,
      color: chartPalette[index % chartPalette.length]
    };
  }
  return {
    name: String(option?.name || option?.label || option?.value || '').trim(),
    color: /^#[0-9a-f]{6}$/i.test(String(option?.color || '').trim())
      ? String(option.color).trim()
      : chartPalette[index % chartPalette.length]
  };
}).filter((option) => option.name);

const listOptionForValue = (definition, value) => {
  const text = payloadValueLabel(value);
  return normalizeListOptions(definition).find((option) => option.name === text) || {
    name: text,
    color: '#64748b'
  };
};

const isTaskRelationDefinition = (definition = {}) => {
  const type = String(definition?.type || '').trim().toLowerCase();
  const source = definition?.source && typeof definition.source === 'object' ? definition.source : {};
  return ['relationship', 'relation', 'relacionamento'].includes(type)
    || (type === 'object' && Boolean(source.demand_id && source.attribute));
};

const taskRelationDefinitionValues = (definition, rawValue) => {
  const sourceAttribute = String(definition?.source?.attribute || '').trim();
  const configuredFaceAttributes = (Array.isArray(definition?.source?.face_attributes)
    ? definition.source.face_attributes
    : [])
    .map((face) => ({
      attribute: String(face?.attribute || '').trim(),
      kind: String(face?.kind || 'text').trim().toLowerCase()
    }))
    .filter((face) => face.attribute && face.kind !== 'image')
    .map((face) => face.attribute)
    .filter((attribute, index, list) => list.indexOf(attribute) === index);
  const values = Array.isArray(rawValue) ? rawValue : [rawValue];
  const relationDisplayValue = (value) => {
    if (value == null) return '';
    if (isPlainObjectValue(value)) {
      const configuredFacesLabel = configuredFaceAttributes.length
        ? configuredFaceAttributes
          .map((attribute) => value?.faces?.[attribute])
          .flatMap((faceValue) => {
            if (Array.isArray(faceValue)) {
              return faceValue
                .map((item) => friendlyScalarValue(item))
                .map((item) => String(item || '').trim())
                .filter(Boolean);
            }
            if (faceValue != null && !isPlainObjectValue(faceValue)) {
              const scalar = String(friendlyScalarValue(faceValue) || '').trim();
              return scalar ? [scalar] : [];
            }
            return [];
          })
          .join(' · ')
        : '';
      if (configuredFacesLabel) return configuredFacesLabel;

      const selectedFaceAttribute = String(value.source_attribute || value.sourceAttribute || '').trim();
      const faceValue = selectedFaceAttribute
        ? value?.faces?.[selectedFaceAttribute]
        : undefined;
      if (faceValue != null) {
        if (Array.isArray(faceValue)) {
          return faceValue
            .map((item) => friendlyScalarValue(item))
            .map((item) => String(item || '').trim())
            .filter(Boolean)
            .join(', ');
        }
        if (!isPlainObjectValue(faceValue)) {
          return friendlyScalarValue(faceValue);
        }
      }
      const sourceValue = sourceAttribute ? value[sourceAttribute] : undefined;
      if (sourceValue != null && !Array.isArray(sourceValue) && !isPlainObjectValue(sourceValue)) {
        return friendlyScalarValue(sourceValue);
      }
      if (value.value != null && !Array.isArray(value.value) && !isPlainObjectValue(value.value)) {
        return friendlyScalarValue(value.value);
      }
      const label = String(value.label || '').trim();
      if (label) return label;
      return '';
    }
    return friendlyScalarValue(value);
  };
  return Array.from(new Set(values
    .map((value) => relationDisplayValue(value))
    .map((value) => String(value || '').trim())
    .filter(Boolean)));
};

const TASK_TABLE_ACTION_COLUMN = '__action';

const buildTaskTableColumns = (demand, tasks) => [
  { key: TASK_TABLE_ACTION_COLUMN, label: 'Ver Task' },
  { key: '__task', label: 'Task' },
  { key: '__status', label: 'Status' },
  ...taskPayloadKeys(tasks).map((key) => ({ key, label: taskFieldLabel(demand, key) }))
];

const taskFilterFields = (demand, tasks) => {
  const payloadKeys = Array.from(new Set([
    ...demandSchemaPayloadKeys(demand),
    ...aggregatedTaskPayloadPaths(demand, tasks)
  ])).sort((a, b) => taskFieldLabel(demand, a).localeCompare(taskFieldLabel(demand, b), 'pt-BR'));
  const schemaKeys = new Set(demandSchemaPayloadKeys(demand));

  return [
    { value: 'task_key', label: 'Task' },
    { value: 'status', label: 'Status' },
    { value: 'error_message', label: 'Erro' },
    { value: 'created_at', label: 'Criada em', type: 'date' },
    { value: 'updated_at', label: 'Atualizada em', type: 'date' },
    { value: 'processed_at', label: 'Processada em', type: 'date' },
    ...payloadKeys.map((key) => ({
      value: `payload.${key}`,
      label: taskFieldLabel(demand, key),
      type: schemaKeys.has(key)
        ? normalizeTaskSchemaType(taskSchemaDefinition(demand, key).type)
        : inferAggregatedChartFieldType(tasks, key),
      aggregated: !schemaKeys.has(key)
    }))
  ];
};

const normalizeTaskFilterCondition = (condition, fields) => {
  const fallbackField = fields[0]?.value || 'task_key';
  const fieldExists = fields.some((field) => field.value === condition?.field);
  const operatorExists = taskFilterOperators.some((operator) => operator.value === condition?.operator);
  return {
    id: condition?.id || taskFilterConditionId(),
    field: fieldExists ? condition.field : fallbackField,
    operator: operatorExists ? condition.operator : 'contains',
    value: String(condition?.value ?? '')
  };
};

const normalizeTaskFilters = (filters, fields) => {
  const conditions = Array.isArray(filters?.conditions) ? filters.conditions : [];
  return {
    logic: filters?.logic === 'or' ? 'or' : 'and',
    conditions: conditions.map((condition) => normalizeTaskFilterCondition(condition, fields))
  };
};

const taskFilterOperatorNeedsValue = (operator) => (
  taskFilterOperators.find((item) => item.value === operator)?.needsValue !== false
);

const serializeTaskFilters = (filters) => {
  const conditions = (Array.isArray(filters?.conditions) ? filters.conditions : [])
    .map((condition) => ({
      field: String(condition.field || '').trim(),
      operator: String(condition.operator || 'contains').trim(),
      value: String(condition.value ?? '').trim()
    }))
    .filter((condition) => condition.field && (
      !taskFilterOperatorNeedsValue(condition.operator) || condition.value
    ));

  return conditions.length ? {
    logic: filters?.logic === 'or' ? 'or' : 'and',
    conditions
  } : null;
};

const taskFiltersCount = (filters) => serializeTaskFilters(filters)?.conditions.length || 0;

const taskFilterStorageScope = (demand, sharedMode, shareToken) => {
  if (demand?.id) return `demand:${demand.id}`;
  if (sharedMode && shareToken) return `share:${String(shareToken).slice(0, 24)}`;
  return 'global';
};

const readStoredTaskFilters = (scope) => {
  try {
    const storage = JSON.parse(localStorage.getItem(TASK_FILTER_STORAGE_KEY) || '{}');
    return Array.isArray(storage?.[scope]) ? storage[scope] : [];
  } catch (error) {
    return [];
  }
};

const writeStoredTaskFilters = (scope, items) => {
  try {
    const storage = JSON.parse(localStorage.getItem(TASK_FILTER_STORAGE_KEY) || '{}');
    storage[scope] = items;
    localStorage.setItem(TASK_FILTER_STORAGE_KEY, JSON.stringify(storage));
  } catch (error) {
    // LocalStorage pode estar indisponível no modo privado; o filtro aplicado continua funcionando.
  }
};

const taskTableCellRawValue = (task, column) => {
  if (column.key === TASK_TABLE_ACTION_COLUMN) return 'Ver task';
  if (column.key === '__task') return taskTitle(task);
  if (column.key === '__status') return task.status || '';
  return task.payload?.[column.key];
};

const isStructuredCellValue = (demand, column, value) => {
  if (!column || [TASK_TABLE_ACTION_COLUMN, '__task', '__status'].includes(column.key)) return false;
  const schemaType = taskColumnSchemaType(demand, column);
  if (schemaType === 'file') return false;
  return Array.isArray(value) || isPlainObjectValue(value);
};

const taskTableCellValue = (task, column, demand) => {
  const rawValue = taskTableCellRawValue(task, column);
  if (isStructuredCellValue(demand, column, rawValue)) {
    return structuredValueInlineSummary(rawValue, { charLimit: TASK_VALUE_VIEWER_CONFIG.charLimit });
  }
  return payloadValueLabel(rawValue);
};

const taskTableCellCopyValue = (task, column, demand) => {
  const rawValue = taskTableCellRawValue(task, column);
  if (isStructuredCellValue(demand, column, rawValue)) {
    return stringifyStructuredValue(rawValue);
  }
  return taskTableCellValue(task, column, demand);
};

const taskTableSortField = (column) => {
  if (!column || column.key === TASK_TABLE_ACTION_COLUMN) return '';
  if (column.key === '__task') return 'task_key';
  if (column.key === '__status') return 'status';
  return `payload.${column.key}`;
};

const isTaskTableSortableColumn = (column) => Boolean(taskTableSortField(column));

const normalizeTaskSchemaType = (type) => {
  const value = String(type || 'string').trim().toLowerCase();
  if (['int', 'integer'].includes(value)) return 'integer';
  if (['decimal', 'float', 'double', 'number', 'numeric'].includes(value)) return value === 'number' ? 'number' : 'decimal';
  if (['bool', 'boolean'].includes(value)) return 'boolean';
  if (['object', 'json'].includes(value)) return 'object';
  if (['array', 'multi-select', 'multiselect'].includes(value)) return 'array';
  if (['file', 'date', 'datetime', 'uuid', 'email', 'url', 'phone', 'select', 'list', 'string', 'text', 'long_text'].includes(value)) return value;
  return 'string';
};

const taskColumnSchemaType = (demand, column) => normalizeTaskSchemaType(taskSchemaDefinition(demand, column?.key).type);

const taskColumnSchemaDefinition = (demand, column) => taskSchemaDefinition(demand, column?.key);

const isTaskListColumn = (demand, column) => taskColumnSchemaType(demand, column) === 'list';

const isTaskRelationColumn = (demand, column) => {
  if (!column || [TASK_TABLE_ACTION_COLUMN, '__task', '__status'].includes(column.key)) return false;
  return isTaskRelationDefinition(taskColumnSchemaDefinition(demand, column));
};

const taskRelationColumnValues = (demand, column, rawValue) => {
  const definition = taskColumnSchemaDefinition(demand, column);
  return taskRelationDefinitionValues(definition, rawValue);
};

const isTaskTableEditableColumn = (demand, columns, columnIndex, canEdit) => {
  const column = columns[columnIndex];
  if (!canEdit || !column) return false;
  if (column.key === '__status') return true;
  if (columnIndex < 3) return false;
  if ([TASK_TABLE_ACTION_COLUMN, '__task'].includes(column.key)) return false;
  if (isTaskRelationColumn(demand, column)) return false;
  return taskColumnSchemaType(demand, column) !== 'file';
};

const parseJsonTableValue = (raw, expectedType, fieldKey) => {
  const text = String(raw ?? '').trim();
  if (!text) {
    throw new Error(`${fieldKey} exige JSON valido.`);
  }
  try {
    const parsed = JSON.parse(text);
    if (expectedType === 'array' && !Array.isArray(parsed)) {
      throw new Error();
    }
    if (expectedType === 'object' && (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))) {
      throw new Error();
    }
    return parsed;
  } catch (error) {
    throw new Error(`${fieldKey} exige JSON ${expectedType === 'array' ? 'array' : 'objeto'} valido.`);
  }
};

const normalizeTaskTableEditValue = (raw, fieldKey, type) => {
  const normalizedType = normalizeTaskSchemaType(type);
  const text = String(raw ?? '').trim();

  if (normalizedType === 'file') {
    throw new Error('Campos de arquivo devem ser editados pelo modal da task.');
  }

  if (['number', 'integer', 'decimal'].includes(normalizedType)) {
    if (!text) return null;
    const number = Number(String(text).replace(',', '.'));
    if (!Number.isFinite(number)) {
      throw new Error(`${fieldKey} precisa ser um numero valido.`);
    }
    if (normalizedType === 'integer' && !Number.isInteger(number)) {
      throw new Error(`${fieldKey} precisa ser um numero inteiro.`);
    }
    return normalizedType === 'integer' ? Math.trunc(number) : number;
  }

  if (normalizedType === 'boolean') {
    if (!text) return null;
    const lowered = text.toLowerCase();
    if (['true', '1', 'sim', 's', 'yes', 'y'].includes(lowered)) return true;
    if (['false', '0', 'nao', 'não', 'n', 'no'].includes(lowered)) return false;
    throw new Error(`${fieldKey} precisa ser booleano: sim/não, true/false ou 1/0.`);
  }

  if (['object', 'array'].includes(normalizedType)) {
    return parseJsonTableValue(raw, normalizedType, fieldKey);
  }

  return text;
};

const areTaskTableValuesEqual = (left, right) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const TASK_CANVAS_ROW_HEIGHT = 36;
const TASK_CANVAS_HEADER_HEIGHT = 40;
const TASK_CANVAS_ROW_NUMBER_WIDTH = 54;
const TASK_CANVAS_OVERSCAN_ROWS = 10;
const TASK_CANVAS_OVERSCAN_COLS = 2;
const TASK_CANVAS_RESIZE_HANDLE_WIDTH = 7;

const taskTableColumnWidth = (column) => {
  if (column.key === TASK_TABLE_ACTION_COLUMN) return 120;
  if (column.key === '__task') return 190;
  if (column.key === '__status') return 150;
  return Math.max(160, Math.min(260, String(column.label || column.key || '').length * 9 + 72));
};

const taskTableColumnWidthKey = (column) => String(column?.key || column?.label || '');

const taskTableColumnWidthBounds = (column) => {
  if (column?.key === TASK_TABLE_ACTION_COLUMN) return { min: 104, max: 168 };
  if (column?.key === '__task') return { min: 150, max: 380 };
  if (column?.key === '__status') return { min: 122, max: 260 };
  return { min: 112, max: 560 };
};

const clampTaskTableColumnWidth = (column, width) => {
  const bounds = taskTableColumnWidthBounds(column);
  return Math.max(bounds.min, Math.min(bounds.max, Math.round(Number(width) || taskTableColumnWidth(column))));
};

const taskTableEditInputValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const isTaskTableBulkTextColumn = (demand, columns, columnIndex, canEdit) => {
  const column = columns[columnIndex];
  if (!column || columnIndex < 3) return false;
  if (!isTaskTableEditableColumn(demand, columns, columnIndex, canEdit)) return false;
  return ['string', 'text', 'long_text', 'email', 'url', 'phone', 'uuid'].includes(taskColumnSchemaType(demand, column));
};

const taskTableBulkTextValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return payloadValueLabel(value);
};

const taskTableRowCopyText = (task, columns, demand) => columns
  .filter((column) => column.key !== TASK_TABLE_ACTION_COLUMN)
  .map((column) => {
    const label = String(column.label || column.key || '').trim();
    const value = taskTableCellCopyValue(task, column, demand) || '-';
    return `${label}= ${value}`;
  })
  .join(', ');

const chartPalette = ['#7c3aed', '#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#0891b2', '#db2777', '#64748b'];

const chartNumber = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });
const chartPercent = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

const chartFieldDefinitions = (demand, rows) => {
  const schemaKeys = demandSchemaPayloadKeys(demand);
  const schemaKeySet = new Set(schemaKeys);
  return Array.from(new Set([
    ...schemaKeys,
    ...aggregatedTaskPayloadPaths(demand, rows)
  ])).map((key) => ({
    key,
    label: taskFieldLabel(demand, key),
    type: schemaKeySet.has(key)
      ? normalizeTaskSchemaType(taskSchemaDefinition(demand, key).type)
      : inferAggregatedChartFieldType(rows, key),
    aggregated: !schemaKeySet.has(key)
  }));
};

const isChartTextField = (type) => ['string', 'text', 'select', 'list', 'email', 'url', 'phone', 'uuid', 'boolean'].includes(type);
const isChartNumericField = (type) => ['integer', 'decimal', 'float', 'double', 'number', 'numeric'].includes(type);

const normalizeChartGroupKey = (value) => payloadValueLabel(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const parseChartNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const text = payloadValueLabel(value)
    .replace(/\s/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');
  if (!text) return null;
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
};

const groupsFromCounts = (counts) => Array.from(counts.values())
  .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'pt-BR'));

const buildTextChart = (field, rows) => {
  const counts = new Map();
  rows.forEach((task) => {
    const raw = taskPayloadPathValue(task, field.key);
    const key = normalizeChartGroupKey(raw);
    if (!key) return;
    const label = payloadValueLabel(raw) || 'Sem valor';
    const current = counts.get(key);
    if (current) current.count += 1;
    else counts.set(key, { key, label, count: 1 });
  });
  const groups = groupsFromCounts(counts);
  if (groups.length === 1) {
    return {
      ...field,
      dispersion: 1,
      constant: true,
      value: groups[0].label,
      count: groups[0].count
    };
  }
  if (!groups.length || groups.length > TASK_TEXT_BUBBLE_MAX_GROUPS) {
    return groups.length ? { ...field, dispersion: groups.length, skipped: true } : null;
  }
  return {
    ...field,
    dispersion: groups.length,
    chartType: groups.length < TASK_TEXT_PIE_MAX_GROUPS
      ? 'pie'
      : groups.length < TASK_TEXT_BAR_MAX_GROUPS
        ? 'bar'
        : 'bubble',
    groups
  };
};

const chartValueSizeClass = (value) => {
  const length = String(value ?? '').length;
  if (length > 18) return 'is-xs';
  if (length > 13) return 'is-sm';
  return '';
};

const escapePdfHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatPdfPercent = (value) => `${chartPercent.format(Number(value) || 0)}%`;

const printPdfBarRows = (groups = [], total = 0) => groups.map((group, index) => {
  const percent = total ? (Number(group.count || 0) / total) * 100 : 0;
  return `
    <tr>
      <td>
        <span class="td-print-dot" style="background:${chartPalette[index % chartPalette.length]}"></span>
        ${escapePdfHtml(group.label || 'Sem valor')}
      </td>
      <td>${chartNumber.format(Number(group.count || 0))}</td>
      <td>
        <span class="td-print-bar"><i style="width:${Math.max(2, Math.min(100, percent))}%"></i></span>
        <b>${formatPdfPercent(percent)}</b>
      </td>
    </tr>`;
}).join('');

const printPdfSection = ({ title, subtitle = '', headers = [], rows = '', empty = 'Sem dados para exibir.' }) => `
  <article class="td-print-card">
    <header>
      <strong>${escapePdfHtml(title)}</strong>
      ${subtitle ? `<span>${escapePdfHtml(subtitle)}</span>` : ''}
    </header>
    ${rows ? `
      <table>
        ${headers.length ? `<thead><tr>${headers.map((header) => `<th>${escapePdfHtml(header)}</th>`).join('')}</tr></thead>` : ''}
        <tbody>${rows}</tbody>
      </table>
    ` : `<p class="td-print-empty">${escapePdfHtml(empty)}</p>`}
  </article>`;

const buildTaskChartsPdfHtml = ({ demand, insights, meta }) => {
  const analyzedTotal = Number(insights?.total || 0);
  const filteredTotal = Number(meta?.count || analyzedTotal);
  const globalTotal = Number(meta?.total || filteredTotal);
  const chartedFields = (insights?.textCharts?.length || 0)
    + (insights?.numericInsights?.length || 0)
    + (insights?.statusChart?.groups?.length ? 1 : 0);
  const averageCompletion = insights?.completionInsights?.length
    ? insights.completionInsights.reduce((sum, item) => sum + Number(item.percent || 0), 0) / insights.completionInsights.length
    : 0;
  const generatedAt = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date());
  const demandName = demand?.name || demand?.protocol || 'Dashboard das tasks';
  const statusRows = printPdfBarRows(insights?.statusChart?.groups || [], analyzedTotal);
  const completionRows = (insights?.completionInsights || []).map((field) => `
    <tr>
      <td>${escapePdfHtml(field.label)}</td>
      <td>${chartNumber.format(Number(field.filled || 0))}</td>
      <td>
        <span class="td-print-bar"><i style="width:${Math.max(2, Math.min(100, Number(field.percent || 0)))}%"></i></span>
        <b>${formatPdfPercent(field.percent)}</b>
      </td>
    </tr>
  `).join('');
  const numericRows = (insights?.numericInsights || []).map((field) => `
    <tr>
      <td>${escapePdfHtml(field.label)}</td>
      <td>${chartNumber.format(Number(field.count || 0))}</td>
      <td>${chartNumber.format(Number(field.average || 0))}</td>
      <td>${chartNumber.format(Number(field.mode || 0))}</td>
      <td>${chartNumber.format(Number(field.min || 0))}</td>
      <td>${chartNumber.format(Number(field.max || 0))}</td>
    </tr>
  `).join('');
  const textCharts = (insights?.textCharts || []).map((chart) => printPdfSection({
    title: chart.label,
    subtitle: `${chart.groups?.length || 0} grupo(s) encontrados`,
    headers: ['Valor', 'Tasks', 'Participacao'],
    rows: printPdfBarRows(chart.groups || [], (chart.groups || []).reduce((sum, group) => sum + Number(group.count || 0), 0))
  })).join('');
  const constantRows = (insights?.constantTextCharts || []).map((field) => `
    <tr>
      <td>${escapePdfHtml(field.label)}</td>
      <td>${escapePdfHtml(field.value || '-')}</td>
      <td>${chartNumber.format(Number(field.count || 0))}</td>
    </tr>
  `).join('');
  const skippedRows = (insights?.skippedTextCharts || []).map((field) => `
    <tr>
      <td>${escapePdfHtml(field.label)}</td>
      <td>${chartNumber.format(Number(field.dispersion || 0))}</td>
      <td>Alta dispersao</td>
    </tr>
  `).join('');

  return `
    <main class="td-print-report">
      <header class="td-print-title">
        <div>
          <span>T-Demand</span>
          <strong>${escapePdfHtml(demandName)}</strong>
        </div>
        <em>${escapePdfHtml(generatedAt)}</em>
      </header>

      <section class="td-print-kpis">
        <article><span>Tasks analisadas</span><strong>${chartNumber.format(analyzedTotal)}</strong></article>
        <article><span>Total no filtro</span><strong>${chartNumber.format(filteredTotal)}</strong></article>
        <article><span>Total da demanda</span><strong>${chartNumber.format(globalTotal)}</strong></article>
        <article><span>Campos em gráficos</span><strong>${chartNumber.format(chartedFields)}</strong></article>
        <article><span>Preenchimento medio</span><strong>${formatPdfPercent(averageCompletion)}</strong></article>
        <article><span>Campos ignorados</span><strong>${chartNumber.format(insights?.skippedTextCharts?.length || 0)}</strong></article>
      </section>

      ${meta?.truncated ? `
        <p class="td-print-note">Relatório gerado com ${chartNumber.format(analyzedTotal)} de ${chartNumber.format(filteredTotal)} tasks do filtro atual. Refine os filtros para um PDF mais específico.</p>
      ` : ''}

      <section class="td-print-grid">
        ${printPdfSection({
          title: 'Status das tasks',
          subtitle: 'Distribuicao operacional',
          headers: ['Status', 'Tasks', 'Participacao'],
          rows: statusRows
        })}
        ${printPdfSection({
          title: 'Campos com menor preenchimento',
          subtitle: 'Qualidade dos dados',
          headers: ['Campo', 'Preenchidos', 'Preenchimento'],
          rows: completionRows
        })}
      </section>

      ${printPdfSection({
        title: 'Campos numericos',
        subtitle: 'Media, moda, minimo e maximo',
        headers: ['Campo', 'Valores', 'Media', 'Moda', 'Min', 'Max'],
        rows: numericRows,
        empty: 'Nenhum campo numerico encontrado.'
      })}

      ${textCharts ? `<section class="td-print-stack">${textCharts}</section>` : ''}

      ${printPdfSection({
        title: 'Atributos sem variacao',
        subtitle: 'Campos constantes nas tasks analisadas',
        headers: ['Campo', 'Valor encontrado', 'Registros'],
        rows: constantRows,
        empty: 'Nenhum atributo constante encontrado.'
      })}

      ${printPdfSection({
        title: 'Campos ignorados por alta dispersao',
        subtitle: 'Campos com muitos valores diferentes',
        headers: ['Campo', 'Grupos distintos', 'Motivo'],
        rows: skippedRows,
        empty: 'Nenhum campo foi ignorado.'
      })}
    </main>`;
};

const exportTaskChartsPdf = ({ demand, insights, meta }) => {
  if (!insights?.total) return false;
  const printWindow = window.open('', '_blank', 'width=1240,height=900');
  if (!printWindow) return false;

  const title = `T-Demand - Grafico - ${demand?.name || demand?.protocol || 'Tasks'}`;
  const reportHtml = buildTaskChartsPdfHtml({ demand, insights, meta });

  printWindow.document.write(`<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapePdfHtml(title)}</title>
  <style>
    @page { size: A4 portrait; margin: 9mm; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; min-height: auto; background: #fff; color: #1f2937; }
    body { font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; font-size: 10px; line-height: 1.25; }
    .td-print-report { width: 100%; display: grid; gap: 7px; }
    .td-print-title { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; border-bottom: 1px solid #ddd6fe; padding-bottom: 6px; margin-bottom: 1px; }
    .td-print-title div { display: grid; gap: 1px; }
    .td-print-title span { color: #7c3aed; font-size: 9px; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
    .td-print-title strong { color: #111827; font-size: 16px; font-weight: 950; }
    .td-print-title em { color: #6b7280; font-size: 9px; font-style: normal; font-weight: 800; white-space: nowrap; }
    .td-print-kpis { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 5px; }
    .td-print-kpis article { min-height: 38px; display: grid; align-content: center; gap: 1px; border: 1px solid #e5e7eb; border-radius: 7px; background: #fbfaff; padding: 6px; }
    .td-print-kpis span { color: #6b7280; font-size: 8.5px; font-weight: 850; }
    .td-print-kpis strong { color: #4c1d95; font-size: 13px; font-weight: 950; }
    .td-print-note { margin: 0; border: 1px solid #ddd6fe; border-radius: 7px; background: #f5f3ff; color: #4c1d95; padding: 5px 7px; font-size: 9px; font-weight: 750; }
    .td-print-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 7px; align-items: start; }
    .td-print-stack { display: grid; gap: 7px; }
    .td-print-card { border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; padding: 7px; break-inside: avoid; page-break-inside: avoid; }
    .td-print-card header { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; border-bottom: 1px solid #f1f5f9; padding-bottom: 4px; margin-bottom: 4px; }
    .td-print-card header strong { color: #111827; font-size: 10.5px; font-weight: 950; }
    .td-print-card header span { color: #6b7280; font-size: 8.5px; font-weight: 800; text-align: right; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    thead { display: table-header-group; }
    tr { break-inside: avoid; page-break-inside: avoid; }
    th, td { border-bottom: 1px solid #f1f5f9; padding: 4px 3px; color: #374151; font-size: 9px; vertical-align: middle; overflow-wrap: anywhere; }
    th { color: #6b7280; font-size: 8px; font-weight: 900; text-align: left; text-transform: uppercase; letter-spacing: .04em; }
    td:nth-child(2), td:nth-child(3), td:nth-child(4), td:nth-child(5), td:nth-child(6) { text-align: right; }
    td:first-child { text-align: left; font-weight: 850; color: #1f2937; }
    .td-print-dot { width: 7px; height: 7px; display: inline-block; border-radius: 99px; margin-right: 5px; vertical-align: middle; }
    .td-print-bar { width: calc(100% - 36px); max-width: 118px; height: 5px; display: inline-block; overflow: hidden; border-radius: 99px; background: #ede9fe; vertical-align: middle; margin-right: 5px; }
    .td-print-bar i { height: 100%; display: block; border-radius: inherit; background: linear-gradient(90deg, #8b5cf6, #6d28d9); }
    .td-print-empty { margin: 0; color: #6b7280; font-size: 9px; font-weight: 750; }
    @media print {
      .td-print-card { box-shadow: none; }
      .td-print-stack .td-print-card { break-inside: auto; page-break-inside: auto; }
    }
  </style>
</head>
<body>
  ${reportHtml}
</body>
</html>`);
  printWindow.document.close();

  let printed = false;
  const print = () => {
    if (printed || printWindow.closed) return;
    printed = true;
    printWindow.focus();
    printWindow.print();
  };
  printWindow.addEventListener('load', () => window.setTimeout(print, 350));
  window.setTimeout(print, 1000);
  return true;
};

const buildNumericInsight = (field, rows) => {
  const values = rows
    .map((task) => parseChartNumber(taskPayloadPathValue(task, field.key)))
    .filter((value) => value !== null);
  if (!values.length) return null;
  const sum = values.reduce((acc, value) => acc + value, 0);
  const counts = new Map();
  values.forEach((value) => {
    const key = String(value);
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  const modeEntry = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || Number(a[0]) - Number(b[0]))[0];
  return {
    ...field,
    count: values.length,
    average: sum / values.length,
    mode: Number(modeEntry?.[0] || values[0]),
    min: Math.min(...values),
    max: Math.max(...values)
  };
};

const buildCompletionInsights = (fields, rows) => {
  const total = rows.length || 0;
  if (!total) return [];
  return fields.map((field) => {
    const filled = rows.filter((task) => normalizeChartGroupKey(taskPayloadPathValue(task, field.key))).length;
    return {
      ...field,
      filled,
      percent: total ? (filled / total) * 100 : 0
    };
  }).sort((a, b) => a.percent - b.percent).slice(0, 6);
};

const buildTaskChartsDashboard = (demand, rows) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const fields = chartFieldDefinitions(demand, safeRows);
  const statusCounts = new Map();
  safeRows.forEach((task) => {
    const status = task.status || 'Sem status';
    const key = normalizeChartGroupKey(status) || 'sem-status';
    const current = statusCounts.get(key);
    if (current) current.count += 1;
    else statusCounts.set(key, { key, label: status, count: 1, tone: statusToneForName(demand, status) });
  });

  const textCharts = fields
    .filter((field) => isChartTextField(field.type))
    .map((field) => buildTextChart(field, safeRows))
    .filter(Boolean);
  const numericInsights = fields
    .filter((field) => isChartNumericField(field.type))
    .map((field) => buildNumericInsight(field, safeRows))
    .filter(Boolean);
  const drawableTextCharts = textCharts.filter((chart) => !chart.skipped);
  const constantTextCharts = textCharts.filter((chart) => chart.constant);
  const skippedTextCharts = textCharts.filter((chart) => chart.skipped);

  return {
    total: safeRows.length,
    fields,
    statusChart: {
      key: '__status',
      label: 'Status',
      chartType: 'pie',
      groups: groupsFromCounts(statusCounts)
    },
    textCharts: drawableTextCharts.filter((chart) => !chart.constant),
    constantTextCharts,
    skippedTextCharts,
    numericInsights,
    completionInsights: buildCompletionInsights(fields, safeRows)
  };
};

const PieChart = ({ groups, total }) => {
  const circumference = 2 * Math.PI * 42;
  let offset = 0;
  return (
    <div className="td-chart-pie-wrap">
      <svg viewBox="0 0 112 112" className="td-chart-pie" aria-hidden="true">
        <circle cx="56" cy="56" r="42" className="td-chart-pie-track" />
        {groups.map((group, index) => {
          const value = total ? (group.count / total) * circumference : 0;
          const segment = (
            <circle
              key={group.key || group.label}
              cx="56"
              cy="56"
              r="42"
              className="td-chart-pie-segment"
              style={{
                stroke: chartPalette[index % chartPalette.length],
                strokeDasharray: `${value} ${circumference - value}`,
                strokeDashoffset: -offset
              }}
            />
          );
          offset += value;
          return segment;
        })}
      </svg>
      <strong>{chartNumber.format(total)}</strong>
    </div>
  );
};

const chartLegendMenuPosition = (event) => {
  const width = 232;
  const height = 92;
  return {
    left: Math.max(8, Math.min(event.clientX, window.innerWidth - width - 8)),
    top: Math.max(8, Math.min(event.clientY, window.innerHeight - height - 8)),
    width
  };
};

const ChartLegendContextMenu = ({ menu, onClose, onFilter }) => {
  useEffect(() => {
    if (!menu) return undefined;
    const close = (event) => {
      if (event?.target?.closest?.('.td-chart-legend-menu')) return;
      onClose();
    };
    document.addEventListener('pointerdown', close);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('pointerdown', close);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [menu, onClose]);

  if (!menu) return null;

  const label = String(menu.group?.label || '');
  return createPortal(
    <div
      className="td-chart-legend-menu"
      role="menu"
      data-chart-export-exclude
      style={{ left: `${menu.left}px`, top: `${menu.top}px`, width: `${menu.width}px` }}
      onClick={(event) => event.stopPropagation()}
    >
      <button type="button" role="menuitem" onClick={async () => {
        await copyPlainText(label);
        onClose();
      }}>
        <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
        Copiar
      </button>
      <button type="button" role="menuitem" onClick={() => {
        onFilter?.({
          field: menu.filterField,
          value: label,
          label,
          group: menu.group
        });
        onClose();
      }}>
        <span className="material-symbols-rounded" aria-hidden="true">filter_alt</span>
        Adicionar em Filtro
      </button>
    </div>,
    document.body
  );
};

const useChartLegendMenu = (filterField, onFilter) => {
  const [menu, setMenu] = useState(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const openMenu = useCallback((event, group, index) => {
    if (!group || !filterField) return;
    event.preventDefault();
    event.stopPropagation();
    setMenu({
      ...chartLegendMenuPosition(event),
      group,
      index,
      filterField
    });
  }, [filterField]);

  const menuElement = (
    <ChartLegendContextMenu menu={menu} onClose={closeMenu} onFilter={onFilter} />
  );

  return { openMenu, menuElement };
};

const ChartLegend = ({ groups, total, filterField, onFilter }) => {
  const { openMenu, menuElement } = useChartLegendMenu(filterField, onFilter);
  return (
    <>
    <div className="td-chart-legend">
      {groups.map((group, index) => (
        <div
          className="td-chart-legend-row"
          key={group.key || group.label}
          onContextMenu={(event) => openMenu(event, group, index)}
          title="Clique com o botão direito para copiar ou filtrar"
        >
          <span style={{ background: chartPalette[index % chartPalette.length] }} aria-hidden="true" />
          <strong>{group.label}</strong>
          <em>{chartNumber.format(group.count)} · {total ? chartPercent.format((group.count / total) * 100) : '0'}%</em>
        </div>
      ))}
    </div>
    {menuElement}
    </>
  );
};

const chartTypeIcon = (type) => {
  if (type === 'pie') return 'pie_chart';
  if (type === 'bubble') return 'bubble_chart';
  return 'bar_chart';
};

const BubbleDistributionChart = ({ groups, total, filterField, onFilter }) => {
  const [activeBubble, setActiveBubble] = useState(null);
  const { openMenu, menuElement } = useChartLegendMenu(filterField, onFilter);
  const counts = groups.map((group) => Number(group.count || 0));
  const maxCount = Math.max(...counts, 1);
  const minCount = Math.min(...counts, maxCount);
  const spread = Math.max(0, maxCount - minCount);

  useEffect(() => {
    if (!activeBubble) return undefined;
    const close = (event) => {
      if (event?.target?.closest?.('[data-bubble-interactive]')) return;
      setActiveBubble(null);
    };
    document.addEventListener('pointerdown', close);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('pointerdown', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [activeBubble]);

  const openBubbleDropdown = (event, group, index, percent) => {
    event.stopPropagation();
    const bubbleKey = `${group.key || group.label}-${index}`;
    if (activeBubble?.key === bubbleKey) {
      setActiveBubble(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const dropdownWidth = 260;
    const left = Math.min(
      window.innerWidth - dropdownWidth - 12,
      Math.max(12, rect.left + (rect.width / 2) - (dropdownWidth / 2))
    );
    const shouldOpenUp = rect.bottom + 178 > window.innerHeight && rect.top > 190;
    setActiveBubble({
      key: bubbleKey,
      group,
      index,
      percent,
      left,
      top: shouldOpenUp ? rect.top - 10 : rect.bottom + 10,
      placement: shouldOpenUp ? 'up' : 'down',
      width: dropdownWidth
    });
  };

  return (
    <>
    <div className="td-chart-bubbles" role="list">
      {groups.map((group, index) => {
        const ratio = spread ? (Number(group.count || 0) - minCount) / spread : 0.18;
        const size = Math.round(28 + Math.sqrt(Math.max(0.08, ratio)) * 48);
        const percent = total ? chartPercent.format((Number(group.count || 0) / total) * 100) : '0';
        const offsets = [
          [0, -12], [15, 8], [-14, 16], [8, -20], [-20, -3],
          [22, 18], [-8, -16], [3, 14], [-23, 9], [17, -9],
          [10, 24], [-17, -22]
        ];
        const [baseDx, baseDy] = offsets[index % offsets.length];
        const drift = ((Math.floor(index / offsets.length) % 3) - 1) * 5;
        const isLarge = size >= 66;
        return (
          <div
            className={`td-chart-bubble-wrap ${activeBubble?.key === `${group.key || group.label}-${index}` ? 'is-active' : ''}`}
            key={group.key || group.label}
            role="listitem"
            style={{
              '--bubble-dx': `${baseDx + drift}px`,
              '--bubble-dy': `${baseDy}px`,
              gridColumn: isLarge ? 'span 2' : undefined,
              gridRow: isLarge ? 'span 2' : undefined
            }}
          >
          <button
            type="button"
            className={`td-chart-bubble ${size < 52 ? 'is-compact' : ''}`}
            title={`${group.label}: ${chartNumber.format(group.count)} task(s) · ${percent}%`}
            aria-label={`${group.label}: ${chartNumber.format(group.count)} task(s), ${percent}%`}
            aria-expanded={activeBubble?.key === `${group.key || group.label}-${index}` ? 'true' : 'false'}
            data-bubble-interactive
            onClick={(event) => openBubbleDropdown(event, group, index, percent)}
            onContextMenu={(event) => openMenu(event, group, index)}
            style={{
              '--bubble-size': `${size}px`,
              '--bubble-color': chartPalette[index % chartPalette.length]
            }}
          >
            <strong>{chartNumber.format(group.count)}</strong>
            <em>{percent}%</em>
            <span>{group.label}</span>
          </button>
          </div>
        );
      })}
    </div>
    {activeBubble && createPortal(
      <div
        className={`td-chart-bubble-dropdown is-${activeBubble.placement}`}
        data-bubble-interactive
        style={{
          left: `${activeBubble.left}px`,
          top: `${activeBubble.top}px`,
          width: `${activeBubble.width}px`,
          transform: activeBubble.placement === 'up' ? 'translateY(-100%)' : 'none'
        }}
        role="dialog"
        aria-label="informações da bolha"
      >
        <header>
          <span style={{ background: chartPalette[activeBubble.index % chartPalette.length] }} aria-hidden="true" />
          <strong>{activeBubble.group.label}</strong>
        </header>
        <dl>
          <div>
            <dt>Tasks</dt>
            <dd>{chartNumber.format(activeBubble.group.count)}</dd>
          </div>
          <div>
            <dt>Participacao</dt>
            <dd>{activeBubble.percent}%</dd>
          </div>
          <div>
            <dt>Ranking</dt>
            <dd>#{activeBubble.index + 1}</dd>
          </div>
          <div>
            <dt>Total analisado</dt>
            <dd>{chartNumber.format(total)}</dd>
          </div>
        </dl>
        <p>Bolha proporcional ao volume deste valor dentro da dispersao do campo.</p>
      </div>,
      document.body
    )}
    {menuElement}
    </>
  );
};

const TextDistributionChart = ({ chart, onLegendFilter }) => {
  const filterField = `payload.${chart.key}`;
  const { openMenu, menuElement } = useChartLegendMenu(filterField, onLegendFilter);
  const chartTotal = chart.groups.reduce((sum, group) => sum + group.count, 0);
  const maxGroupCount = Math.max(...chart.groups.map((item) => item.count), 1);
  return (
    <article className={`td-chart-card is-${chart.chartType}`}>
      <header>
        <div>
          <strong>{chart.label}</strong>
          <div className="td-chart-field-caption">
            {chart.aggregated && <em className="td-chart-field-origin">Agregado</em>}
            {chart.chartType === 'bubble' && <span>{chartNumber.format(chart.groups.length)} grupos em bolhas</span>}
          </div>
        </div>
        <span className="material-symbols-rounded" aria-hidden="true">{chartTypeIcon(chart.chartType)}</span>
      </header>
      {chart.chartType === 'pie' ? (
        <div className="td-chart-pie-grid">
          <PieChart groups={chart.groups} total={chartTotal} />
          <ChartLegend groups={chart.groups} total={chartTotal} filterField={filterField} onFilter={onLegendFilter} />
        </div>
      ) : chart.chartType === 'bubble' ? (
        <BubbleDistributionChart groups={chart.groups} total={chartTotal} filterField={filterField} onFilter={onLegendFilter} />
      ) : (
        <div className="td-chart-bars">
          {chart.groups.map((group, index) => {
            const width = Math.max(3, (group.count / maxGroupCount) * 100);
            return (
              <div
                className="td-chart-bar-row"
                key={group.key || group.label}
                onContextMenu={(event) => openMenu(event, group, index)}
                title="Clique com o botão direito para copiar ou filtrar"
              >
                <span>{group.label}</span>
                <div>
                  <i style={{ width: `${width}%`, background: chartPalette[index % chartPalette.length] }} />
                </div>
                <strong>{chartNumber.format(group.count)}</strong>
              </div>
            );
          })}
        </div>
      )}
      {menuElement}
    </article>
  );
};

const TaskChartsDashboard = ({ demand, rows, loading, error, meta, onRefresh, onLegendFilter }) => {
  const [exportNotice, setExportNotice] = useState('');
  const insights = useMemo(() => buildTaskChartsDashboard(demand, rows), [demand, rows]);
  const chartedFields = insights.textCharts.length + insights.numericInsights.length + (insights.statusChart.groups.length ? 1 : 0);
  const aggregatedFields = insights.fields.filter((field) => field.aggregated).length;
  const averageCompletion = insights.completionInsights.length
    ? insights.completionInsights.reduce((sum, item) => sum + item.percent, 0) / insights.completionInsights.length
    : 0;
  const canExportPdf = Boolean(demand && insights.total && !loading);

  const handlePdfExport = useCallback(() => {
    setExportNotice('');
    const opened = exportTaskChartsPdf({
      demand,
      insights,
      meta
    });
    if (!opened) {
      setExportNotice('Não foi possível abrir a janela de PDF. Libere pop-ups para o T-Demand e tente novamente.');
    }
  }, [demand, insights, meta]);

  return (
    <div className="td-charts-dashboard">
      <header className="td-charts-hero">
        <div>
          <span className="material-symbols-rounded" aria-hidden="true">query_stats</span>
          <div>
            <strong>Dashboard das tasks</strong>
            <p>Leitura automática dos campos da demanda, com gráficos apenas onde a dispersão ajuda a entender os dados.</p>
          </div>
        </div>
        <div className="td-charts-hero-actions" data-chart-export-exclude>
          <button type="button" className="secondary-button" onClick={handlePdfExport} disabled={!canExportPdf}>
            <span className="material-symbols-rounded" aria-hidden="true">picture_as_pdf</span>
            Salvar PDF
          </button>
          <button type="button" className="secondary-button" onClick={onRefresh} disabled={loading || !demand}>
            <span className="material-symbols-rounded" aria-hidden="true">sync</span>
            Atualizar gráficos
          </button>
        </div>
      </header>

      {error && <div className="td-kanban-notice">{error}</div>}
      {exportNotice && <div className="td-kanban-notice" data-chart-export-exclude>{exportNotice}</div>}
      {loading && <div className="td-kanban-notice is-loading">Calculando gráficos das tasks...</div>}

      <section className="td-chart-kpis">
        <article>
          <span>Tasks analisadas</span>
          <strong>{chartNumber.format(insights.total)}</strong>
          {meta?.truncated && <em>amostra limitada a {chartNumber.format(meta.count || insights.total)}</em>}
        </article>
        <article>
          <span>Campos em gráficos</span>
          <strong>{chartNumber.format(chartedFields)}</strong>
          <em>{aggregatedFields} agregado(s) · {insights.skippedTextCharts.length} ignorado(s) por alta dispersão</em>
        </article>
        <article>
          <span>Preenchimento médio</span>
          <strong>{chartPercent.format(averageCompletion)}%</strong>
          <em>ideia extra: qualidade dos dados</em>
        </article>
      </section>

      {!loading && !insights.total ? (
        <div className="td-chart-empty">
          <span className="material-symbols-rounded" aria-hidden="true">insights</span>
          <strong>Nenhuma task para analisar</strong>
          <p>Importe tasks ou ajuste busca/filtros para gerar gráficos.</p>
        </div>
      ) : (
        <>
          <section className="td-chart-main-grid">
            <article className="td-chart-card is-status">
              <header>
                <div>
                  <strong>Status das tasks</strong>
                </div>
                <span className="material-symbols-rounded" aria-hidden="true">donut_large</span>
              </header>
              <div className="td-chart-pie-grid">
                <PieChart groups={insights.statusChart.groups} total={insights.total} />
                <ChartLegend groups={insights.statusChart.groups} total={insights.total} filterField="status" onFilter={onLegendFilter} />
              </div>
            </article>

            <article className="td-chart-card is-quality">
              <header>
                <div>
                  <strong>Campos com menor preenchimento</strong>
                </div>
                <span className="material-symbols-rounded" aria-hidden="true">fact_check</span>
              </header>
              <div className="td-chart-bars">
                {insights.completionInsights.map((field) => (
                  <div className="td-chart-bar-row" key={field.key}>
                    <span>{field.label}</span>
                    <div><i style={{ width: `${Math.max(3, field.percent)}%` }} /></div>
                    <strong>{chartPercent.format(field.percent)}%</strong>
                  </div>
                ))}
              </div>
            </article>
          </section>

          {insights.numericInsights.length > 0 && (
            <section className="td-chart-section">
              <header>
                <strong>Campos numericos</strong>
                <span>Media, moda, minimo e maximo</span>
              </header>
              <div className="td-number-grid">
                {insights.numericInsights.map((field) => (
                  <article className="td-number-card" key={field.key}>
                    <header>
                      <div>
                        <strong>{field.label}</strong>
                        <div className="td-chart-field-caption">
                          {field.aggregated && <em className="td-chart-field-origin">Agregado</em>}
                          <span>{chartNumber.format(field.count)} valores analisados</span>
                        </div>
                      </div>
                    </header>
                    <dl>
                      {[
                        ['Media', field.average],
                        ['Moda', field.mode],
                        ['Min', field.min],
                        ['Max', field.max]
                      ].map(([label, value]) => {
                        const formatted = chartNumber.format(value);
                        return (
                          <div key={label}>
                            <dt>{label}</dt>
                            <dd className={chartValueSizeClass(formatted)}>{formatted}</dd>
                          </div>
                        );
                      })}
                    </dl>
                  </article>
                ))}
              </div>
            </section>
          )}

          {insights.textCharts.length > 0 && (
            <section className="td-chart-section">
              <header>
                <strong>Dispersao de campos de texto</strong>
                <span>2 a 4 grupos vira pizza; 5 a 19 barras; 20 a 200 bolhas.</span>
              </header>
              <div className="td-text-chart-grid">
                {insights.textCharts.map((chart) => (
                  <TextDistributionChart key={chart.key} chart={chart} total={insights.total} onLegendFilter={onLegendFilter} />
                ))}
              </div>
            </section>
          )}

          {insights.skippedTextCharts.length > 0 && (
            <section className="td-chart-skip">
              <span className="material-symbols-rounded" aria-hidden="true">scatter_plot</span>
              <div>
                <strong>Campos ignorados por alta dispersao</strong>
                <p>{insights.skippedTextCharts.map((chart) => `${chart.label} (${chart.dispersion})`).join(', ')}</p>
              </div>
            </section>
          )}

          {insights.constantTextCharts.length > 0 && (
            <section className="td-chart-section">
              <header>
                <strong>Atributos sem variação</strong>
                <span>Campos onde todos os registros preenchidos têm o mesmo valor.</span>
              </header>
              <div className="td-chart-constant-table" role="table" aria-label="Atributos sem variação">
                <div role="row" className="td-chart-constant-head">
                  <span role="columnheader">Campo</span>
                  <span role="columnheader">Valor encontrado</span>
                  <span role="columnheader">Registros</span>
                </div>
                {insights.constantTextCharts.map((field) => (
                  <div role="row" className="td-chart-constant-row" key={field.key}>
                    <strong role="cell">{field.label}{field.aggregated && <small className="td-chart-field-origin">Agregado</small>}</strong>
                    <span role="cell">{field.value || '-'}</span>
                    <em role="cell">{chartNumber.format(field.count)}</em>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

const roundRectPath = (ctx, x, y, width, height, radius) => {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const drawRoundRect = (ctx, x, y, width, height, radius, fillStyle, strokeStyle = '') => {
  roundRectPath(ctx, x, y, width, height, radius);
  if (fillStyle) {
    ctx.fillStyle = fillStyle;
    ctx.fill();
  }
  if (strokeStyle) {
    ctx.strokeStyle = strokeStyle;
    ctx.stroke();
  }
};

const TaskPresenceStack = ({ viewers = [], open, onToggle, onClose, disabled = false }) => {
  const rootRef = useRef(null);
  const visibleViewers = viewers.slice(0, 5);
  const hiddenCount = Math.max(0, viewers.length - visibleViewers.length);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current || rootRef.current.contains(event.target)) return;
      onClose?.();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [onClose, open]);

  return (
    <div className="tasks-presence" ref={rootRef}>
      <button
        type="button"
        className="tasks-presence-trigger"
        onClick={onToggle}
        disabled={disabled || !viewers.length}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : 'false'}
        title={viewers.length ? `${viewers.length} pessoa(s) visualizando` : 'Nenhuma pessoa visualizando agora'}
      >
        <span className="tasks-presence-label">Online</span>
        <span className="tasks-presence-stack" aria-hidden="true">
          {visibleViewers.map((viewer, index) => (
            <span
              className={`tasks-presence-avatar ${viewer.avatar_url ? 'has-image' : ''}`}
              key={viewer.id || `${viewer.name}-${index}`}
              style={{ zIndex: 20 - index }}
            >
              {viewer.avatar_url ? <img src={viewer.avatar_url} alt="" loading="lazy" /> : viewerInitials(viewer)}
            </span>
          ))}
          {!visibleViewers.length && (
            <span className="tasks-presence-avatar is-empty">
              <span className="material-symbols-rounded" aria-hidden="true">visibility</span>
            </span>
          )}
          {hiddenCount > 0 && <span className="tasks-presence-more">+{hiddenCount}</span>}
        </span>
      </button>
      {open && (
        <div className="tasks-presence-popover" role="dialog" aria-label="Pessoas visualizando esta demanda">
          <header>
            <strong>Visualizando agora</strong>
            <span>{viewers.length.toLocaleString('pt-BR')} pessoa(s)</span>
          </header>
          <div className="tasks-presence-list">
            {viewers.map((viewer, index) => (
              <div className="tasks-presence-person" key={viewer.id || `${viewer.name}-${index}`}>
                <span className={`tasks-presence-person-avatar ${viewer.avatar_url ? 'has-image' : ''}`}>
                  {viewer.avatar_url ? <img src={viewer.avatar_url} alt="" loading="lazy" /> : viewerInitials(viewer)}
                </span>
                <div>
                  <strong>{viewer.name || 'Usuario'}</strong>
                  <small>{viewer.email || viewer.role || (viewer.mode === 'shared' ? 'Link compartilhado' : 'Online')}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const canvasStatusColors = (theme, tone) => ({
  success: [theme.green, 'rgba(34, 197, 94, 0.13)'],
  error: [theme.red, 'rgba(239, 68, 68, 0.13)'],
  waiting: [theme.amber, 'rgba(245, 158, 11, 0.15)'],
  pending: [theme.primary, theme.primarySoft],
  custom: [theme.muted, theme.controlBg]
}[tone] || [theme.muted, theme.controlBg]);

const drawCanvasEyeIcon = (ctx, x, y, color) => {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.ellipse(x + 8, y + 8, 7, 4.8, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x + 8, y + 8, 2.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const truncateCanvasText = (ctx, text, maxWidth) => {
  const value = String(text ?? '');
  if (!value || maxWidth <= 0) return '';
  if (ctx.measureText(value).width <= maxWidth) return value;
  let start = 0;
  let end = value.length;
  while (start < end) {
    const middle = Math.ceil((start + end) / 2);
    const candidate = `${value.slice(0, middle)}...`;
    if (ctx.measureText(candidate).width <= maxWidth) start = middle;
    else end = middle - 1;
  }
  return `${value.slice(0, Math.max(1, start))}...`;
};

const canvasThemeFromElement = (element) => {
  const styles = window.getComputedStyle(element || document.documentElement);
  const css = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
  return {
    surface: css('--surface', '#ffffff'),
    surface2: css('--surface-2', '#eef3f9'),
    controlBg: css('--control-bg', '#f7faff'),
    inputBg: css('--input-bg', '#ffffff'),
    text: css('--text', '#172033'),
    panelTitle: css('--panel-title', '#0f172a'),
    muted: css('--muted', '#6b7280'),
    primary: css('--primary', '#2563eb'),
    primarySoft: css('--primary-soft', '#e8f0ff'),
    line: css('--line', 'rgba(31, 42, 55, 0.14)'),
    lineSoft: css('--line-soft', 'rgba(31, 42, 55, 0.1)'),
    tableGridRow: css('--td-table-grid-row', 'rgba(31, 42, 55, 0.13)'),
    tableGridColumn: css('--td-table-grid-column', 'rgba(31, 42, 55, 0.15)'),
    tableGridStrong: css('--td-table-grid-strong', 'rgba(31, 42, 55, 0.2)'),
    tableResizeHandle: css('--td-table-resize-handle', 'rgba(100, 116, 139, 0.22)'),
    tableResizeActive: css('--td-table-resize-active', '#7c3aed'),
    tableRowAlt: css('--td-table-row-alt', 'rgba(31, 42, 55, 0.025)'),
    tableActionBg: css('--td-table-action-bg', 'rgba(124, 58, 237, 0.1)'),
    tableActionBorder: css('--td-table-action-border', 'rgba(124, 58, 237, 0.3)'),
    green: css('--green', '#16a34a'),
    red: css('--red', '#dc2626'),
    amber: '#b45309',
    white: '#ffffff'
  };
};

const buildCsvRows = (demand, tasks) => {
  const payloadKeys = taskPayloadKeys(tasks);
  return tasks.map((task) => {
    const payload = task.payload || {};
    const row = {
      demanda: demand?.name || demand?.protocol || '',
      task_id: task.id,
      task_key: task.task_key || '',
      status: task.status || '',
      erro: task.error_message || '',
      criada_em: formatDateTime(task.created_at),
      atualizada_em: formatDateTime(task.updated_at),
      processada_em: formatDateTime(task.processed_at)
    };
    payloadKeys.forEach((key) => {
      row[`payload.${taskFieldLabel(demand, key)}`] = payloadValueLabel(payload[key]);
    });
    return row;
  });
};

const downloadTextFile = (filename, content, mimeType) => {
  const isCsv = String(mimeType || '').toLowerCase().includes('text/csv') || filename.toLowerCase().endsWith('.csv');
  const normalizedContent = isCsv && !String(content).startsWith('\uFEFF')
    ? `\uFEFF${content}`
    : content;
  const blob = new Blob([normalizedContent], { type: mimeType || 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const copyPlainText = async (value) => {
  const text = String(value ?? '');
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
    return true;
  }
};

const isKanbanFileValue = (value) => (
  isFileLikePayloadValue(value)
  || (Array.isArray(value) && value.some((item) => isFileLikePayloadValue(item)))
);

const defaultKanbanCardFieldPaths = (demand, payload) => {
  const schema = demand?.task_schema || demand?.args || {};
  const schemaKeys = demandSchemaPayloadKeys(demand);
  const payloadKeys = Object.keys(payload || {});
  const orderedFields = [...new Set([...schemaKeys, ...payloadKeys])];
  const selected = orderedFields.slice(0, 3);
  const firstFileField = orderedFields.find((field) => (
    normalizeTaskSchemaType(schema?.[field]?.type || schema?.[field]) === 'file'
    || isKanbanFileValue(taskPayloadPathValue({ payload }, field))
  ));
  if (firstFileField && !selected.includes(firstFileField)) selected.push(firstFileField);
  return selected;
};

const kanbanCardFieldPaths = (demand, payload) => (
  Array.isArray(demand?.kanban_card_fields)
    ? demand.kanban_card_fields
    : defaultKanbanCardFieldPaths(demand, payload)
);

const payloadPreviewValues = (demand, payload) => {
  const values = kanbanCardFieldPaths(demand, payload)
    .map((key) => {
      const value = taskPayloadPathValue({ payload }, key);
      if (value === undefined || isKanbanFileValue(value)) return null;
      const label = taskFieldLabel(demand, key);
      const displayValue = structuredValueInlineSummary(value, { charLimit: 72 }).trim();
      if (!displayValue) return null;
      const definition = taskSchemaDefinition(demand, key);
      const type = isTaskRelationDefinition(definition)
        ? 'relationship'
        : normalizeTaskSchemaType(definition.type);
      const listOption = type === 'list' ? listOptionForValue(definition, value) : null;
      const relationValues = type === 'relationship' ? taskRelationDefinitionValues(definition, value) : [];
      if (type === 'relationship' && !relationValues.length) return null;
      return {
        key,
        label,
        value: displayValue,
        text: `${label}: ${displayValue}`,
        type,
        color: listOption?.color || '',
        relationValues
      };
    })
    .filter(Boolean);
  return values;
};

const findTaskCoverFile = (task, demand) => {
  const payload = task?.payload || {};
  const visibleFields = new Set(kanbanCardFieldPaths(demand, payload));
  for (const [field, value] of Object.entries(payload)) {
    if (!visibleFields.has(field)) continue;
    const files = Array.isArray(value) ? value : [value];
    const index = files.findIndex((item) => item?.storage?.relative_path || item?.storage_path);
    if (index === -1) continue;

    const file = files[index];
    const mimeType = file.mime_type || file.mimeType || file.type || 'application/octet-stream';
    return {
      field,
      index,
      name: file.name || file.file_name || file.filename || field,
      mimeType,
      isImage: mimeType.startsWith('image/'),
      url: `/api/demands/${task.demand_id}/tasks/${task.id}/files/${encodeURIComponent(field)}/${index}`
    };
  }
  return null;
};

const normalizeStatuses = (demand) => {
  const statuses = Array.isArray(demand?.task_statuses) ? demand.task_statuses : [];
  if (statuses.length) return statuses;
  return [
    { name: 'Pendente', role: 'pending', initial: true },
    { name: 'Aguardando', role: 'waiting', initial: false },
    { name: 'Concluida', role: 'success', initial: false },
    { name: 'Falha', role: 'error', initial: false }
  ];
};

const makeBoard = (demand, tasks, statusCounts = {}) => {
  if (!demand) return emptyBoard;

  const statuses = normalizeStatuses(demand);
  const board = {
    root: {
      id: 'root',
      title: 'Root',
      children: statuses.map((status, index) => `column-${index}-${slug(status.name)}`),
      totalChildrenCount: statuses.length,
      parentId: null
    }
  };

  const columnByStatus = new Map();

  statuses.forEach((status, index) => {
    const columnId = `column-${index}-${slug(status.name)}`;
    const cards = tasks
      .filter((task) => task.status === status.name)
      .map((task) => `task-${task.id}`);

    columnByStatus.set(status.name, columnId);
    board[columnId] = {
      id: columnId,
      title: status.name,
      parentId: 'root',
      children: cards,
      totalChildrenCount: Number(statusCounts[status.name] ?? cards.length),
      content: {
        status,
        tone: roleTone[status.role] || 'custom'
      }
    };
  });

  tasks.forEach((task) => {
    const statusExists = columnByStatus.has(task.status);
    const columnId = statusExists ? columnByStatus.get(task.status) : board.root.children[0];
    if (!statusExists && columnId && !board[columnId].children.includes(`task-${task.id}`)) {
      board[columnId].children.push(`task-${task.id}`);
      board[columnId].totalChildrenCount += 1;
    }

    board[`task-${task.id}`] = {
      id: `task-${task.id}`,
      title: taskTitle(task),
      parentId: columnId,
      children: [],
      totalChildrenCount: 0,
      type: 'card',
      content: {
        task,
        coverFile: findTaskCoverFile(task, demand),
        payloadPreview: payloadPreviewValues(demand, task.payload),
        updatedAt: formatDateTime(task.updated_at)
      }
    };
  });

  return board;
};

const appendTasksToBoard = (currentBoard, demand, rows, statusCounts = {}) => {
  if (!rows.length) return currentBoard;
  const nextBoard = { ...currentBoard };
  const columnsByStatus = new Map((nextBoard.root?.children || []).map((columnId) => [
    nextBoard[columnId]?.title,
    columnId
  ]));

  rows.forEach((task) => {
    const taskId = `task-${task.id}`;
    if (nextBoard[taskId]) return;
    const columnId = columnsByStatus.get(task.status) || nextBoard.root?.children?.[0];
    if (!columnId || !nextBoard[columnId]) return;

    nextBoard[columnId] = {
      ...nextBoard[columnId],
      children: [...(nextBoard[columnId].children || []), taskId],
      totalChildrenCount: Number(statusCounts[task.status] ?? nextBoard[columnId].totalChildrenCount ?? 0)
    };
    nextBoard[taskId] = {
      id: taskId,
      title: taskTitle(task),
      parentId: columnId,
      children: [],
      totalChildrenCount: 0,
      type: 'card',
      content: {
        task,
        coverFile: findTaskCoverFile(task, demand),
        payloadPreview: payloadPreviewValues(demand, task.payload),
        updatedAt: formatDateTime(task.updated_at)
      }
    };
  });

  return nextBoard;
};

const objectColumnsFromList = (items = [], maxColumns = TASK_VALUE_VIEWER_CONFIG.maxColumns) => {
  const keys = [];
  const seen = new Set();
  const limit = positiveInt(maxColumns, TASK_VALUE_VIEWER_CONFIG.maxColumns, 3, 20);
  const sample = items.slice(0, 80);
  sample.forEach((item) => {
    if (!isPlainObjectValue(item)) return;
    Object.keys(item).forEach((key) => {
      const normalized = String(key || '').trim();
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      keys.push(normalized);
    });
  });
  return keys.slice(0, limit);
};

const TaskValueViewerModal = ({
  open,
  title,
  value,
  onClose
}) => {
  const [jsonMode, setJsonMode] = useState(false);
  const [friendlyExpanded, setFriendlyExpanded] = useState(false);
  const [copiedNotice, setCopiedNotice] = useState('');
  const [stack, setStack] = useState([]);
  const [visibleCount, setVisibleCount] = useState(TASK_VALUE_VIEWER_CONFIG.initialItems);

  useEffect(() => {
    if (!open) return;
    setJsonMode(false);
    setFriendlyExpanded(false);
    setCopiedNotice('');
    setVisibleCount(TASK_VALUE_VIEWER_CONFIG.initialItems);
    setStack([{
      label: title || 'Valor da task',
      value
    }]);
  }, [open, title, value]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, open]);

  useEffect(() => {
    if (!copiedNotice) return undefined;
    const timer = window.setTimeout(() => setCopiedNotice(''), 1800);
    return () => window.clearTimeout(timer);
  }, [copiedNotice]);

  const current = stack[stack.length - 1] || null;
  const currentValue = current?.value;
  const listKind = Array.isArray(currentValue) ? detectStructuredListKind(currentValue) : '';
  const totalItems = Array.isArray(currentValue)
    ? currentValue.length
    : isPlainObjectValue(currentValue)
      ? Object.keys(currentValue).length
      : 0;

  const effectiveInitial = Math.min(totalItems || TASK_VALUE_VIEWER_CONFIG.initialItems, TASK_VALUE_VIEWER_CONFIG.initialItems);
  const effectiveVisibleCount = Math.min(totalItems, Math.max(effectiveInitial || 0, visibleCount));
  const hasMoreItems = totalItems > effectiveVisibleCount;

  const openNestedValue = useCallback((nextValue, label) => {
    setStack((currentStack) => ([
      ...currentStack,
      {
        label,
        value: nextValue
      }
    ]));
    setJsonMode(false);
    setFriendlyExpanded(false);
    setVisibleCount(TASK_VALUE_VIEWER_CONFIG.initialItems);
  }, []);

  const closeNestedValue = useCallback(() => {
    setStack((currentStack) => (currentStack.length > 1 ? currentStack.slice(0, -1) : currentStack));
    setVisibleCount(TASK_VALUE_VIEWER_CONFIG.initialItems);
    setJsonMode(false);
    setFriendlyExpanded(false);
  }, []);

  const copyCurrentValue = useCallback(async () => {
    const serialized = stringifyStructuredValue(currentValue);
    if (!serialized) return;
    const copied = await copyPlainText(serialized);
    if (copied) setCopiedNotice('Valor copiado.');
  }, [currentValue]);

  const renderNestedCell = useCallback((nestedValue, label) => {
    const preview = truncateVisualText(describeNestedStructuredValue(nestedValue), TASK_VALUE_VIEWER_CONFIG.charLimit);
    if (Array.isArray(nestedValue) || isPlainObjectValue(nestedValue)) {
      return (
        <button
          type="button"
          className="td-structured-nested-link"
          title={describeNestedStructuredValue(nestedValue)}
          onClick={() => openNestedValue(nestedValue, label)}
        >
          {preview}
        </button>
      );
    }
    const scalar = friendlyScalarValue(nestedValue);
    return (
      <span title={scalar}>
        {truncateVisualText(scalar, TASK_VALUE_VIEWER_CONFIG.charLimit)}
      </span>
    );
  }, [openNestedValue]);

  if (!open || !current) return null;

  const hasStructuredValue = Array.isArray(currentValue) || isPlainObjectValue(currentValue);

  const renderFriendlyContent = () => {
    if (Array.isArray(currentValue)) {
      if (!currentValue.length) {
        return <div className="td-structured-empty">Esta lista nao possui itens.</div>;
      }

      const visibleItems = currentValue.slice(0, effectiveVisibleCount);
      if (listKind === 'simple') {
        return (
          <ol className="td-structured-simple-list" start={1}>
            {visibleItems.map((item, index) => {
              const fullValue = friendlyScalarValue(item);
              return (
                <li key={`simple-${index}`} title={fullValue}>
                  {truncateVisualText(fullValue, TASK_VALUE_VIEWER_CONFIG.charLimit)}
                </li>
              );
            })}
          </ol>
        );
      }

      if (listKind === 'objects') {
        const objectColumns = objectColumnsFromList(currentValue);
        return (
          <div className="td-structured-table-wrap">
            <table className="td-structured-table">
              <thead>
                <tr>
                  <th>#</th>
                  {objectColumns.map((key) => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item, rowIndex) => (
                  <tr key={`obj-${rowIndex}`}>
                    <th>{rowIndex + 1}</th>
                    {objectColumns.map((key) => (
                      <td key={`${rowIndex}-${key}`}>
                        {renderNestedCell(item?.[key], `${current.label} - ${key}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      return (
        <ol className="td-structured-mixed-list" start={1}>
          {visibleItems.map((item, index) => {
            const itemType = structuredValueTypeLabel(item);
            const preview = truncateVisualText(describeNestedStructuredValue(item), TASK_VALUE_VIEWER_CONFIG.charLimit);
            return (
              <li key={`mixed-${index}`}>
                <strong>{itemType}:</strong>
                {Array.isArray(item) || isPlainObjectValue(item)
                  ? renderNestedCell(item, `${current.label} - item ${index + 1}`)
                  : <span title={describeNestedStructuredValue(item)}>{preview}</span>}
              </li>
            );
          })}
        </ol>
      );
    }

    if (isPlainObjectValue(currentValue)) {
      const entries = Object.entries(currentValue);
      if (!entries.length) {
        return <div className="td-structured-empty">Este objeto nao possui campos.</div>;
      }
      return (
        <div className="td-structured-object-grid">
          {entries.slice(0, effectiveVisibleCount).map(([key, entryValue]) => (
            <div className="td-structured-object-row" key={key}>
              <span>{key}</span>
              <div>{renderNestedCell(entryValue, `${current.label} - ${key}`)}</div>
            </div>
          ))}
        </div>
      );
    }

    const scalarValue = friendlyScalarValue(currentValue);
    return (
      <div className="td-structured-scalar-value" title={scalarValue}>
        {truncateVisualText(scalarValue, TASK_VALUE_VIEWER_CONFIG.charLimit)}
      </div>
    );
  };

  return (
    <div className="td-structured-viewer-backdrop" role="presentation" onClick={onClose}>
      <section
        className="td-structured-viewer-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Visualizacao de valor estruturado"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <div>
            <span>{current.label}</span>
            <strong>{structuredValueTypeLabel(currentValue)}</strong>
            {(Array.isArray(currentValue) || isPlainObjectValue(currentValue)) && (
              <p>
                {Array.isArray(currentValue)
                  ? structuredListKindLabel(listKind)
                  : `${totalItems} campo${totalItems === 1 ? '' : 's'}`}
              </p>
            )}
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Fechar">
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </header>

        <div className="td-structured-viewer-toolbar">
          {stack.length > 1 && (
            <button type="button" className="secondary-button" onClick={closeNestedValue}>
              <span className="material-symbols-rounded" aria-hidden="true">arrow_back</span>
              Voltar
            </button>
          )}
          <button type="button" className="secondary-button" onClick={() => setJsonMode((currentMode) => !currentMode)}>
            <span className="material-symbols-rounded" aria-hidden="true">data_object</span>
            {jsonMode ? 'Visualizacao amigavel' : 'Ver JSON'}
          </button>
          {!jsonMode && hasStructuredValue && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => setFriendlyExpanded((currentExpanded) => !currentExpanded)}
            >
              <span className="material-symbols-rounded" aria-hidden="true">{friendlyExpanded ? 'unfold_less' : 'unfold_more'}</span>
              {friendlyExpanded ? 'Recolher conteudo' : 'Expandir conteudo'}
            </button>
          )}
          <button type="button" className="secondary-button" onClick={copyCurrentValue}>
            <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
            Copiar valor
          </button>
          {!jsonMode && friendlyExpanded && hasMoreItems && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => setVisibleCount((currentCount) => Math.min(totalItems, currentCount + TASK_VALUE_VIEWER_CONFIG.loadStep))}
            >
              Ver mais itens
            </button>
          )}
          {!jsonMode && friendlyExpanded && totalItems > effectiveInitial && effectiveVisibleCount > effectiveInitial && (
            <button type="button" className="secondary-button" onClick={() => setVisibleCount(effectiveInitial)}>
              Ver menos
            </button>
          )}
          {!jsonMode && hasStructuredValue && (
            <button type="button" className="secondary-button" onClick={() => setJsonMode(true)}>
              Visualizar conteudo completo
            </button>
          )}
        </div>

        {hasStructuredValue && !jsonMode && friendlyExpanded && (
          <div className="td-structured-viewer-meta">
            Exibindo {effectiveVisibleCount.toLocaleString('pt-BR')} de {totalItems.toLocaleString('pt-BR')} {Array.isArray(currentValue) ? 'itens' : 'campos'}
          </div>
        )}

        <div className="td-structured-viewer-body">
          {jsonMode
            ? <pre>{stringifyStructuredValue(currentValue)}</pre>
            : hasStructuredValue && !friendlyExpanded
              ? (
                <div className="td-structured-empty">
                  Conteudo retraido por padrao. Clique em Expandir conteudo para visualizar.
                </div>
              )
              : renderFriendlyContent()}
        </div>

        {copiedNotice && <footer>{copiedNotice}</footer>}
      </section>
    </div>
  );
};

const taskTTableBadgeTone = (tone) => {
  if (tone === 'success') return 'success';
  if (tone === 'error') return 'danger';
  if (tone === 'pending') return 'warning';
  if (tone === 'waiting') return 'info';
  return 'purple';
};

const createTaskTTableAction = (task, onTaskClick) => {
  const container = document.createElement('div');
  container.className = 'tt-custom-actions td-task-ttable-actions';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'tt-custom-action-btn tt-custom-action-btn--primary tt-custom-action-btn--text';
  button.title = `Ver ${taskTitle(task)}`;
  button.setAttribute('aria-label', `Ver task ${taskTitle(task)}`);
  const icon = document.createElement('span');
  icon.className = 'material-symbols-rounded g-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = 'visibility';
  const label = document.createElement('span');
  label.className = 'tt-custom-action-label';
  label.textContent = 'Ver task';
  button.append(icon, label);
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    onTaskClick(task);
  });
  container.appendChild(button);
  return container;
};

const createTaskTTableRelationTags = (task, column, demand) => {
  const values = taskRelationColumnValues(demand, column, taskTableCellRawValue(task, column));
  const container = document.createElement('div');
  container.className = 'searchable-select__trigger-tags td-task-table-relation-tags';
  container.title = values.join(', ');

  if (!values.length) {
    const empty = document.createElement('span');
    empty.className = 'td-task-table-relation-empty';
    empty.textContent = '-';
    container.appendChild(empty);
    return container;
  }

  const visibleValues = values.slice(0, 2);
  visibleValues.forEach((value) => {
    const tag = document.createElement('span');
    tag.className = 'searchable-select__tag';
    tag.title = value;
    tag.textContent = value;
    container.appendChild(tag);
  });

  if (values.length > visibleValues.length) {
    const remaining = document.createElement('span');
    remaining.className = 'searchable-select__tag searchable-select__tag--more';
    remaining.textContent = `+${values.length - visibleValues.length}`;
    container.appendChild(remaining);
  }

  return container;
};

const createTaskTTableListTag = (task, column, demand) => {
  const rawValue = taskTableCellRawValue(task, column);
  const displayValue = payloadValueLabel(rawValue);
  if (!displayValue) {
    const empty = document.createElement('span');
    empty.className = 'td-task-table-relation-empty';
    empty.textContent = '-';
    return empty;
  }

  const option = listOptionForValue(taskColumnSchemaDefinition(demand, column), rawValue);
  const tag = document.createElement('span');
  tag.className = 'td-task-table-list-tag';
  tag.title = displayValue;
  tag.style.setProperty('--td-list-color', option.color);

  const dot = document.createElement('i');
  dot.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.textContent = displayValue;
  tag.append(dot, label);
  return tag;
};

const taskTTableColumnType = (demand, column) => {
  if (column.key === TASK_TABLE_ACTION_COLUMN) return 'actions';
  if (column.key === '__status') return 'badge';
  if (isTaskRelationColumn(demand, column)) return 'tags';
  const type = taskColumnSchemaType(demand, column);
  if (['number', 'integer', 'decimal'].includes(type)) return 'number';
  if (type === 'date') return 'date';
  if (type === 'list') return 'badge';
  return 'text';
};

const TaskTTableView = ({ demand, tasks, canEdit, onTaskClick, onTaskSaved, onSort, onBulkAction, bulkTextScope, pagination }) => {
  const hostRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const paginationHostRef = useRef(null);
  if (!paginationHostRef.current && typeof document !== 'undefined') {
    paginationHostRef.current = document.createElement('div');
    paginationHostRef.current.className = 'td-ttable-pagination-content';
  }
  const latestRef = useRef({ demand, tasks, canEdit, onTaskClick, onTaskSaved, onSort, onBulkAction });
  const [editor, setEditor] = useState(null);
  const [editorValue, setEditorValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [headerContextMenu, setHeaderContextMenu] = useState(null);
  const [bulkTextModal, setBulkTextModal] = useState(null);
  const [bulkNotice, setBulkNotice] = useState('');
  const columns = useMemo(() => buildTaskTableColumns(demand, tasks), [demand, tasks]);
  const columnsSignature = useMemo(
    () => columns.map((column) => `${column.key}:${column.label}`).join('|'),
    [columns]
  );

  useEffect(() => {
    latestRef.current = { demand, tasks, canEdit, onTaskClick, onTaskSaved, onSort, onBulkAction };
  }, [canEdit, demand, onBulkAction, onSort, onTaskClick, onTaskSaved, tasks]);

  const openEditor = useCallback((task, column) => {
    if (!task || !column) return;
    if ([TASK_TABLE_ACTION_COLUMN, '__task'].includes(column.key)) {
      latestRef.current.onTaskClick(task);
      return;
    }
    const columnIndex = columns.findIndex((item) => item.key === column.key);
    if (!isTaskTableEditableColumn(demand, columns, columnIndex, canEdit)) {
      if (column.key !== '__status') latestRef.current.onTaskClick(task);
      return;
    }
    const rawValue = taskTableCellRawValue(task, column);
    setEditor({ task, column, type: column.key === '__status' ? 'status' : taskColumnSchemaType(demand, column) });
    setEditorValue(taskTableEditInputValue(rawValue));
    setNotice('');
  }, [canEdit, columns, demand]);

  const mappedRows = useMemo(() => tasks.map((task) => {
    const row = { id: String(task.id), __taskRecord: task };
    columns.forEach((column) => {
      row[column.key] = taskTableCellRawValue(task, column);
    });
    return row;
  }), [columns, tasks]);

  const showBulkNotice = useCallback((message, tone = 'info') => {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    setBulkNotice(`${tone}:${message}`);
    noticeTimerRef.current = window.setTimeout(
      () => setBulkNotice(''),
      tone === 'error' ? 5000 : 2400
    );
  }, []);

  const copyTextToClipboard = useCallback(async (text, successMessage) => {
    const value = String(text ?? '');
    if (!value) {
      showBulkNotice('A coluna não possui valores para copiar.', 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }
    showBulkNotice(successMessage, 'success');
  }, [showBulkNotice]);

  useEffect(() => {
    if (!headerContextMenu) return undefined;
    const close = () => setHeaderContextMenu(null);
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('click', close);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [headerContextMenu]);

  useEffect(() => () => {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
  }, []);

  const handleHeaderCopy = useCallback(() => {
    const column = headerContextMenu?.column;
    if (!column) return;
    const text = latestRef.current.tasks
      .map((task) => taskTableCellCopyValue(task, column, latestRef.current.demand))
      .join('\n');
    void copyTextToClipboard(text, `Coluna ${column.label} copiada.`);
    setHeaderContextMenu(null);
  }, [copyTextToClipboard, headerContextMenu]);

  const handleHeaderSort = useCallback(() => {
    const column = headerContextMenu?.column;
    const field = taskTableSortField(column);
    if (field) latestRef.current.onSort?.(field);
    setHeaderContextMenu(null);
  }, [headerContextMenu]);

  const openBulkTextModal = useCallback((operation) => {
    const columnIndex = headerContextMenu?.columnIndex;
    const column = columns[columnIndex];
    if (!column || !isTaskTableBulkTextColumn(demand, columns, columnIndex, canEdit)) {
      showBulkNotice('Esta coluna não permite alteração em massa de texto.', 'error');
      setHeaderContextMenu(null);
      return;
    }
    setBulkTextModal({
      operation,
      columnIndex,
      column,
      from: '',
      to: '',
      affix: '',
      running: false,
      error: ''
    });
    setHeaderContextMenu(null);
  }, [canEdit, columns, demand, headerContextMenu, showBulkNotice]);

  const executeBulkTextOperation = useCallback(async () => {
    if (!bulkTextModal || !demand?.id || bulkTextModal.running) return;
    const column = columns[bulkTextModal.columnIndex];
    if (!column || !isTaskTableBulkTextColumn(demand, columns, bulkTextModal.columnIndex, canEdit)) {
      setBulkTextModal((current) => current ? { ...current, error: 'Coluna inválida para operação em massa.' } : current);
      return;
    }

    const from = String(bulkTextModal.from ?? '');
    const to = String(bulkTextModal.to ?? '');
    const affix = String(bulkTextModal.affix ?? '');
    if (bulkTextModal.operation === 'replace' && !from && !to) {
      setBulkTextModal((current) => current ? { ...current, error: 'Preencha Trocar De ou Para.' } : current);
      return;
    }
    if (['prefix', 'suffix'].includes(bulkTextModal.operation) && !affix) {
      setBulkTextModal((current) => current ? { ...current, error: 'Informe o texto que deseja adicionar.' } : current);
      return;
    }

    setBulkTextModal((current) => current ? { ...current, running: true, error: '' } : current);
    try {
      const result = await api(`/api/demands/${encodeURIComponent(demand.id)}/tasks/text-bulk`, {
        method: 'POST',
        body: JSON.stringify({
          field_key: column.key,
          operation: bulkTextModal.operation,
          from,
          to,
          affix,
          search: bulkTextScope?.search || '',
          filters: bulkTextScope?.filters || { logic: 'and', conditions: [] }
        })
      });
      const matched = Number(result?.data?.matched_count || 0);
      setBulkTextModal(null);
      showBulkNotice(
        `${matched.toLocaleString('pt-BR')} task(s) enviadas para processamento. Você receberá uma notificação ao concluir.`,
        'success'
      );
    } catch (error) {
      setBulkTextModal((current) => current ? {
        ...current,
        running: false,
        error: error.message || 'Não foi possível solicitar a alteração em lote.'
      } : current);
    }
  }, [bulkTextModal, bulkTextScope, canEdit, columns, demand, showBulkNotice]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !window.TDemandTTable) return;
    const originalColumns = columns.map((column, columnIndex) => {
      const type = taskTTableColumnType(demand, column);
      const definition = taskColumnSchemaDefinition(demand, column);
      const badgeColors = {};
      if (column.key === '__status') {
        normalizeStatuses(demand).forEach((status) => {
          badgeColors[status.name] = taskTTableBadgeTone(statusToneForName(demand, status.name));
        });
      }
      return {
        field: column.key,
        title: column.label,
        type,
        width: taskTableColumnWidth(column),
        minWidth: taskTableColumnWidthBounds(column).min,
        maxWidth: taskTableColumnWidthBounds(column).max,
        sortable: column.key !== TASK_TABLE_ACTION_COLUMN,
        filterable: column.key !== TASK_TABLE_ACTION_COLUMN,
        groupable: column.key === '__status' || type === 'badge',
        exportable: column.key !== TASK_TABLE_ACTION_COLUMN,
        resizable: column.key !== TASK_TABLE_ACTION_COLUMN,
        editable: isTaskTableEditableColumn(demand, columns, columnIndex, canEdit),
        badgeColors,
        formatter: (_value, row) => taskTableCellValue(row.__taskRecord, column, demand),
        filterValue: (_value, row) => taskTableCellCopyValue(row.__taskRecord, column, demand),
        exportValue: (_value, row) => taskTableCellCopyValue(row.__taskRecord, column, demand),
        copyValue: (_value, row) => taskTableCellCopyValue(row.__taskRecord, column, demand),
        render: column.key === TASK_TABLE_ACTION_COLUMN
          ? (_value, row) => createTaskTTableAction(row.__taskRecord, (task) => latestRef.current.onTaskClick(task))
          : isTaskRelationColumn(demand, column)
            ? (_value, row) => createTaskTTableRelationTags(row.__taskRecord, column, demand)
            : isTaskListColumn(demand, column)
              ? (_value, row) => createTaskTTableListTag(row.__taskRecord, column, demand)
              : undefined
      };
    });
    window.TDemandTTable.mount(host, {
      key: `tasks-${demand?.id || 'none'}`,
      title: demand ? `Tasks · ${demandTitle(demand)}` : 'Tasks',
      data: mappedRows,
      columns: originalColumns,
      groupableFields: originalColumns.filter((column) => column.groupable).map((column) => column.field),
      bulkActions: canEdit ? [
        { id: 'move', name: 'Mover', icon: 'move', skipConfirm: true },
        { id: 'copy', name: 'Copiar', icon: 'copy', skipConfirm: true },
        { id: 'remove', name: 'Remover', icon: 'trash', danger: true, skipConfirm: true }
      ] : [],
      features: {
        search: false,
        filters: false,
        sorting: true,
        grouping: true,
        export: false,
        bulkActions: canEdit,
        copy: true,
        columnResize: true,
        darkModeToggle: false,
        help: false,
        charts: false,
        calendar: false,
        import: false
      },
      toolbar: { order: ['count', 'sort', 'group'] },
      paginationHost: paginationHostRef.current,
      emptyTitle: 'Nenhuma task nesta pagina',
      emptyDescription: 'Use a busca, os filtros ou navegue para outra pagina.',
      callbacks: {
        onHeaderContextMenu: (originalColumn, position) => {
          const columnIndex = columns.findIndex((item) => item.key === originalColumn?.field);
          const column = columns[columnIndex];
          if (!column) return false;
          const menuWidth = 226;
          const menuHeight = isTaskTableBulkTextColumn(demand, columns, columnIndex, canEdit) ? 232 : 96;
          setHeaderContextMenu({
            column,
            columnIndex,
            canSort: isTaskTableSortableColumn(column),
            canBulkText: isTaskTableBulkTextColumn(demand, columns, columnIndex, canEdit),
            left: Math.max(8, Math.min(Number(position?.clientX || 0), window.innerWidth - menuWidth - 8)),
            top: Math.max(8, Math.min(Number(position?.clientY || 0), window.innerHeight - menuHeight - 8))
          });
          return true;
        },
        onSortChange: (field, direction) => {
          const column = columns.find((item) => item.key === field);
          const sortField = taskTableSortField(column);
          if (sortField) latestRef.current.onSort?.(sortField, direction);
        },
        onBulkAction: (action, selectedIds) => {
          const taskIds = (Array.isArray(selectedIds) ? selectedIds : [])
            .map((item) => Number(item))
            .filter((item) => Number.isInteger(item) && item > 0);
          if (!taskIds.length) return;
          latestRef.current.onBulkAction?.(
            action?.id || action?.name,
            taskIds,
            () => window.TDemandTTable?.clearSelection(host)
          );
        },
        onRowDoubleClick: (row, originalColumn) => {
          const task = row?.__taskRecord;
          const column = columns.find((item) => item.key === originalColumn?.field);
          if (!task) return;
          if (!column) {
            latestRef.current.onTaskClick(task);
            return;
          }
          openEditor(task, column);
        }
      }
    });
    return () => {
      window.TDemandTTable?.destroy(host);
    };
  }, [canEdit, columns, columnsSignature, demand, mappedRows, openEditor]);

  const closeEditor = useCallback(() => {
    if (saving) return;
    setEditor(null);
    setEditorValue('');
    setNotice('');
  }, [saving]);

  const saveEditor = useCallback(async (event) => {
    event.preventDefault();
    if (!editor || !demand?.id || saving) return;
    const currentTask = latestRef.current.tasks.find((task) => String(task.id) === String(editor.task.id)) || editor.task;
    setSaving(true);
    setNotice('');
    try {
      let payload;
      if (editor.column.key === '__status') {
        payload = { status: String(editorValue || '').trim() };
      } else {
        const normalizedValue = normalizeTaskTableEditValue(editorValue, editor.column.key, editor.type);
        payload = {
          task_key: currentTask.task_key,
          status: currentTask.status,
          payload: {
            ...(currentTask.payload || {}),
            [editor.column.key]: normalizedValue
          }
        };
      }
      const response = await api(`/api/demands/${demand.id}/tasks/${currentTask.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      latestRef.current.onTaskSaved(response.data || {
        ...currentTask,
        ...payload,
        payload: payload.payload || currentTask.payload
      });
      setEditor(null);
      setEditorValue('');
    } catch (error) {
      setNotice(error.message || 'Nao foi possivel salvar o valor.');
    } finally {
      setSaving(false);
    }
  }, [demand?.id, editor, editorValue, saving]);

  const statusOptions = normalizeStatuses(demand);
  const listOptions = editor?.type === 'list'
    ? normalizeListOptions(taskColumnSchemaDefinition(demand, editor.column))
    : [];
  const textareaEditor = ['object', 'array', 'text', 'long_text'].includes(editor?.type);
  const [bulkNoticeTone, bulkNoticeText] = bulkNotice
    ? bulkNotice.split(/:(.*)/s).filter(Boolean)
    : ['', ''];

  return (
    <div className="td-task-ttable-shell">
      <div ref={hostRef} className="ttable-original-host td-task-ttable-host" data-ttable-id={`tasks-${demand?.id || 'none'}`} />
      {paginationHostRef.current && createPortal(
        <>
          <span className="td-ttable-pagination-summary">
            Mostrando {Number(pagination?.loaded || 0).toLocaleString('pt-BR')} de {Number(pagination?.total || 0).toLocaleString('pt-BR')}
          </span>
          <div className="td-ttable-pagination-actions">
            <label>
              Linhas
              <select value={pagination?.pageSize || TASK_TABLE_DEFAULT_PAGE_SIZE} onChange={pagination?.onPageSizeChange}>
                {TASK_TABLE_PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="tt-page-btn"
              aria-label="Pagina anterior"
              disabled={Number(pagination?.page || 1) <= 1 || pagination?.loading}
              onClick={pagination?.onPrevious}
            >
              <span className="material-symbols-rounded" aria-hidden="true">chevron_left</span>
            </button>
            <strong>{Number(pagination?.page || 1).toLocaleString('pt-BR')} / {Number(pagination?.pageCount || 1).toLocaleString('pt-BR')}</strong>
            <button
              type="button"
              className="tt-page-btn"
              aria-label="Proxima pagina"
              disabled={Number(pagination?.page || 1) >= Number(pagination?.pageCount || 1) || pagination?.loading}
              onClick={pagination?.onNext}
            >
              <span className="material-symbols-rounded" aria-hidden="true">chevron_right</span>
            </button>
          </div>
        </>,
        paginationHostRef.current
      )}
      {headerContextMenu && typeof document !== 'undefined' && createPortal(
        <div
          className="td-canvas-context-menu td-ttable-header-context-menu"
          style={{ left: `${headerContextMenu.left}px`, top: `${headerContextMenu.top}px` }}
          onClick={(event) => event.stopPropagation()}
          role="menu"
          aria-label={`Ações da coluna ${headerContextMenu.column?.label || ''}`}
        >
          <button type="button" onClick={handleHeaderCopy} role="menuitem">
            <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
            Copiar
          </button>
          {headerContextMenu.canSort && (
            <button type="button" onClick={handleHeaderSort} role="menuitem">
              <span className="material-symbols-rounded" aria-hidden="true">sort</span>
              Ordenar
            </button>
          )}
          {headerContextMenu.canBulkText && (
            <>
              <button type="button" onClick={() => openBulkTextModal('replace')} role="menuitem">
                <span className="material-symbols-rounded" aria-hidden="true">find_replace</span>
                Trocar texto
              </button>
              <button type="button" onClick={() => openBulkTextModal('prefix')} role="menuitem">
                <span className="material-symbols-rounded" aria-hidden="true">first_page</span>
                Adicionar na Frente
              </button>
              <button type="button" onClick={() => openBulkTextModal('suffix')} role="menuitem">
                <span className="material-symbols-rounded" aria-hidden="true">last_page</span>
                Adicionar no Final
              </button>
            </>
          )}
        </div>,
        document.body
      )}
      {bulkTextModal && typeof document !== 'undefined' && createPortal(
        <div className="td-table-bulk-backdrop" role="presentation" onClick={() => !bulkTextModal.running && setBulkTextModal(null)}>
          <form
            className="td-table-bulk-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Operação em massa na coluna"
            onClick={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              void executeBulkTextOperation();
            }}
          >
            <header>
              <div>
                <span>{bulkTextModal.column?.label}</span>
                <strong>
                  {bulkTextModal.operation === 'replace'
                    ? 'Trocar texto'
                    : bulkTextModal.operation === 'prefix'
                      ? 'Adicionar na Frente'
                      : 'Adicionar no Final'}
                </strong>
                <p>Processa em background somente as Tasks que correspondem ao filtro atual no momento da solicitação. Cada alteração gera log e timeline.</p>
              </div>
              <button type="button" className="icon-button" onClick={() => setBulkTextModal(null)} disabled={bulkTextModal.running} aria-label="Fechar">
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </header>
            <div className="td-table-bulk-body">
              {bulkTextModal.operation === 'replace' ? (
                <>
                  <label className="modal-field">
                    <span>Trocar De</span>
                    <input
                      value={bulkTextModal.from}
                      onChange={(event) => setBulkTextModal((current) => current ? { ...current, from: event.target.value, error: '' } : current)}
                      placeholder="Texto a localizar"
                      disabled={bulkTextModal.running}
                      autoFocus
                    />
                  </label>
                  <label className="modal-field">
                    <span>Para</span>
                    <input
                      value={bulkTextModal.to}
                      onChange={(event) => setBulkTextModal((current) => current ? { ...current, to: event.target.value, error: '' } : current)}
                      placeholder="Novo texto ou vazio para remover"
                      disabled={bulkTextModal.running}
                    />
                  </label>
                  <p className="td-table-bulk-hint">Se <strong>Trocar De</strong> estiver vazio, <strong>Para</strong> preenche apenas células vazias. Se <strong>Para</strong> estiver vazio, os matches serão removidos.</p>
                </>
              ) : (
                <label className="modal-field modal-field-wide">
                  <span>{bulkTextModal.operation === 'prefix' ? 'Prefixo' : 'Sufixo'}</span>
                  <input
                    value={bulkTextModal.affix}
                    onChange={(event) => setBulkTextModal((current) => current ? { ...current, affix: event.target.value, error: '' } : current)}
                    placeholder={bulkTextModal.operation === 'prefix' ? 'Texto antes do valor atual' : 'Texto depois do valor atual'}
                    disabled={bulkTextModal.running}
                    autoFocus
                  />
                </label>
              )}
              {bulkTextModal.error && <div className="td-table-bulk-error">{bulkTextModal.error}</div>}
            </div>
            <footer>
              <button type="button" className="secondary-button" onClick={() => setBulkTextModal(null)} disabled={bulkTextModal.running}>Cancelar</button>
              <button type="submit" className="primary-button" disabled={bulkTextModal.running}>
                <span className="material-symbols-rounded" aria-hidden="true">{bulkTextModal.running ? 'sync' : 'done_all'}</span>
                {bulkTextModal.running ? 'Aplicando...' : 'Aplicar alteração'}
              </button>
            </footer>
          </form>
        </div>,
        document.body
      )}
      {editor && (
        <div className="td-ttable-editor-backdrop" role="presentation" onClick={closeEditor}>
          <form className="td-ttable-editor" role="dialog" aria-modal="true" aria-label={`Editar ${editor.column.label}`} onClick={(event) => event.stopPropagation()} onSubmit={saveEditor}>
            <header>
              <div className="td-ttable-editor-icon"><span className="material-symbols-rounded" aria-hidden="true">edit_square</span></div>
              <div>
                <span>Edicao rapida</span>
                <h3>{editor.column.label}</h3>
                <p>{taskTitle(editor.task)}</p>
              </div>
              <button type="button" className="icon-button" aria-label="Fechar" onClick={closeEditor} disabled={saving}>
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </header>
            <label className="modal-field">
              <span>Novo valor</span>
              {editor.column.key === '__status' ? (
                <select value={editorValue} onChange={(event) => setEditorValue(event.target.value)} autoFocus>
                  {statusOptions.map((status) => <option key={status.name} value={status.name}>{status.name}</option>)}
                </select>
              ) : editor.type === 'list' ? (
                <select value={editorValue} onChange={(event) => setEditorValue(event.target.value)} autoFocus>
                  <option value="">Selecione</option>
                  {listOptions.map((option) => <option key={option.name} value={option.name}>{option.name}</option>)}
                </select>
              ) : editor.type === 'boolean' ? (
                <select value={editorValue} onChange={(event) => setEditorValue(event.target.value)} autoFocus>
                  <option value="true">Sim</option>
                  <option value="false">Nao</option>
                </select>
              ) : textareaEditor ? (
                <textarea value={editorValue} onChange={(event) => setEditorValue(event.target.value)} rows={editor.type === 'object' || editor.type === 'array' ? 10 : 5} autoFocus />
              ) : (
                <input
                  type={['number', 'integer', 'decimal'].includes(editor.type) ? 'number' : editor.type === 'date' ? 'date' : 'text'}
                  value={editorValue}
                  onChange={(event) => setEditorValue(event.target.value)}
                  autoFocus
                />
              )}
            </label>
            {notice && <div className="notice inline-notice error">{notice}</div>}
            <footer>
              <button type="button" className="secondary-button" onClick={closeEditor} disabled={saving}>Cancelar</button>
              <button type="submit" className="primary-button" disabled={saving}>
                <span className="material-symbols-rounded" aria-hidden="true">save</span>
                {saving ? 'Salvando...' : 'Salvar valor'}
              </button>
            </footer>
          </form>
        </div>
      )}
      {bulkNoticeText && (
        <div className={`td-canvas-save-notice is-${bulkNoticeTone || 'info'}`}>
          <span className="material-symbols-rounded" aria-hidden="true">
            {bulkNoticeTone === 'error' ? 'error' : bulkNoticeTone === 'success' ? 'check_circle' : 'sync'}
          </span>
          {bulkNoticeText}
        </div>
      )}
    </div>
  );
};

const TaskCanvasTableView = ({ demand, tasks, canEdit, sortState, onSort, onTaskClick, onTaskSaved, onTaskDeleteRequest }) => {
  const panelRef = useRef(null);
  const scrollRef = useRef(null);
  const canvasRef = useRef(null);
  const inputRef = useRef(null);
  const rafRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [scroll, setScroll] = useState({ left: 0, top: 0 });
  const [selection, setSelection] = useState(null);
  const [editing, setEditing] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [valueViewer, setValueViewer] = useState(null);
  const [saveNotice, setSaveNotice] = useState('');
  const [customColumnWidths, setCustomColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [bulkTextModal, setBulkTextModal] = useState(null);
  const columns = useMemo(() => buildTaskTableColumns(demand, tasks), [demand, tasks]);
  const statusItems = useMemo(() => normalizeStatuses(demand), [demand]);
  const statusOptions = useMemo(() => statusItems.map((status) => status.name), [statusItems]);
  const columnWidths = useMemo(() => columns.map((column) => {
    const key = taskTableColumnWidthKey(column);
    return clampTaskTableColumnWidth(column, customColumnWidths[key] || taskTableColumnWidth(column));
  }), [columns, customColumnWidths]);
  const columnOffsets = useMemo(() => {
    const offsets = [];
    let current = 0;
    columnWidths.forEach((width) => {
      offsets.push(current);
      current += width;
    });
    return offsets;
  }, [columnWidths]);
  const totalColumnsWidth = useMemo(() => columnWidths.reduce((sum, width) => sum + width, 0), [columnWidths]);
  const totalWidth = TASK_CANVAS_ROW_NUMBER_WIDTH + totalColumnsWidth;
  const totalHeight = TASK_CANVAS_HEADER_HEIGHT + Math.max(tasks.length, 1) * TASK_CANVAS_ROW_HEIGHT;

  const showSaveNotice = useCallback((message, tone = 'info') => {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    setSaveNotice(`${tone}:${message}`);
    noticeTimerRef.current = window.setTimeout(() => setSaveNotice(''), tone === 'error' ? 4200 : 1800);
  }, []);

  const copyTextToClipboard = useCallback(async (text, successMessage = 'Copiado.') => {
    const value = String(text ?? '');
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      showSaveNotice(successMessage, 'success');
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      showSaveNotice(successMessage, 'success');
    }
  }, [showSaveNotice]);

  const openStructuredCellViewer = useCallback((rowIndex, columnIndex) => {
    const task = tasks[rowIndex];
    const column = columns[columnIndex];
    if (!task || !column) return;
    const rawValue = taskTableCellRawValue(task, column);
    if (!isStructuredCellValue(demand, column, rawValue)) return;
    setValueViewer({
      rowIndex,
      columnIndex,
      title: `${column.label} - ${taskTitle(task)}`,
      value: rawValue
    });
  }, [columns, demand, tasks]);

  useEffect(() => () => {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    const allowed = new Set(columns.map(taskTableColumnWidthKey));
    setCustomColumnWidths((current) => {
      const next = {};
      Object.entries(current).forEach(([key, width]) => {
        if (allowed.has(key)) next[key] = width;
      });
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [columns]);

  useEffect(() => {
    if (!panelRef.current) return undefined;
    const resize = () => {
      const rect = panelRef.current.getBoundingClientRect();
      setViewport({ width: Math.max(0, rect.width), height: Math.max(0, rect.height) });
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setSelection((current) => {
      if (!current) return current;
      return current.rowIndex < tasks.length && current.columnIndex < columns.length ? current : null;
    });
    setEditing((current) => {
      if (!current) return current;
      return current.rowIndex < tasks.length && current.columnIndex < columns.length ? current : null;
    });
    setContextMenu(null);
  }, [columns.length, tasks.length]);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setContextMenu(null);
    };
    window.addEventListener('click', close);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [contextMenu]);

  useEffect(() => {
    if (!resizingColumn) return undefined;
    const handlePointerMove = (event) => {
      event.preventDefault();
      const delta = event.clientX - resizingColumn.startClientX;
      const nextWidth = clampTaskTableColumnWidth(resizingColumn.column, resizingColumn.startWidth + delta);
      const key = taskTableColumnWidthKey(resizingColumn.column);
      setCustomColumnWidths((current) => (
        current[key] === nextWidth ? current : { ...current, [key]: nextWidth }
      ));
    };
    const handlePointerUp = () => {
      suppressClickRef.current = true;
      setResizingColumn(null);
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    };
    document.body.classList.add('td-table-column-resizing');
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      document.body.classList.remove('td-table-column-resizing');
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [resizingColumn]);

  const visibleColumns = useMemo(() => {
    if (!columns.length) return [];
    const viewportStart = Math.max(0, scroll.left - TASK_CANVAS_ROW_NUMBER_WIDTH);
    const viewportEnd = scroll.left + viewport.width;
    let startIndex = 0;
    while (startIndex < columns.length - 1 && columnOffsets[startIndex] + columnWidths[startIndex] < viewportStart) startIndex += 1;
    let endIndex = startIndex;
    while (endIndex < columns.length && columnOffsets[endIndex] <= viewportEnd) endIndex += 1;
    startIndex = Math.max(0, startIndex - TASK_CANVAS_OVERSCAN_COLS);
    endIndex = Math.min(columns.length, endIndex + TASK_CANVAS_OVERSCAN_COLS);
    return columns.slice(startIndex, endIndex).map((column, index) => ({
      column,
      index: startIndex + index,
      width: columnWidths[startIndex + index],
      offset: columnOffsets[startIndex + index]
    }));
  }, [columnOffsets, columnWidths, columns, scroll.left, viewport.width]);

  const visibleRows = useMemo(() => {
    const bodyTop = Math.max(0, scroll.top - TASK_CANVAS_HEADER_HEIGHT);
    const start = Math.max(0, Math.floor(bodyTop / TASK_CANVAS_ROW_HEIGHT) - TASK_CANVAS_OVERSCAN_ROWS);
    const end = Math.min(
      tasks.length,
      Math.ceil((bodyTop + viewport.height) / TASK_CANVAS_ROW_HEIGHT) + TASK_CANVAS_OVERSCAN_ROWS
    );
    return { start, end };
  }, [scroll.top, tasks.length, viewport.height]);

  const getCellAtPoint = useCallback((clientX, clientY) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < TASK_CANVAS_ROW_NUMBER_WIDTH) {
      if (y < TASK_CANVAS_HEADER_HEIGHT) return { type: 'corner' };
      const rowIndex = Math.floor((y - TASK_CANVAS_HEADER_HEIGHT + scroll.top) / TASK_CANVAS_ROW_HEIGHT);
      return rowIndex >= 0 && rowIndex < tasks.length ? { type: 'row', rowIndex } : null;
    }
    const gridX = x - TASK_CANVAS_ROW_NUMBER_WIDTH + scroll.left;
    let columnIndex = -1;
    for (let index = 0; index < columns.length; index += 1) {
      if (gridX >= columnOffsets[index] && gridX < columnOffsets[index] + columnWidths[index]) {
        columnIndex = index;
        break;
      }
    }
    if (columnIndex === -1) return null;
    if (y < TASK_CANVAS_HEADER_HEIGHT) return { type: 'header', columnIndex };
    const rowIndex = Math.floor((y - TASK_CANVAS_HEADER_HEIGHT + scroll.top) / TASK_CANVAS_ROW_HEIGHT);
    if (rowIndex < 0 || rowIndex >= tasks.length) return null;
    return { type: 'cell', rowIndex, columnIndex };
  }, [columnOffsets, columnWidths, columns.length, scroll.left, scroll.top, tasks.length]);

  const getColumnResizeAtPoint = useCallback((clientX, clientY) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (y < 0 || y > TASK_CANVAS_HEADER_HEIGHT) return null;
    if (x < TASK_CANVAS_ROW_NUMBER_WIDTH) return null;
    const gridX = x - TASK_CANVAS_ROW_NUMBER_WIDTH + scroll.left;
    for (let index = 0; index < columns.length; index += 1) {
      const edge = columnOffsets[index] + columnWidths[index];
      if (Math.abs(gridX - edge) <= TASK_CANVAS_RESIZE_HANDLE_WIDTH) {
        return {
          type: 'column-resize',
          columnIndex: index,
          column: columns[index],
          startWidth: columnWidths[index]
        };
      }
    }
    return null;
  }, [columnOffsets, columnWidths, columns, scroll.left]);

  const commitEdit = useCallback(async (shouldSave = true, overrideValue) => {
    const current = overrideValue === undefined ? editing : { ...editing, value: overrideValue };
    if (!current) return;
    setEditing(null);
    if (!shouldSave || !canEdit || !demand?.id) return;
    const task = tasks[current.rowIndex];
    const column = columns[current.columnIndex];
    if (!task || !column || !isTaskTableEditableColumn(demand, columns, current.columnIndex, canEdit)) return;

    if (column.key === '__status') {
      const nextStatus = String(current.value || '').trim();
      if (!statusOptions.includes(nextStatus)) {
        showSaveNotice('Status inválido para esta demanda.', 'error');
        return;
      }
      if (task.status === nextStatus) return;

      showSaveNotice('Salvando status...', 'info');
      try {
        const response = await api(`/api/demands/${demand.id}/tasks/${task.id}`, {
          method: 'PUT',
          body: JSON.stringify({ status: nextStatus })
        });
        const savedTask = response.data || { ...task, status: nextStatus };
        onTaskSaved?.(savedTask);
        showSaveNotice('Status salvo.', 'success');
      } catch (error) {
        showSaveNotice(error.message || 'Não foi possível salvar o status.', 'error');
      }
      return;
    }

    if (isTaskListColumn(demand, column)) {
      const nextValue = String(current.value || '').trim();
      const options = normalizeListOptions(taskColumnSchemaDefinition(demand, column));
      if (options.length && !options.some((option) => option.name === nextValue)) {
        showSaveNotice('Item inválido para esta lista.', 'error');
        return;
      }
      const oldPayload = task.payload || {};
      if (areTaskTableValuesEqual(oldPayload[column.key], nextValue)) return;

      showSaveNotice('Salvando item...', 'info');
      try {
        const response = await api(`/api/demands/${demand.id}/tasks/${task.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            task_key: task.task_key,
            status: task.status,
            payload: {
              ...oldPayload,
              [column.key]: nextValue
            }
          })
        });
        const savedTask = response.data || {
          ...task,
          payload: {
            ...oldPayload,
            [column.key]: nextValue
          }
        };
        onTaskSaved?.(savedTask);
        showSaveNotice('Item salvo.', 'success');
      } catch (error) {
        showSaveNotice(error.message || 'Não foi possível salvar o item.', 'error');
      }
      return;
    }

    let normalizedValue;
    try {
      normalizedValue = normalizeTaskTableEditValue(current.value, column.key, taskColumnSchemaType(demand, column));
    } catch (error) {
      showSaveNotice(error.message, 'error');
      return;
    }

    const oldPayload = task.payload || {};
    if (areTaskTableValuesEqual(oldPayload[column.key], normalizedValue)) return;

    showSaveNotice('Salvando celula...', 'info');
    try {
      const response = await api(`/api/demands/${demand.id}/tasks/${task.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          task_key: task.task_key,
          status: task.status,
          payload: {
            ...oldPayload,
            [column.key]: normalizedValue
          }
        })
      });
      const savedTask = response.data || {
        ...task,
        payload: {
          ...oldPayload,
          [column.key]: normalizedValue
        }
      };
      onTaskSaved?.(savedTask);
      showSaveNotice('Celula salva.', 'success');
    } catch (error) {
      showSaveNotice(error.message || 'Não foi possível salvar a célula.', 'error');
    }
  }, [canEdit, columns, demand, editing, onTaskSaved, showSaveNotice, statusOptions, tasks]);

  useEffect(() => {
    if (!editing) return;
    if (['status', 'list'].includes(editing.kind)) return;
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing?.kind, editing?.rowIndex, editing?.columnIndex]);

  useEffect(() => {
    if (!['status', 'list'].includes(editing?.kind)) return undefined;
    const closeStatusDropdown = () => setEditing(null);
    const closeStatusDropdownOnEscape = (event) => {
      if (event.key === 'Escape') setEditing(null);
    };
    window.addEventListener('click', closeStatusDropdown);
    window.addEventListener('keydown', closeStatusDropdownOnEscape);
    return () => {
      window.removeEventListener('click', closeStatusDropdown);
      window.removeEventListener('keydown', closeStatusDropdownOnEscape);
    };
  }, [editing?.kind]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const panel = panelRef.current;
    if (!canvas || !panel || !viewport.width || !viewport.height) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const nextWidth = Math.floor(viewport.width * dpr);
    const nextHeight = Math.floor(viewport.height * dpr);
    if (canvas.width !== nextWidth) canvas.width = nextWidth;
    if (canvas.height !== nextHeight) canvas.height = nextHeight;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;

    const theme = canvasThemeFromElement(panel);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, viewport.width, viewport.height);
    ctx.fillStyle = theme.surface;
    ctx.fillRect(0, 0, viewport.width, viewport.height);

    const bodyHeight = Math.max(0, viewport.height - TASK_CANVAS_HEADER_HEIGHT);
    ctx.fillStyle = theme.controlBg;
    ctx.fillRect(0, 0, viewport.width, TASK_CANVAS_HEADER_HEIGHT);
    ctx.fillStyle = theme.surface2;
    ctx.fillRect(0, 0, TASK_CANVAS_ROW_NUMBER_WIDTH, viewport.height);

    ctx.strokeStyle = theme.tableGridStrong;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, TASK_CANVAS_HEADER_HEIGHT + 0.5);
    ctx.lineTo(viewport.width, TASK_CANVAS_HEADER_HEIGHT + 0.5);
    ctx.moveTo(TASK_CANVAS_ROW_NUMBER_WIDTH + 0.5, 0);
    ctx.lineTo(TASK_CANVAS_ROW_NUMBER_WIDTH + 0.5, viewport.height);
    ctx.stroke();

    ctx.save();
    ctx.beginPath();
    ctx.rect(TASK_CANVAS_ROW_NUMBER_WIDTH, 0, Math.max(0, viewport.width - TASK_CANVAS_ROW_NUMBER_WIDTH), viewport.height);
    ctx.clip();

    ctx.font = '800 11px Inter, system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    visibleColumns.forEach(({ column, index, width, offset }) => {
      const x = TASK_CANVAS_ROW_NUMBER_WIDTH + offset - scroll.left;
      ctx.fillStyle = theme.controlBg;
      ctx.fillRect(x, 0, width, TASK_CANVAS_HEADER_HEIGHT);
      ctx.strokeStyle = theme.tableGridColumn;
      ctx.beginPath();
      ctx.moveTo(x + width + 0.5, 0);
      ctx.lineTo(x + width + 0.5, viewport.height);
      ctx.stroke();
      const isResizingThisColumn = resizingColumn?.columnIndex === index;
      if (isResizingThisColumn) {
        ctx.strokeStyle = theme.tableResizeActive;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x + width + 0.5, 0);
        ctx.lineTo(x + width + 0.5, viewport.height);
        ctx.stroke();
        ctx.lineWidth = 1;
      }
      ctx.fillStyle = column.key === TASK_TABLE_ACTION_COLUMN ? theme.primary : theme.panelTitle;
      const isSorted = sortState?.field && taskTableSortField(column) === sortState.field;
      ctx.fillText(truncateCanvasText(ctx, column.label, width - (isTaskTableSortableColumn(column) ? 38 : 22)), x + 11, TASK_CANVAS_HEADER_HEIGHT / 2);
      if (isTaskTableSortableColumn(column)) {
        ctx.fillStyle = isSorted ? theme.primary : theme.muted;
        ctx.font = '900 12px Inter, system-ui, sans-serif';
        ctx.fillText(isSorted && sortState.direction === 'asc' ? '▲' : '▼', x + width - 22, TASK_CANVAS_HEADER_HEIGHT / 2);
      }
      drawRoundRect(
        ctx,
        x + width - 3,
        10,
        2,
        TASK_CANVAS_HEADER_HEIGHT - 20,
        1,
        isResizingThisColumn ? theme.tableResizeActive : theme.tableResizeHandle
      );
      if (selection?.columnIndex === index) {
        ctx.fillStyle = 'rgba(37, 99, 235, 0.07)';
        ctx.fillRect(x, TASK_CANVAS_HEADER_HEIGHT, width, bodyHeight);
      }
    });

    for (let rowIndex = visibleRows.start; rowIndex < visibleRows.end; rowIndex += 1) {
      const task = tasks[rowIndex];
      const y = TASK_CANVAS_HEADER_HEIGHT + rowIndex * TASK_CANVAS_ROW_HEIGHT - scroll.top;
      ctx.fillStyle = rowIndex % 2 === 0 ? theme.surface : theme.tableRowAlt;
      ctx.fillRect(TASK_CANVAS_ROW_NUMBER_WIDTH, y, Math.max(0, viewport.width - TASK_CANVAS_ROW_NUMBER_WIDTH), TASK_CANVAS_ROW_HEIGHT);
      ctx.strokeStyle = theme.tableGridRow;
      ctx.beginPath();
      ctx.moveTo(0, y + TASK_CANVAS_ROW_HEIGHT + 0.5);
      ctx.lineTo(viewport.width, y + TASK_CANVAS_ROW_HEIGHT + 0.5);
      ctx.stroke();

      visibleColumns.forEach(({ column, index, width, offset }) => {
        const x = TASK_CANVAS_ROW_NUMBER_WIDTH + offset - scroll.left;
        const value = taskTableCellValue(task, column, demand);
        ctx.strokeStyle = theme.tableGridColumn;
        ctx.beginPath();
        ctx.moveTo(x + width + 0.5, y);
        ctx.lineTo(x + width + 0.5, y + TASK_CANVAS_ROW_HEIGHT);
        ctx.stroke();
        if (selection?.rowIndex === rowIndex && selection?.columnIndex === index) {
          drawRoundRect(ctx, x + 2, y + 2, width - 4, TASK_CANVAS_ROW_HEIGHT - 4, 5, 'rgba(37, 99, 235, 0.08)', theme.primary);
        }
        if (column.key === TASK_TABLE_ACTION_COLUMN) {
          const buttonWidth = Math.min(100, width - 16);
          const buttonX = x + 8;
          const buttonY = y + 5;
          drawRoundRect(ctx, buttonX, buttonY, buttonWidth, 26, 13, theme.tableActionBg, theme.tableActionBorder);
          drawCanvasEyeIcon(ctx, buttonX + 9, buttonY + 5, theme.primary);
          ctx.fillStyle = theme.primary;
          ctx.font = '850 10.5px Inter, system-ui, sans-serif';
          ctx.fillText('Ver task', buttonX + 31, buttonY + 13);
          return;
        }
        if (column.key === '__status') {
          const tone = statusToneForTask(demand, task);
          const colors = canvasStatusColors(theme, tone);
          ctx.font = '800 10.5px Inter, system-ui, sans-serif';
          const label = truncateCanvasText(ctx, value || '-', width - 26);
          const pillWidth = Math.min(width - 16, Math.max(54, ctx.measureText(label).width + 20));
          drawRoundRect(ctx, x + 8, y + 7, pillWidth, 22, 11, colors[1], '');
          ctx.fillStyle = colors[0];
          ctx.fillText(label, x + 18, y + TASK_CANVAS_ROW_HEIGHT / 2);
          if (canEdit) {
            ctx.fillStyle = theme.muted;
            ctx.font = '900 10px Inter, system-ui, sans-serif';
            ctx.fillText('⌄', x + Math.min(width - 24, pillWidth + 14), y + TASK_CANVAS_ROW_HEIGHT / 2);
          }
          return;
        }
        if (isTaskListColumn(demand, column)) {
          const option = listOptionForValue(taskColumnSchemaDefinition(demand, column), value);
          ctx.font = '800 10.5px Inter, system-ui, sans-serif';
          const label = truncateCanvasText(ctx, option.name || '-', width - 34);
          const pillWidth = Math.min(width - 16, Math.max(58, ctx.measureText(label).width + 28));
          drawRoundRect(ctx, x + 8, y + 7, pillWidth, 22, 11, `${option.color}22`, `${option.color}55`);
          ctx.fillStyle = option.color;
          ctx.beginPath();
          ctx.arc(x + 19, y + TASK_CANVAS_ROW_HEIGHT / 2, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = theme.text;
          ctx.fillText(label, x + 28, y + TASK_CANVAS_ROW_HEIGHT / 2);
          if (canEdit) {
            ctx.fillStyle = theme.muted;
            ctx.font = '900 10px Inter, system-ui, sans-serif';
            ctx.fillText('⌄', x + Math.min(width - 24, pillWidth + 14), y + TASK_CANVAS_ROW_HEIGHT / 2);
          }
          return;
        }
        ctx.font = column.key === '__task'
          ? '800 11.5px JetBrains Mono, SFMono-Regular, monospace'
          : '600 11.5px Inter, system-ui, sans-serif';
        ctx.fillStyle = column.key === '__task' ? theme.panelTitle : theme.text;
        ctx.fillText(truncateCanvasText(ctx, value || '-', width - 20), x + 10, y + TASK_CANVAS_ROW_HEIGHT / 2);
      });
    }
    ctx.restore();

    ctx.fillStyle = theme.surface2;
    ctx.fillRect(0, 0, TASK_CANVAS_ROW_NUMBER_WIDTH, TASK_CANVAS_HEADER_HEIGHT);
    ctx.fillStyle = theme.muted;
    ctx.font = '900 10px Inter, system-ui, sans-serif';
    ctx.fillText('#', 22, TASK_CANVAS_HEADER_HEIGHT / 2);
    for (let rowIndex = visibleRows.start; rowIndex < visibleRows.end; rowIndex += 1) {
      const y = TASK_CANVAS_HEADER_HEIGHT + rowIndex * TASK_CANVAS_ROW_HEIGHT - scroll.top;
      ctx.fillStyle = rowIndex % 2 === 0 ? theme.surface2 : theme.controlBg;
      ctx.fillRect(0, y, TASK_CANVAS_ROW_NUMBER_WIDTH, TASK_CANVAS_ROW_HEIGHT);
      ctx.fillStyle = selection?.rowIndex === rowIndex ? theme.primary : theme.muted;
      ctx.font = '800 10.5px Inter, system-ui, sans-serif';
      ctx.fillText(String(rowIndex + 1), 16, y + TASK_CANVAS_ROW_HEIGHT / 2);
    }
  }, [canEdit, columns, columnWidths, demand, resizingColumn, scroll.left, scroll.top, selection, sortState, tasks, viewport.height, viewport.width, visibleColumns, visibleRows]);

  const handleScroll = useCallback((event) => {
    setContextMenu(null);
    setEditing((current) => ['status', 'list'].includes(current?.kind) ? null : current);
    const target = event.currentTarget;
    if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    rafRef.current = window.requestAnimationFrame(() => {
      setScroll({ left: target.scrollLeft, top: target.scrollTop });
    });
  }, []);

  const handleCanvasPointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    const resizeHit = getColumnResizeAtPoint(event.clientX, event.clientY);
    if (!resizeHit) return;
    event.preventDefault();
    event.stopPropagation();
    suppressClickRef.current = true;
    setContextMenu(null);
    setEditing(null);
    setSelection(null);
    setResizingColumn({
      columnIndex: resizeHit.columnIndex,
      column: resizeHit.column,
      startWidth: resizeHit.startWidth,
      startClientX: event.clientX
    });
  }, [getColumnResizeAtPoint]);

  const beginCellEdit = useCallback((rowIndex, columnIndex, showReadonlyNotice = true) => {
    const task = tasks[rowIndex];
    const column = columns[columnIndex];
    if (!task || !column) return false;
    if (column.key === TASK_TABLE_ACTION_COLUMN) {
      onTaskClick(task);
      return false;
    }
    if (!isTaskTableEditableColumn(demand, columns, columnIndex, canEdit)) {
      if (showReadonlyNotice) showSaveNotice('Esta coluna e somente leitura.', 'error');
      return false;
    }
    const listColumn = isTaskListColumn(demand, column);
    const currentValue = column.key === '__status' ? task.status : task.payload?.[column.key];
    setSelection({ rowIndex, columnIndex });
    setEditing({
      kind: column.key === '__status' ? 'status' : listColumn ? 'list' : 'payload',
      rowIndex,
      columnIndex,
      value: column.key === '__status'
        ? currentValue || statusOptions[0] || ''
        : listColumn
          ? taskTableEditInputValue(currentValue) || normalizeListOptions(taskColumnSchemaDefinition(demand, column))[0]?.name || ''
          : taskTableEditInputValue(currentValue)
    });
    return true;
  }, [canEdit, columns, demand, onTaskClick, showSaveNotice, statusOptions, tasks]);

  const handleCanvasClick = useCallback((event) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setContextMenu(null);
    const hit = getCellAtPoint(event.clientX, event.clientY);
    if (!hit) return;
    if (hit.type === 'header') {
      const column = columns[hit.columnIndex];
      if (isTaskTableSortableColumn(column)) {
        setSelection(null);
        setEditing(null);
        onSort?.(taskTableSortField(column));
      }
      return;
    }
    if (hit.type === 'cell') {
      const task = tasks[hit.rowIndex];
      const column = columns[hit.columnIndex];
      setSelection({ rowIndex: hit.rowIndex, columnIndex: hit.columnIndex });
      if (column?.key === TASK_TABLE_ACTION_COLUMN && task) onTaskClick(task);
      if (column?.key === '__status' && task && isTaskTableEditableColumn(demand, columns, hit.columnIndex, canEdit)) {
        setEditing({
          kind: 'status',
          rowIndex: hit.rowIndex,
          columnIndex: hit.columnIndex,
          value: task.status || statusOptions[0] || ''
        });
      }
      if (column && isTaskListColumn(demand, column) && task && isTaskTableEditableColumn(demand, columns, hit.columnIndex, canEdit)) {
        const options = normalizeListOptions(taskColumnSchemaDefinition(demand, column));
        setEditing({
          kind: 'list',
          rowIndex: hit.rowIndex,
          columnIndex: hit.columnIndex,
          value: taskTableEditInputValue(task.payload?.[column.key]) || options[0]?.name || ''
        });
      }
    }
  }, [canEdit, columns, demand, getCellAtPoint, onSort, onTaskClick, statusOptions, tasks]);

  const handleCanvasContextMenu = useCallback((event) => {
    event.preventDefault();
    const hit = getCellAtPoint(event.clientX, event.clientY);
    if (!hit || !['cell', 'row', 'header'].includes(hit.type)) {
      setContextMenu(null);
      return;
    }

    const panelRect = panelRef.current?.getBoundingClientRect();
    const x = panelRect ? event.clientX - panelRect.left : event.clientX;
    const y = panelRect ? event.clientY - panelRect.top : event.clientY;
    const menuWidth = hit.type === 'header' ? 218 : 214;
    const menuHeight = hit.type === 'header' ? (canEdit ? 236 : 92) : hit.type === 'row' ? (canEdit ? 92 : 48) : 154;
    const left = Math.max(8, Math.min(x, viewport.width - menuWidth - 8));
    const top = Math.max(8, Math.min(y, viewport.height - menuHeight - 8));

    if (hit.type === 'header') {
      const column = columns[hit.columnIndex];
      if (!column) return;
      setSelection(null);
      setContextMenu({
        mode: 'header',
        rowIndex: null,
        columnIndex: hit.columnIndex,
        left,
        top,
        canCopy: true,
        canSort: isTaskTableSortableColumn(column),
        canBulkText: isTaskTableBulkTextColumn(demand, columns, hit.columnIndex, canEdit)
      });
      return;
    }

    const task = tasks[hit.rowIndex];
    if (!task) return;

    if (hit.type === 'row') {
      setSelection({ rowIndex: hit.rowIndex, columnIndex: 0 });
      setContextMenu({
        mode: 'row',
        rowIndex: hit.rowIndex,
        columnIndex: null,
        left,
        top,
        canDelete: canEdit
      });
      return;
    }

    const column = columns[hit.columnIndex];
    if (column?.key === TASK_TABLE_ACTION_COLUMN) {
      setContextMenu(null);
      return;
    }
    if (column?.key === '__status' || isTaskListColumn(demand, column)) {
      setSelection({ rowIndex: hit.rowIndex, columnIndex: hit.columnIndex });
      const canEditChoice = isTaskTableEditableColumn(demand, columns, hit.columnIndex, canEdit);
      setContextMenu(canEditChoice ? {
        mode: column.key === '__status' ? 'status' : 'list',
        rowIndex: hit.rowIndex,
        columnIndex: hit.columnIndex,
        left,
        top,
        canCopy: false,
        canEditCell: true
      } : null);
      return;
    }
    const cellRawValue = taskTableCellRawValue(task, column);
    const canViewValue = isStructuredCellValue(demand, column, cellRawValue);
    setSelection({ rowIndex: hit.rowIndex, columnIndex: hit.columnIndex });
    setContextMenu({
      mode: 'cell',
      rowIndex: hit.rowIndex,
      columnIndex: hit.columnIndex,
      left,
      top,
      canCopy: true,
      canViewValue,
      canEditCell: Boolean(column && isTaskTableEditableColumn(demand, columns, hit.columnIndex, canEdit))
    });
  }, [canEdit, columns, demand, getCellAtPoint, tasks, viewport.height, viewport.width]);

  const handleContextCopy = useCallback(() => {
    if (!contextMenu) return;
    if (contextMenu.mode === 'header') {
      const column = columns[contextMenu.columnIndex];
      if (!column) return;
      const text = tasks.map((task) => taskTableCellCopyValue(task, column, demand)).join('\n');
      copyTextToClipboard(text, `Coluna ${column.label} copiada.`);
      setContextMenu(null);
      return;
    }
    const task = tasks[contextMenu.rowIndex];
    if (!task) return;
    if (contextMenu.mode === 'row') {
      copyTextToClipboard(taskTableRowCopyText(task, columns, demand), 'Linha copiada com titulos.');
    } else {
      if (contextMenu.canCopy === false) return;
      const column = columns[contextMenu.columnIndex];
      if (!column) return;
      copyTextToClipboard(taskTableCellCopyValue(task, column, demand), 'Celula copiada.');
    }
    setContextMenu(null);
  }, [columns, contextMenu, copyTextToClipboard, demand, tasks]);

  const handleContextViewValue = useCallback(() => {
    if (!contextMenu || contextMenu.mode !== 'cell' || !contextMenu.canViewValue) return;
    openStructuredCellViewer(contextMenu.rowIndex, contextMenu.columnIndex);
    setContextMenu(null);
  }, [contextMenu, openStructuredCellViewer]);

  const handleContextSort = useCallback(() => {
    if (!contextMenu || contextMenu.mode !== 'header') return;
    const column = columns[contextMenu.columnIndex];
    if (column && isTaskTableSortableColumn(column)) onSort?.(taskTableSortField(column));
    setContextMenu(null);
  }, [columns, contextMenu, onSort]);

  const openBulkTextModal = useCallback((operation) => {
    if (!contextMenu || contextMenu.mode !== 'header') return;
    const column = columns[contextMenu.columnIndex];
    if (!column) return;
    if (!isTaskTableBulkTextColumn(demand, columns, contextMenu.columnIndex, canEdit)) {
      showSaveNotice('Esta coluna não  permite alteração em massa de texto.', 'error');
      setContextMenu(null);
      return;
    }
    setBulkTextModal({
      operation,
      columnIndex: contextMenu.columnIndex,
      column,
      from: '',
      to: '',
      affix: '',
      running: false,
      error: ''
    });
    setContextMenu(null);
  }, [canEdit, columns, contextMenu, demand, showSaveNotice]);

  const executeBulkTextOperation = useCallback(async () => {
    if (!bulkTextModal || !demand?.id || bulkTextModal.running) return;
    const column = columns[bulkTextModal.columnIndex];
    if (!column || !isTaskTableBulkTextColumn(demand, columns, bulkTextModal.columnIndex, canEdit)) {
      setBulkTextModal((current) => current ? { ...current, error: 'Coluna inválida para operação em massa.' } : current);
      return;
    }

    const from = String(bulkTextModal.from ?? '');
    const to = String(bulkTextModal.to ?? '');
    const affix = String(bulkTextModal.affix ?? '');
    if (bulkTextModal.operation === 'replace' && !from && !to) {
      setBulkTextModal((current) => current ? { ...current, error: 'Preencha Trocar De ou Para.' } : current);
      return;
    }
    if (['prefix', 'suffix'].includes(bulkTextModal.operation) && !affix) {
      setBulkTextModal((current) => current ? { ...current, error: 'Informe o texto que deseja adicionar.' } : current);
      return;
    }

    const transform = (value) => {
      const text = String(value ?? '');
      if (bulkTextModal.operation === 'replace') {
        if (!from) return text ? text : to;
        return text.split(from).join(to);
      }
      if (bulkTextModal.operation === 'prefix') return `${affix}${text}`;
      if (bulkTextModal.operation === 'suffix') return `${text}${affix}`;
      return text;
    };

    const changes = tasks
      .map((task) => {
        const oldPayload = task.payload || {};
        const currentValue = taskTableBulkTextValue(oldPayload[column.key]);
        const nextText = transform(currentValue);
        let nextValue = nextText;
        try {
          nextValue = normalizeTaskTableEditValue(nextText, column.key, taskColumnSchemaType(demand, column));
        } catch (error) {
          return { task, currentValue, nextValue: nextText, invalid: error.message };
        }
        return { task, currentValue: oldPayload[column.key], nextValue, invalid: '' };
      })
      .filter((item) => item.invalid || !areTaskTableValuesEqual(item.currentValue, item.nextValue));

    if (!changes.length) {
      setBulkTextModal((current) => current ? { ...current, error: 'Nenhum valor desta pagina foi alterado.' } : current);
      return;
    }

    setBulkTextModal((current) => current ? { ...current, running: true, error: '' } : current);
    showSaveNotice(`Atualizando ${changes.length.toLocaleString('pt-BR')} task(s)...`, 'info');
    let updated = 0;
    let failed = 0;
    let lastError = '';

    for (const item of changes) {
      try {
        if (item.invalid) {
          throw new Error(item.invalid);
        }
        const oldPayload = item.task.payload || {};
        const response = await api(`/api/demands/${demand.id}/tasks/${item.task.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            task_key: item.task.task_key,
            status: item.task.status,
            payload: {
              ...oldPayload,
              [column.key]: item.nextValue
            }
          })
        });
        updated += 1;
        onTaskSaved?.(response.data || {
          ...item.task,
          payload: {
            ...oldPayload,
            [column.key]: item.nextValue
          }
        });
      } catch (error) {
        failed += 1;
        lastError = error.message || 'Não foi possível salvar uma task.';
      }
    }

    setBulkTextModal(null);
    showSaveNotice(
      failed
        ? `${updated} task(s) atualizada(s), ${failed} falha(s).${lastError ? ` Ultima falha: ${lastError}` : ''}`
        : `${updated} task(s) atualizada(s).`,
      failed ? 'error' : 'success'
    );
  }, [bulkTextModal, canEdit, columns, demand, onTaskSaved, showSaveNotice, tasks]);

  const handleContextEdit = useCallback(() => {
    if (!contextMenu || !['cell', 'status', 'list'].includes(contextMenu.mode)) return;
    beginCellEdit(contextMenu.rowIndex, contextMenu.columnIndex, true);
    setContextMenu(null);
  }, [beginCellEdit, contextMenu]);

  const handleContextDelete = useCallback(() => {
    if (!contextMenu || contextMenu.mode !== 'row' || !contextMenu.canDelete) return;
    const task = tasks[contextMenu.rowIndex];
    if (task) onTaskDeleteRequest?.(task);
    setContextMenu(null);
  }, [contextMenu, onTaskDeleteRequest, tasks]);

  const handleCanvasDoubleClick = useCallback((event) => {
    const hit = getCellAtPoint(event.clientX, event.clientY);
    if (!hit || hit.type !== 'cell') return;
    const task = tasks[hit.rowIndex];
    const column = columns[hit.columnIndex];
    if (task && column) {
      const rawValue = taskTableCellRawValue(task, column);
      if (isStructuredCellValue(demand, column, rawValue)) {
        openStructuredCellViewer(hit.rowIndex, hit.columnIndex);
        return;
      }
    }
    beginCellEdit(hit.rowIndex, hit.columnIndex, true);
  }, [beginCellEdit, columns, demand, getCellAtPoint, openStructuredCellViewer, tasks]);

  const handleCanvasMouseMove = useCallback((event) => {
    if (resizingColumn) {
      if (canvasRef.current) canvasRef.current.style.cursor = 'col-resize';
      return;
    }
    if (getColumnResizeAtPoint(event.clientX, event.clientY)) {
      if (canvasRef.current) canvasRef.current.style.cursor = 'col-resize';
      return;
    }
    const hit = getCellAtPoint(event.clientX, event.clientY);
    const column = ['cell', 'header'].includes(hit?.type) ? columns[hit.columnIndex] : null;
    const task = hit?.type === 'cell' ? tasks[hit.rowIndex] : null;
    const rawValue = task && column ? taskTableCellRawValue(task, column) : null;
    const canInspectStructured = hit?.type === 'cell' && column ? isStructuredCellValue(demand, column, rawValue) : false;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = hit?.type === 'header' && isTaskTableSortableColumn(column)
        ? 'pointer'
        : [TASK_TABLE_ACTION_COLUMN, '__status'].includes(column?.key) || isTaskListColumn(demand, column) || canInspectStructured
          ? 'pointer'
          : 'cell';
    }
  }, [columns, demand, getCellAtPoint, getColumnResizeAtPoint, resizingColumn, tasks]);

  const editingColumn = editing ? columns[editing.columnIndex] : null;
  const editingLeft = editing && editingColumn ? TASK_CANVAS_ROW_NUMBER_WIDTH + columnOffsets[editing.columnIndex] - scroll.left : 0;
  const editingTop = editing ? TASK_CANVAS_HEADER_HEIGHT + editing.rowIndex * TASK_CANVAS_ROW_HEIGHT - scroll.top : 0;
  const editingWidth = editingColumn ? columnWidths[editing.columnIndex] : 0;
  const editingVisible = editing
    && editingLeft + editingWidth > TASK_CANVAS_ROW_NUMBER_WIDTH
    && editingLeft < viewport.width
    && editingTop + TASK_CANVAS_ROW_HEIGHT > TASK_CANVAS_HEADER_HEIGHT
    && editingTop < viewport.height;
  const listItems = editingColumn && editing?.kind === 'list'
    ? normalizeListOptions(taskColumnSchemaDefinition(demand, editingColumn))
    : [];
  const activeDropdownItems = editing?.kind === 'list' ? listItems : statusItems;
  const statusDropdownHeight = Math.min(238, 38 + activeDropdownItems.length * 37);
  const statusDropdownTop = editing
    ? (
      editingTop + statusDropdownHeight + 8 > viewport.height
        ? Math.max(TASK_CANVAS_HEADER_HEIGHT + 6, editingTop + TASK_CANVAS_ROW_HEIGHT - statusDropdownHeight)
        : editingTop + 3
    )
    : 0;
  const [noticeTone, noticeText] = saveNotice ? saveNotice.split(/:(.*)/s).filter(Boolean) : ['', ''];

  return (
    <div className="td-canvas-table-panel" ref={panelRef}>
      <div className="td-canvas-scroll" ref={scrollRef} onScroll={handleScroll}>
        <div className="td-canvas-spacer" style={{ width: `${totalWidth}px`, height: `${totalHeight}px` }} />
        <canvas
          className="td-canvas-layer"
          ref={canvasRef}
          aria-label="Tabela de tasks em canvas"
          role="grid"
          style={{ transform: `translate(${scroll.left}px, ${scroll.top}px)` }}
          onPointerDown={handleCanvasPointerDown}
          onClick={handleCanvasClick}
          onDoubleClick={handleCanvasDoubleClick}
          onMouseMove={handleCanvasMouseMove}
          onContextMenu={handleCanvasContextMenu}
        />
      </div>
      {contextMenu && (
        <div
          className="td-canvas-context-menu"
          style={{ left: `${contextMenu.left}px`, top: `${contextMenu.top}px` }}
          onClick={(event) => event.stopPropagation()}
          role="menu"
        >
          {contextMenu.mode === 'header' ? (
            <>
              <button type="button" onClick={handleContextCopy} role="menuitem">
                <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
                Copiar
              </button>
              {contextMenu.canSort && (
                <button type="button" onClick={handleContextSort} role="menuitem">
                  <span className="material-symbols-rounded" aria-hidden="true">sort</span>
                  Ordenar
                </button>
              )}
              {contextMenu.canBulkText && (
                <>
                  <button type="button" onClick={() => openBulkTextModal('replace')} role="menuitem">
                    <span className="material-symbols-rounded" aria-hidden="true">find_replace</span>
                    Trocar texto
                  </button>
                  <button type="button" onClick={() => openBulkTextModal('prefix')} role="menuitem">
                    <span className="material-symbols-rounded" aria-hidden="true">first_page</span>
                    Adicionar na Frente
                  </button>
                  <button type="button" onClick={() => openBulkTextModal('suffix')} role="menuitem">
                    <span className="material-symbols-rounded" aria-hidden="true">last_page</span>
                    Adicionar no Final
                  </button>
                </>
              )}
            </>
          ) : (
            <>
          {contextMenu.canCopy !== false && (
            <button type="button" onClick={handleContextCopy} role="menuitem">
              <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
              Copiar
            </button>
          )}
          {contextMenu.mode === 'cell' && contextMenu.canViewValue && (
            <button type="button" onClick={handleContextViewValue} role="menuitem">
              <span className="material-symbols-rounded" aria-hidden="true">visibility</span>
              Visualizar conteudo
            </button>
          )}
          {['cell', 'status', 'list'].includes(contextMenu.mode) && contextMenu.canEditCell && (
            <button type="button" onClick={handleContextEdit} role="menuitem">
              <span className="material-symbols-rounded" aria-hidden="true">{contextMenu.mode === 'status' ? 'palette' : contextMenu.mode === 'list' ? 'format_list_bulleted' : 'edit'}</span>
              {contextMenu.mode === 'status' ? 'Alterar status' : contextMenu.mode === 'list' ? 'Alterar item' : 'Editar'}
            </button>
          )}
          {contextMenu.mode === 'row' && contextMenu.canDelete && (
            <button className="is-danger" type="button" onClick={handleContextDelete} role="menuitem">
              <span className="material-symbols-rounded" aria-hidden="true">delete</span>
              Apagar
            </button>
          )}
            </>
          )}
        </div>
      )}
      {bulkTextModal && (
        <div className="td-table-bulk-backdrop" role="presentation" onClick={() => !bulkTextModal.running && setBulkTextModal(null)}>
          <form
            className="td-table-bulk-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Operacao em massa na coluna"
            onClick={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              executeBulkTextOperation();
            }}
          >
            <header>
              <div>
                <span>{bulkTextModal.column?.label}</span>
                <strong>
                  {bulkTextModal.operation === 'replace'
                    ? 'Trocar texto'
                    : bulkTextModal.operation === 'prefix'
                      ? 'Adicionar na Frente'
                      : 'Adicionar no Final'}
                </strong>
                <p>Aplica a alteração nas tasks carregadas nesta página da tabela e registra log/timeline em cada task alterada.</p>
              </div>
              <button type="button" className="icon-button" onClick={() => setBulkTextModal(null)} disabled={bulkTextModal.running} aria-label="Fechar">
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </header>
            <div className="td-table-bulk-body">
              {bulkTextModal.operation === 'replace' ? (
                <>
                  <label className="modal-field">
                    <span>Trocar De</span>
                    <input
                      value={bulkTextModal.from}
                      onChange={(event) => setBulkTextModal((current) => current ? { ...current, from: event.target.value, error: '' } : current)}
                      placeholder="Texto a localizar"
                      disabled={bulkTextModal.running}
                      autoFocus
                    />
                  </label>
                  <label className="modal-field">
                    <span>Para</span>
                    <input
                      value={bulkTextModal.to}
                      onChange={(event) => setBulkTextModal((current) => current ? { ...current, to: event.target.value, error: '' } : current)}
                      placeholder="Novo texto ou vazio para remover"
                      disabled={bulkTextModal.running}
                    />
                  </label>
                  <p className="td-table-bulk-hint">Se <strong>Trocar De</strong> estiver vazio, o texto de <strong>Para</strong> preenche apenas células vazias. Se <strong>Para</strong> estiver vazio, os matches encontrados serão removidos.</p>
                </>
              ) : (
                <label className="modal-field modal-field-wide">
                  <span>{bulkTextModal.operation === 'prefix' ? 'Prefixo' : 'Sufixo'}</span>
                  <input
                    value={bulkTextModal.affix}
                    onChange={(event) => setBulkTextModal((current) => current ? { ...current, affix: event.target.value, error: '' } : current)}
                    placeholder={bulkTextModal.operation === 'prefix' ? 'Texto antes do valor atual' : 'Texto depois do valor atual'}
                    disabled={bulkTextModal.running}
                    autoFocus
                  />
                </label>
              )}
              {bulkTextModal.error && <div className="td-table-bulk-error">{bulkTextModal.error}</div>}
            </div>
            <footer>
              <button type="button" className="secondary-button" onClick={() => setBulkTextModal(null)} disabled={bulkTextModal.running}>Cancelar</button>
              <button type="submit" className="primary-button" disabled={bulkTextModal.running}>
                <span className="material-symbols-rounded" aria-hidden="true">{bulkTextModal.running ? 'sync' : 'done_all'}</span>
                {bulkTextModal.running ? 'Aplicando...' : 'Aplicar alteração'}
              </button>
            </footer>
          </form>
        </div>
      )}
      {!tasks.length && (
        <div className="td-canvas-empty">
          <span className="material-symbols-rounded" aria-hidden="true">table_rows</span>
          <strong>Nenhuma task nesta página</strong>
          <p>Use a busca, altere a pagina ou importe novas tasks para preencher a tabela.</p>
        </div>
      )}
      {editingVisible && (
        ['status', 'list'].includes(editing?.kind) ? (
        <div
          className="td-canvas-status-dropdown"
          style={{
            left: `${Math.max(TASK_CANVAS_ROW_NUMBER_WIDTH + 2, editingLeft + 3)}px`,
            top: `${statusDropdownTop}px`,
            width: `${Math.max(158, Math.min(Math.max(editingWidth - 6, 158), viewport.width - editingLeft - 12))}px`,
            maxHeight: `${Math.max(120, Math.min(statusDropdownHeight, viewport.height - TASK_CANVAS_HEADER_HEIGHT - 12))}px`
          }}
          role="listbox"
          aria-label={editing.kind === 'list' ? 'Alterar item da lista' : 'Alterar status da task'}
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key === 'Escape') commitEdit(false);
          }}
        >
          <div className="td-canvas-status-dropdown-head">
            <span className="material-symbols-rounded" aria-hidden="true">{editing.kind === 'list' ? 'format_list_bulleted' : 'palette'}</span>
            <strong>{editing.kind === 'list' ? editingColumn?.label || 'Lista' : 'Status'}</strong>
          </div>
          {activeDropdownItems.map((item) => {
            const tone = editing.kind === 'list' ? 'custom' : statusToneForName(demand, item.name);
            const customColor = editing.kind === 'list' ? item.color : '';
            return (
            <button
              key={item.name}
              className={`td-canvas-status-option tone-${tone} ${editing.value === item.name ? 'is-active' : ''}`}
              style={customColor ? { color: customColor } : undefined}
              type="button"
              role="option"
              aria-selected={editing.value === item.name}
              onClick={() => commitEdit(true, item.name)}
            >
              <span className="td-canvas-status-dot" aria-hidden="true" />
              <span>{item.name}</span>
              {editing.value === item.name && (
                <span className="material-symbols-rounded" aria-hidden="true">check</span>
              )}
            </button>
          );})}
        </div>
        ) : (
        <input
          ref={inputRef}
          className="td-canvas-edit-input"
          value={editing.value}
          style={{
            left: `${Math.max(TASK_CANVAS_ROW_NUMBER_WIDTH + 2, editingLeft + 3)}px`,
            top: `${editingTop + 3}px`,
            width: `${Math.max(80, Math.min(editingWidth - 6, viewport.width - editingLeft - 12))}px`,
            height: `${TASK_CANVAS_ROW_HEIGHT - 6}px`
          }}
          onChange={(event) => setEditing((current) => current ? { ...current, value: event.target.value } : current)}
          onBlur={() => commitEdit(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commitEdit(true);
            if (event.key === 'Escape') commitEdit(false);
          }}
        />
        )
      )}
      {valueViewer && (
        <TaskValueViewerModal
          open={Boolean(valueViewer)}
          title={valueViewer.title}
          value={valueViewer.value}
          onClose={() => setValueViewer(null)}
        />
      )}
      {noticeText && (
        <div className={`td-canvas-save-notice is-${noticeTone || 'info'}`}>
          <span className="material-symbols-rounded" aria-hidden="true">
            {noticeTone === 'error' ? 'error' : noticeTone === 'success' ? 'check_circle' : 'sync'}
          </span>
          {noticeText}
        </div>
      )}
    </div>
  );
};

const TaskCard = ({ data }) => {
  const task = data.content.task;
  const coverFile = data.content.coverFile;
  const commentsCount = Number(task.comments_count || task.comment_count || task.commentsCount || 0);
  const hasProcessedRouting = Boolean(task.processed_at);
  return (
    <article
      className="td-task-card"
      data-task-card-id={task.id}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
        window.dispatchEvent(new CustomEvent('tdemand:task-card-contextmenu', {
          detail: {
            task,
            clientX: event.clientX,
            clientY: event.clientY
          }
        }));
      }}
    >
      {coverFile && (
        <div className={`td-task-cover ${coverFile.isImage ? 'is-image' : 'is-file'}`}>
          {coverFile.isImage ? (
            <img src={coverFile.url} alt={coverFile.name} loading="lazy" draggable={false} />
          ) : (
            <>
              <span className="material-symbols-rounded" aria-hidden="true">draft</span>
              <strong>{coverFile.name}</strong>
              <small>{coverFile.mimeType}</small>
            </>
          )}
        </div>
      )}
      <header>
        <strong className="td-task-card-id" title={data.title}>{compactTaskTitle(data.title)}</strong>
        <span>{task.status}</span>
      </header>
      <div className="td-task-card-values" aria-label="Valores da task">
        {data.content.payloadPreview?.length ? data.content.payloadPreview.map((item, index) => (
          item.type === 'relationship' ? (
            <div
              className="searchable-select__trigger-tags td-task-card-relation-values"
              key={`${item.key}-${index}`}
              title={item.text}
            >
              {item.relationValues.slice(0, 2).map((value) => (
                <span className="searchable-select__tag td-task-card-relation-tag" key={value}>{value}</span>
              ))}
              {item.relationValues.length > 2 && (
                <span className="searchable-select__tag searchable-select__tag--more td-task-card-relation-tag is-more">
                  +{item.relationValues.length - 2}
                </span>
              )}
            </div>
          ) : item.type === 'list' ? (
            <span
              className="td-task-card-list-value"
              key={`${item.key}-${index}`}
              style={{ '--td-list-color': item.color }}
              title={item.text}
            >
              <i aria-hidden="true" />
              <b>{item.value}</b>
            </span>
          ) : <span key={`${item.key}-${index}`} title={item.text}>{item.value}</span>
        )) : <em>Payload vazio</em>}
      </div>
      <footer className="td-task-card-footer">
        <div className="td-task-card-icons" aria-label="Indicadores da task">
          <span
            className={`td-task-card-icon ${hasProcessedRouting ? 'is-processed' : 'is-pending'}`}
            title={hasProcessedRouting ? 'Roteamento processado' : 'Roteamento ainda não  processado'}
            aria-label={hasProcessedRouting ? 'Roteamento processado' : 'Roteamento não  processado'}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              {hasProcessedRouting ? 'alt_route' : 'route'}
            </span>
          </span>
          <span
            className={`td-task-card-comment-badge ${commentsCount > 0 ? 'has-comments' : 'is-empty'}`}
            title={`${commentsCount} comentário${commentsCount === 1 ? '' : 's'}`}
            aria-label={`${commentsCount} comentário${commentsCount === 1 ? '' : 's'}`}
          >
            <span className="material-symbols-rounded" aria-hidden="true">forum</span>
            <strong>{commentsCount}</strong>
          </span>
        </div>
        <time title="Data da última atualização">{data.content.updatedAt}</time>
      </footer>
    </article>
  );
};

const TaskFilterDropdown = ({
  open,
  onToggle,
  fields,
  draft,
  activeCount,
  savedFilters,
  saveName,
  onSaveNameChange,
  onDraftChange,
  onApply,
  onClear,
  onSave,
  onLoadSaved,
  onDeleteSaved,
  onMoveFiltered,
  onCopyFiltered,
  onDeleteFiltered,
  canEdit,
  busy,
  disabled
}) => {
  const normalizedDraft = normalizeTaskFilters(draft, fields);
  const hasRows = normalizedDraft.conditions.length > 0;

  const updateCondition = (id, patch) => {
    onDraftChange({
      ...normalizedDraft,
      conditions: normalizedDraft.conditions.map((condition) => (
        condition.id === id ? { ...condition, ...patch } : condition
      ))
    });
  };

  const addCondition = () => {
    const firstField = fields[0]?.value || 'task_key';
    onDraftChange({
      ...normalizedDraft,
      conditions: [
        ...normalizedDraft.conditions,
        {
          id: taskFilterConditionId(),
          field: firstField,
          operator: 'contains',
          value: ''
        }
      ]
    });
  };

  const removeCondition = (id) => {
    onDraftChange({
      ...normalizedDraft,
      conditions: normalizedDraft.conditions.filter((condition) => condition.id !== id)
    });
  };

  return (
    <div className="td-filter-menu" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={`secondary-button td-filter-trigger ${activeCount ? 'is-active' : ''}`}
        onClick={onToggle}
        disabled={disabled}
        aria-expanded={open}
      >
        <span className="material-symbols-rounded" aria-hidden="true">filter_alt</span>
        Filtros
        {activeCount > 0 && <strong>{activeCount}</strong>}
      </button>

      {open && (
        <div className="td-filter-popover">
          <header className="td-filter-head">
            <div>
              <strong>Filtros avançados</strong>
              <span>Monte condições como no grid, salve e reutilize depois.</span>
            </div>
            <button type="button" onClick={onToggle} aria-label="Fechar filtros">
              <span className="material-symbols-rounded" aria-hidden="true">close</span>
            </button>
          </header>

          <div className="td-filter-toolbar">
            <label>
              Combinar
              <select
                value={normalizedDraft.logic}
                onChange={(event) => onDraftChange({ ...normalizedDraft, logic: event.target.value })}
              >
                <option value="and">Todas as condições</option>
                <option value="or">Qualquer condição</option>
              </select>
            </label>
            <button type="button" className="secondary-button" onClick={addCondition}>
              <span className="material-symbols-rounded" aria-hidden="true">add</span>
              Condição
            </button>
          </div>

          <div className="td-filter-conditions">
            {hasRows ? normalizedDraft.conditions.map((condition, index) => {
              const operatorNeedsValue = taskFilterOperatorNeedsValue(condition.operator);
              const fieldType = fields.find((field) => field.value === condition.field)?.type || 'string';
              const inputType = ['date', 'datetime'].includes(fieldType) ? 'date' : 'text';
              return (
                <div className="td-filter-row" key={condition.id}>
                  <span>{index + 1}</span>
                  <select
                    value={condition.field}
                    onChange={(event) => updateCondition(condition.id, { field: event.target.value })}
                  >
                    {fields.map((field) => (
                      <option key={field.value} value={field.value}>{field.label}</option>
                    ))}
                  </select>
                  <select
                    value={condition.operator}
                    onChange={(event) => updateCondition(condition.id, {
                      operator: event.target.value,
                      value: taskFilterOperatorNeedsValue(event.target.value) ? condition.value : ''
                    })}
                  >
                    {taskFilterOperators.map((operator) => (
                      <option key={operator.value} value={operator.value}>{operator.label}</option>
                    ))}
                  </select>
                  <input
                    type={inputType}
                    value={condition.value}
                    onChange={(event) => updateCondition(condition.id, { value: event.target.value })}
                    placeholder={operatorNeedsValue ? 'Valor' : 'Sem valor'}
                    disabled={!operatorNeedsValue}
                  />
                  <button type="button" className="td-filter-icon-button" onClick={() => removeCondition(condition.id)} aria-label="Remover condição">
                    <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                  </button>
                </div>
              );
            }) : (
              <div className="td-filter-empty">
                <span className="material-symbols-rounded" aria-hidden="true">rule</span>
                <strong>Nenhuma condição criada</strong>
                <p>Adicione uma condição para filtrar a tabela em todas as páginas.</p>
              </div>
            )}
          </div>

          <section className="td-filter-save-box">
            <label>
              Nome do filtro
              <input
                type="text"
                value={saveName}
                onChange={(event) => onSaveNameChange(event.target.value)}
                placeholder="Ex: Pendentes enterprise"
              />
            </label>
            <button type="button" className="secondary-button" onClick={onSave} disabled={!taskFiltersCount(normalizedDraft)}>
              <span className="material-symbols-rounded" aria-hidden="true">bookmark_add</span>
              Salvar
            </button>
          </section>

          {savedFilters.length > 0 && (
            <section className="td-filter-saved">
              <strong>Filtros salvos</strong>
              {savedFilters.map((item) => (
                <div className="td-filter-saved-item" key={item.id}>
                  <button type="button" onClick={() => onLoadSaved(item)}>
                    <span className="material-symbols-rounded" aria-hidden="true">bookmark</span>
                    {item.name}
                  </button>
                  <button type="button" onClick={() => onDeleteSaved(item.id)} aria-label={`Excluir filtro ${item.name}`}>
                    <span className="material-symbols-rounded" aria-hidden="true">close</span>
                  </button>
                </div>
              ))}
            </section>
          )}

          <section className="td-filter-batch-actions">
            <strong>Ações no filtro atual</strong>
            <div className="td-filter-batch-grid">
              <button
                type="button"
                className="secondary-button"
                onClick={onMoveFiltered}
                disabled={disabled || busy || !canEdit}
              >
                <span className="material-symbols-rounded" aria-hidden="true">drive_file_move</span>
                Mover para outra demanda
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={onCopyFiltered}
                disabled={disabled || busy || !canEdit}
              >
                <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
                Copiar para outra demanda
              </button>
              <button
                type="button"
                className="secondary-button is-danger"
                onClick={onDeleteFiltered}
                disabled={disabled || busy || !canEdit}
              >
                <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                Apagar
              </button>
            </div>
          </section>

          <footer className="td-filter-footer">
            <button type="button" className="secondary-button" onClick={onClear}>
              Limpar
            </button>
            <button type="button" className="primary-button" onClick={onApply}>
              <span className="material-symbols-rounded" aria-hidden="true">check</span>
              Aplicar filtros
            </button>
          </footer>
        </div>
      )}
    </div>
  );
};

const TasksKanbanApp = () => {
  const resolveTasksPageActive = () => {
    if (window.TDEMAND_SHARED_TASKS?.token) return true;
    const content = document.getElementById('appView')?.querySelector('.content');
    return Boolean(content?.classList.contains('tasks-board-mode') || content?.classList.contains('shared-tasks-mode'));
  };
  const [demand, setDemand] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [board, setBoard] = useState(emptyBoard);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('Selecione uma demanda para visualizar as tasks.');
  const [viewMode, setViewMode] = useState('kanban');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [canEdit, setCanEdit] = useState(window.TDEMAND_CAN_EDIT !== false);
  const [sharedMode, setSharedMode] = useState(Boolean(window.TDEMAND_SHARED_TASKS?.token));
  const [tasksPageActive, setTasksPageActive] = useState(resolveTasksPageActive);
  const [shareToken, setShareToken] = useState(window.TDEMAND_SHARED_TASKS?.token || '');
  const [sharePassword, setSharePassword] = useState('');
  const [sharePasswordDraft, setSharePasswordDraft] = useState('');
  const [sharePasswordRequired, setSharePasswordRequired] = useState(false);
  const [shareMeta, setShareMeta] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [csvExporting, setCsvExporting] = useState(false);
  const [kanbanContextMenu, setKanbanContextMenu] = useState(null);
  const [columnOrderSaving, setColumnOrderSaving] = useState(false);
  const [filterBatchModal, setFilterBatchModal] = useState(null);
  const [statusActionModal, setStatusActionModal] = useState(null);
  const [chartRows, setChartRows] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState('');
  const [chartMeta, setChartMeta] = useState(null);
  const [chartRefreshKey, setChartRefreshKey] = useState(0);
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: TASK_TABLE_DEFAULT_PAGE_SIZE,
    pageCount: 1
  });
  const [tableSort, setTableSort] = useState({
    field: 'created_at',
    direction: 'desc'
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [presenceOpen, setPresenceOpen] = useState(false);
  const [presenceViewers, setPresenceViewers] = useState([]);
  const [draftFilters, setDraftFilters] = useState(emptyTaskFilters);
  const [activeFilters, setActiveFilters] = useState(emptyTaskFilters);
  const [savedFilters, setSavedFilters] = useState([]);
  const [filterSaveName, setFilterSaveName] = useState('');
  const [taskStats, setTaskStats] = useState({
    total: 0,
    loaded: 0,
    limit: TASK_KANBAN_COLUMN_LIMIT,
    truncated: false
  });
  const boardRef = useRef(board);
  const kanbanShellRef = useRef(null);
  const requestRef = useRef(0);
  const chartRequestRef = useRef(0);
  const demandIdRef = useRef('');
  const shareTokenRef = useRef('');
  const realtimeSocketRef = useRef(null);
  const realtimeRefreshTimerRef = useRef(null);
  const searchPopoverRef = useRef(null);
  const loadingMoreColumnsRef = useRef(new Set());

  useEffect(() => {
    boardRef.current = board;
  }, [board]);

  useEffect(() => {
    demandIdRef.current = String(demand?.id || '');
  }, [demand?.id]);

  useEffect(() => {
    shareTokenRef.current = String(shareToken || '');
  }, [shareToken]);

  useEffect(() => {
    setPresenceViewers([]);
    setPresenceOpen(false);
  }, [demand?.id, shareToken, sharedMode]);

  const [collapsedColumns, setCollapsedColumns] = useState(() => new Set());

  useEffect(() => {
    setCollapsedColumns(new Set());
  }, [demand?.id, shareToken, sharedMode]);

  useEffect(() => {
    const content = document.getElementById('appView')?.querySelector('.content');
    if (!content) return undefined;
    const sync = () => {
      setTasksPageActive(
        Boolean(sharedMode || content.classList.contains('tasks-board-mode') || content.classList.contains('shared-tasks-mode'))
      );
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(content, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [sharedMode]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!searchPopoverRef.current || searchPopoverRef.current.contains(event.target)) return;
      setSearchOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [searchOpen]);

  const filterFields = useMemo(() => taskFilterFields(demand, [...tasks, ...chartRows]), [chartRows, demand, tasks]);
  const filterScope = useMemo(() => taskFilterStorageScope(demand, sharedMode, shareToken), [demand, shareToken, sharedMode]);
  const activeFiltersParam = useMemo(() => {
    const serialized = serializeTaskFilters(activeFilters);
    return serialized ? JSON.stringify(serialized) : '';
  }, [activeFilters]);
  const activeFiltersCount = useMemo(() => taskFiltersCount(activeFilters), [activeFilters]);

  useEffect(() => {
    setSavedFilters(readStoredTaskFilters(filterScope));
  }, [filterScope]);

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const close = () => setFiltersOpen(false);
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setFiltersOpen(false);
    };
    window.addEventListener('click', close);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [filtersOpen]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const loadDemandTasks = useCallback((nextDemand, params = {}) => api(`/api/demands/${encodeURIComponent(nextDemand.id)}/tasks/filter`, {
    method: 'POST',
    body: JSON.stringify(params)
  }), []);

  const loadSharedTasks = useCallback((params = {}) => api(`/api/shared-tasks/${encodeURIComponent(shareToken)}`, {
    method: 'POST',
    body: JSON.stringify({
      password: sharePassword,
      ...params
    })
  }), [sharePassword, shareToken]);

  const resetTaskState = useCallback((message = 'Selecione uma demanda para visualizar as tasks.') => {
    setTasks([]);
    setBoard(emptyBoard);
    setTaskStats({
      total: 0,
      loaded: 0,
      limit: TASK_KANBAN_COLUMN_LIMIT,
      truncated: false
    });
    setTableState((current) => ({
      ...current,
      page: 1,
      pageCount: 1
    }));
    setNotice(message);
  }, []);

  const applySharedDemandSnapshot = useCallback((nextDemand) => {
    if (!nextDemand?.id) return;
    if (!demand?.id || String(demand.id) !== String(nextDemand.id)) {
      setDemand(nextDemand);
      window.TDEMAND_ACTIVE_TASKS_DEMAND = nextDemand;
      document.getElementById('tasksDemandTitle').textContent = nextDemand.name || nextDemand.protocol || 'Tasks compartilhadas';
      document.getElementById('tasksDemandSubtitle').textContent = 'Compartilhado.';
    }
  }, [demand?.id]);

  const loadCurrentTasks = useCallback(async () => {
    if (!tasksPageActive && !sharedMode) return;
    if (viewMode === 'charts') {
      if (!sharedMode && !demand?.id) resetTaskState();
      return;
    }
    if (!sharedMode && !demand?.id) {
      resetTaskState();
      return;
    }
    if (sharedMode && !shareToken) {
      resetTaskState('Compartilhamento inválido.');
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setNotice('');

    try {
      const fetchTasks = (params) => sharedMode
        ? loadSharedTasks(params)
        : loadDemandTasks(demand, params);

      if (viewMode === 'table') {
	        const result = await fetchTasks({
	          view: 'table',
	          page: tableState.page,
	          page_size: tableState.pageSize,
	          search: debouncedSearchTerm,
	          filters: activeFiltersParam,
	          sort_field: tableSort.field,
	          sort_direction: tableSort.direction
	        });
        if (requestRef.current !== requestId) return;
        const nextDemand = result.demand || demand;
        applySharedDemandSnapshot(result.demand);
        setShareMeta(result.share || null);
        setSharePasswordRequired(false);
        const rows = Array.isArray(result.data) ? result.data : [];
        const total = Number(result.total_count ?? result.count ?? rows.length);
        setTasks(rows);
        setTaskStats({
          total,
          loaded: rows.length,
          limit: Number(result.page_size || tableState.pageSize),
          truncated: Boolean(result.truncated)
        });
        setTableState((current) => {
          const next = {
            ...current,
            page: Number(result.page || tableState.page),
            pageSize: Number(result.page_size || tableState.pageSize),
            pageCount: Number(result.page_count || 1)
          };
          return next.page === current.page && next.pageSize === current.pageSize && next.pageCount === current.pageCount
            ? current
            : next;
        });
        setNotice(rows.length ? '' : 'Nenhuma task encontrada para os filtros atuais.');
        return;
      }

      const result = await fetchTasks({
        view: 'kanban',
        limit: TASK_KANBAN_COLUMN_LIMIT,
        search: debouncedSearchTerm,
        filters: activeFiltersParam
      });
      if (requestRef.current !== requestId) return;
      const nextDemand = result.demand || demand;
      applySharedDemandSnapshot(result.demand);
      setShareMeta(result.share || null);
      setSharePasswordRequired(false);
      const rows = Array.isArray(result.data) ? result.data : [];
      const total = Number(result.total_count ?? result.count ?? rows.length);
      const loaded = Number(result.count ?? rows.length);
      setTasks(rows);
      setTaskStats({
        total,
        loaded,
        limit: Number(result.limit || TASK_KANBAN_COLUMN_LIMIT),
        truncated: Boolean(result.truncated)
      });
      setBoard(makeBoard(nextDemand, rows, result.status_counts || {}));
      setNotice(rows.length
        ? (total > loaded ? `Kanban carregou ${loaded.toLocaleString('pt-BR')} cards iniciais de ${total.toLocaleString('pt-BR')}. Role as colunas para buscar mais.` : '')
        : 'Nenhuma task encontrada para os filtros atuais.');
    } catch (error) {
      if (requestRef.current === requestId) {
        if (sharedMode && (error.status === 401 || error.data?.password_required)) {
          setSharePasswordRequired(true);
          setNotice('');
        } else {
          setNotice(error.message);
        }
      }
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [activeFiltersParam, applySharedDemandSnapshot, debouncedSearchTerm, demand, loadDemandTasks, loadSharedTasks, resetTaskState, shareToken, sharedMode, tableSort.direction, tableSort.field, tableState.page, tableState.pageSize, tasksPageActive, viewMode]);

	  const loadChartTasks = useCallback(async () => {
    if (!tasksPageActive && !sharedMode) return;
    if (viewMode !== 'charts') return;
    if (!sharedMode && !demand?.id) {
      setChartRows([]);
      setChartMeta(null);
      setChartError('');
      return;
    }
    if (sharedMode && !shareToken) {
      setChartRows([]);
      setChartMeta(null);
      setChartError('Compartilhamento inválido.');
      return;
    }

    const requestId = chartRequestRef.current + 1;
    chartRequestRef.current = requestId;
    setChartLoading(true);
    setChartError('');
    setNotice('');

    try {
      const params = {
        view: 'export',
        limit: TASK_CHART_EXPORT_LIMIT,
        search: debouncedSearchTerm,
        filters: activeFiltersParam
      };
      const result = sharedMode
        ? await loadSharedTasks(params)
        : await loadDemandTasks(demand, params);
      if (chartRequestRef.current !== requestId) return;
      applySharedDemandSnapshot(result.demand);
      setShareMeta(result.share || null);
      setSharePasswordRequired(false);
      const rows = Array.isArray(result.data) ? result.data : [];
      setChartRows(rows);
      setChartMeta({
        count: Number(result.count || rows.length),
        total: Number(result.total_count || rows.length),
        truncated: Boolean(result.truncated)
      });
    } catch (error) {
      if (chartRequestRef.current === requestId) {
        if (sharedMode && (error.status === 401 || error.data?.password_required)) {
          setSharePasswordRequired(true);
          setChartError('');
        } else {
          setChartRows([]);
          setChartMeta(null);
          setChartError(error.message || 'Não foi possível carregar os gráficos.');
        }
      }
    } finally {
      if (chartRequestRef.current === requestId) setChartLoading(false);
	    }
  }, [activeFiltersParam, applySharedDemandSnapshot, debouncedSearchTerm, demand, loadDemandTasks, loadSharedTasks, shareToken, sharedMode, tasksPageActive, viewMode]);

	  const removeTaskFromState = useCallback((taskId) => {
	    const idText = String(taskId);
	    setTasks((current) => current.filter((task) => String(task.id) !== idText));
	    setBoard((current) => {
	      const taskNodeId = `task-${taskId}`;
	      if (!current[taskNodeId]) return current;
	      const next = { ...current };
	      const parentId = next[taskNodeId]?.parentId;
	      if (parentId && next[parentId]) {
	        next[parentId] = {
	          ...next[parentId],
	          children: (next[parentId].children || []).filter((id) => id !== taskNodeId),
	          totalChildrenCount: Math.max(0, Number(next[parentId].totalChildrenCount || 0) - 1)
	        };
	      }
	      delete next[taskNodeId];
	      return next;
	    });
	    setTaskStats((current) => ({
	      ...current,
	      total: Math.max(0, Number(current.total || 0) - 1),
	      loaded: Math.max(0, Number(current.loaded || 0) - 1)
	    }));
	  }, []);

	  useEffect(() => {
    const loadHandler = (event) => {
      const nextDemand = event.detail?.demand || null;
      const nextDemandId = String(nextDemand?.id || '');
      const sameDemand = Boolean(nextDemandId && nextDemandId === demandIdRef.current && !sharedMode);
      if (sameDemand) {
        setDemand(nextDemand);
        window.TDEMAND_ACTIVE_TASKS_DEMAND = nextDemand;
        setRefreshKey((current) => current + 1);
        setChartRefreshKey((current) => current + 1);
        return;
      }
      setSharedMode(false);
      setShareToken('');
      setSharePassword('');
      setSharePasswordDraft('');
      setSharePasswordRequired(false);
      setShareMeta(null);
      window.TDEMAND_ACTIVE_TASKS_DEMAND = nextDemand;
      setDemand(nextDemand);
      setSearchTerm('');
      setDebouncedSearchTerm('');
      setFiltersOpen(false);
      setDraftFilters(emptyTaskFilters());
      setActiveFilters(emptyTaskFilters());
      setFilterSaveName('');
	      setTableState((current) => ({
	        ...current,
	        page: 1,
	        pageCount: 1
	      }));
	      setTableSort({ field: 'created_at', direction: 'desc' });
	      resetTaskState(nextDemand ? 'Carregando tasks...' : 'Selecione uma demanda para visualizar as tasks.');
	      if (nextDemand?.id) {
	        setRefreshKey((current) => current + 1);
	        setChartRefreshKey((current) => current + 1);
	      }
    };
    const refreshHandler = () => {
      setRefreshKey((current) => current + 1);
    };
    const deletedHandler = (event) => {
      const taskId = event.detail?.taskId;
      if (!taskId) return;
      removeTaskFromState(taskId);
    };
    const permissionsHandler = (event) => {
      setCanEdit(event.detail?.canEdit !== false);
    };
    const dashboardFilterHandler = (event) => {
      const incoming = event.detail?.filters || {};
      const nextFilters = {
        logic: incoming.logic === 'or' ? 'or' : 'and',
        conditions: (Array.isArray(incoming.conditions) ? incoming.conditions : [])
          .filter((condition) => condition?.field)
          .map((condition) => ({
            id: taskFilterConditionId(),
            field: String(condition.field),
            operator: String(condition.operator || 'equals'),
            value: String(condition.value ?? '')
          }))
      };
      setViewMode('table');
      setDraftFilters(nextFilters);
      setActiveFilters(nextFilters);
      setFiltersOpen(false);
      setTableState((current) => ({ ...current, page: 1 }));
    };
    const sharedLoadHandler = (event) => {
      const token = event.detail?.token || window.TDEMAND_SHARED_TASKS?.token || '';
      const sameToken = Boolean(token && token === shareTokenRef.current && sharedMode);
      if (sameToken) {
        setRefreshKey((current) => current + 1);
        setChartRefreshKey((current) => current + 1);
        return;
      }
      window.TDEMAND_ACTIVE_TASKS_DEMAND = null;
      setSharedMode(true);
      setCanEdit(false);
      setShareToken(token);
      setSharePassword('');
      setSharePasswordDraft('');
      setSharePasswordRequired(false);
      setShareMeta(null);
      setDemand(null);
      setSearchTerm('');
      setDebouncedSearchTerm('');
      setFiltersOpen(false);
      setDraftFilters(emptyTaskFilters());
      setActiveFilters(emptyTaskFilters());
      setFilterSaveName('');
	      setTableState((current) => ({
	        ...current,
	        page: 1,
	        pageCount: 1
	      }));
	      setTableSort({ field: 'created_at', direction: 'desc' });
	      resetTaskState(token ? 'Carregando compartilhamento...' : 'Compartilhamento inválido.');
	      if (token) {
	        setRefreshKey((current) => current + 1);
	        setChartRefreshKey((current) => current + 1);
	      }
    };

    window.addEventListener('tdemand:tasks-board', loadHandler);
    window.addEventListener('tdemand:tasks-refresh', refreshHandler);
    window.addEventListener('tdemand:task-deleted', deletedHandler);
    window.addEventListener('tdemand:permissions', permissionsHandler);
    window.addEventListener('tdemand:tasks_apply_filter', dashboardFilterHandler);
    window.addEventListener('tdemand:shared-tasks-load', sharedLoadHandler);
    window.dispatchEvent(new CustomEvent('tdemand:tasks-ready'));
    if (window.TDEMAND_ACTIVE_TASKS_DEMAND) {
      window.setTimeout(() => loadHandler({ detail: { demand: window.TDEMAND_ACTIVE_TASKS_DEMAND } }), 0);
    } else if (window.TDEMAND_SHARED_TASKS?.token) {
      window.setTimeout(() => sharedLoadHandler({ detail: window.TDEMAND_SHARED_TASKS }), 0);
    }

    return () => {
      window.removeEventListener('tdemand:tasks-board', loadHandler);
      window.removeEventListener('tdemand:tasks-refresh', refreshHandler);
      window.removeEventListener('tdemand:task-deleted', deletedHandler);
      window.removeEventListener('tdemand:permissions', permissionsHandler);
      window.removeEventListener('tdemand:tasks_apply_filter', dashboardFilterHandler);
      window.removeEventListener('tdemand:shared-tasks-load', sharedLoadHandler);
    };
  }, [removeTaskFromState, resetTaskState]);

  useEffect(() => {
    loadCurrentTasks();
  }, [loadCurrentTasks, refreshKey]);

  useEffect(() => {
    loadChartTasks();
  }, [loadChartTasks, chartRefreshKey]);

  useEffect(() => {
    setTableState((current) => current.page === 1 ? current : { ...current, page: 1 });
  }, [activeFiltersParam, debouncedSearchTerm, viewMode]);

  useEffect(() => () => {
    window.clearTimeout(realtimeRefreshTimerRef.current);
  }, []);

  useEffect(() => {
    const contextHandler = (event) => {
      const task = event.detail?.task;
      if (!task || sharedMode) return;
      const menuWidth = 184;
      const menuHeight = canEdit ? 132 : 48;
      setKanbanContextMenu({
        type: 'task',
        task,
        left: Math.max(8, Math.min(Number(event.detail?.clientX || 0), window.innerWidth - menuWidth - 8)),
        top: Math.max(8, Math.min(Number(event.detail?.clientY || 0), window.innerHeight - menuHeight - 8)),
        canDelete: canEdit
      });
    };
    const closeHandler = (event) => {
      if (!event.target?.closest?.('.td-canvas-context-menu')) setKanbanContextMenu(null);
    };
    window.addEventListener('tdemand:task-card-contextmenu', contextHandler);
    window.addEventListener('click', closeHandler);
    return () => {
      window.removeEventListener('tdemand:task-card-contextmenu', contextHandler);
      window.removeEventListener('click', closeHandler);
    };
  }, [canEdit, sharedMode]);

  const statusOptions = useMemo(() => normalizeStatuses(demand).map((item) => item.name).filter(Boolean), [demand]);

  const kanbanBoard = useMemo(() => {
    if (!collapsedColumns.size || !board?.root?.children?.length) return board;
    const nextBoard = { ...board };
    board.root.children.forEach((columnId) => {
      if (!collapsedColumns.has(String(columnId))) return;
      const currentColumn = board[columnId];
      if (!currentColumn) return;
      nextBoard[columnId] = {
        ...currentColumn,
        children: []
      };
    });
    return nextBoard;
  }, [board, collapsedColumns]);

  useEffect(() => {
    const existing = new Set((board?.root?.children || []).map((columnId) => String(columnId)));
    setCollapsedColumns((current) => {
      if (!current.size) return current;
      const next = new Set([...current].filter((columnId) => existing.has(String(columnId))));
      return next.size === current.size ? current : next;
    });
  }, [board]);

  const columnForStatus = useCallback((statusName) => {
    const status = String(statusName || '').trim();
    if (!status) return null;
    return (board?.root?.children || [])
      .map((columnId) => ({ columnId, column: board[columnId] }))
      .find((entry) => entry.column?.title === status) || null;
  }, [board]);

  const openStatusActionModal = useCallback((modal) => {
    if (!canEdit) {
      setNotice('Sua role User permite apenas visualização.');
      return;
    }
    if (!statusOptions.length) {
      setNotice('Nenhum status disponível nesta demanda.');
      return;
    }
    const currentStatus = String(modal?.currentStatus || '').trim();
    const fallback = statusOptions.includes(currentStatus) ? currentStatus : statusOptions[0];
    setStatusActionModal({
      scope: modal.scope,
      task: modal.task || null,
      columnId: modal.columnId || '',
      currentStatus,
      options: statusOptions,
      value: fallback,
      running: false,
      error: ''
    });
  }, [canEdit, statusOptions]);

  const toggleColumnCollapsed = useCallback((columnId, force = null) => {
    const key = String(columnId || '').trim();
    if (!key) return;
    setCollapsedColumns((current) => {
      const next = new Set(current);
      const shouldCollapse = force == null ? !next.has(key) : Boolean(force);
      if (shouldCollapse) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const columnsById = useMemo(() => {
    if (!kanbanBoard.root) return {};
    return kanbanBoard.root.children.reduce((acc, columnId) => {
      acc[columnId] = kanbanBoard[columnId];
      return acc;
    }, {});
  }, [kanbanBoard]);

  const configMap = useMemo(() => ({
    card: {
      render: TaskCard,
      isDraggable: canEdit
    }
  }), [canEdit]);

	  const requestTaskDelete = useCallback((task) => {
	    if (!task || !canEdit || sharedMode) return;
	    setKanbanContextMenu(null);
	    window.dispatchEvent(new CustomEvent('tdemand:task-delete-request', {
	      detail: {
	        task,
	        demand
	      }
	    }));
	  }, [canEdit, demand, sharedMode]);

  const applySavedTask = useCallback((savedTask, source = 'table') => {
    if (!savedTask?.id) return;
    setTasks((current) => current.map((task) => (
      String(task.id) === String(savedTask.id)
        ? {
            ...task,
            ...savedTask,
            comments_count: savedTask.comments_count ?? savedTask.comment_count ?? task.comments_count ?? task.comment_count ?? 0
          }
        : task
    )));
    setBoard((current) => {
      const taskNodeId = `task-${savedTask.id}`;
      if (!current[taskNodeId]) return current;
      const currentTask = current[taskNodeId].content?.task || {};
      const nextTask = {
        ...currentTask,
        ...savedTask,
        comments_count: savedTask.comments_count ?? savedTask.comment_count ?? currentTask.comments_count ?? currentTask.comment_count ?? 0
      };
      return {
        ...current,
        [taskNodeId]: {
          ...current[taskNodeId],
          title: taskTitle(nextTask),
          content: {
            ...current[taskNodeId].content,
            task: nextTask,
            coverFile: findTaskCoverFile(nextTask, demand),
            payloadPreview: payloadPreviewValues(demand, nextTask.payload),
            updatedAt: formatDateTime(nextTask.updated_at)
          }
        }
      };
    });
    window.dispatchEvent(new CustomEvent('tdemand:task-updated', {
      detail: {
        demandId: demand?.id || savedTask.demand_id,
        task: savedTask,
        source
      }
    }));
  }, [demand?.id]);

  const handleTaskSavedFromTable = useCallback((savedTask) => {
    applySavedTask(savedTask, 'table');
  }, [applySavedTask]);

  const scheduleRealtimeRefresh = useCallback((incomingTask = null) => {
    if (incomingTask?.id) applySavedTask(incomingTask, 'realtime');
    window.clearTimeout(realtimeRefreshTimerRef.current);
    realtimeRefreshTimerRef.current = window.setTimeout(() => {
      if (viewMode === 'charts') {
        setChartRefreshKey((current) => current + 1);
      } else {
        setRefreshKey((current) => current + 1);
      }
    }, 350);
  }, [applySavedTask, viewMode]);

  useEffect(() => {
    const demandId = demand?.id;
    if (!tasksPageActive && !sharedMode) return undefined;
    if (!sharedMode && !demandId) return undefined;
    if (sharedMode && !shareToken) return undefined;
    if (sharedMode && sharePasswordRequired && !sharePassword) return undefined;

    let disposed = false;
    let reconnectTimer = null;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}/ws/tasks`;

    const connect = () => {
      if (disposed) return;
      const socket = new WebSocket(url);
      realtimeSocketRef.current = socket;

      socket.addEventListener('open', () => {
        const payload = sharedMode
          ? { type: 'subscribe-shared', token: shareToken, password: sharePassword }
          : { type: 'subscribe', demandId };
        socket.send(JSON.stringify(payload));
      });

      socket.addEventListener('message', (event) => {
        let message = null;
        try {
          message = JSON.parse(event.data);
        } catch (error) {
          return;
        }
        if (message.type === 'error') {
          if (sharedMode && message.password_required) {
            setSharePasswordRequired(true);
          }
          return;
        }
        if (message.type === 'tasks.presence') {
          const sameDemand = sharedMode
            ? (!demand?.id || String(message.demandId || '') === String(demand.id))
            : String(message.demandId || '') === String(demandId);
          if (!sameDemand) return;
          setPresenceViewers(Array.isArray(message.viewers) ? message.viewers : []);
          return;
        }
        if (message.type !== 'tasks.changed') return;
        const sameDemand = sharedMode
          ? (!demand?.id || String(message.demandId || '') === String(demand.id))
          : String(message.demandId || '') === String(demandId);
        if (!sameDemand) return;
        scheduleRealtimeRefresh(message.task || null);
      });

      socket.addEventListener('close', () => {
        if (realtimeSocketRef.current === socket) realtimeSocketRef.current = null;
        if (!disposed) reconnectTimer = window.setTimeout(connect, 2500);
      });

      socket.addEventListener('error', () => {
        socket.close();
      });
    };

    connect();

    return () => {
      disposed = true;
      window.clearTimeout(reconnectTimer);
      if (realtimeSocketRef.current) {
        realtimeSocketRef.current.close();
        realtimeSocketRef.current = null;
      }
    };
  }, [demand?.id, scheduleRealtimeRefresh, sharePassword, sharePasswordRequired, shareToken, sharedMode, tasksPageActive]);

  const handleCardMove = useCallback(async (move) => {
    if (!canEdit) {
      setNotice('Sua role User permite apenas visualização.');
      return;
    }
    const card = board[move.cardId];
    const targetColumn = columnsById[move.toColumnId];
    if (!card || !targetColumn || !demand?.id) return;

    const nextStatus = targetColumn.title;
    const previousBoard = board;
    const previousTasks = tasks;
    const movedAcrossColumns = move.fromColumnId !== move.toColumnId;
    const nextBoard = dropHandler(
      move,
      board,
      (column, movedCard) => ({
        ...movedCard,
        parentId: column.id,
        content: {
          ...movedCard.content,
          task: {
            ...movedCard.content.task,
            status: column.title
          }
        }
      }),
      (column) => ({
        ...column,
        totalChildrenCount: movedAcrossColumns
          ? Math.max(column.children.length, Number(previousBoard[column.id]?.totalChildrenCount || 0) + 1)
          : Number(previousBoard[column.id]?.totalChildrenCount ?? column.children.length)
      }),
      (column) => ({
        ...column,
        totalChildrenCount: movedAcrossColumns
          ? Math.max(column.children.length, Number(previousBoard[column.id]?.totalChildrenCount || 0) - 1)
          : Number(previousBoard[column.id]?.totalChildrenCount ?? column.children.length)
      })
    );

    setBoard(nextBoard);
    setTasks((current) => current.map((task) => (
      String(task.id) === String(card.content.task.id)
        ? { ...task, status: nextStatus, updated_at: new Date().toISOString() }
        : task
    )));
    setNotice('');

    try {
      const response = await api(`/api/demands/${demand.id}/tasks/${card.content.task.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: nextStatus
        })
      });
      const savedTask = response.data || {
        ...card.content.task,
        status: nextStatus
      };
      applySavedTask(savedTask, 'kanban');
    } catch (error) {
      setBoard(previousBoard);
      setTasks(previousTasks);
      setNotice(error.message);
    }
  }, [applySavedTask, board, canEdit, columnsById, demand, tasks]);

  const handleColumnMove = useCallback(async (move) => {
    if (!canEdit || sharedMode || columnOrderSaving || !demand?.id) return;
    const previousBoard = boardRef.current;
    if (!previousBoard?.root?.children?.length || move.fromIndex === move.toIndex) return;
    const previousColumnOrder = [...previousBoard.root.children];

    const nextBoard = dropColumnHandler(move, {
      ...previousBoard,
      root: {
        ...previousBoard.root,
        children: [...previousBoard.root.children]
      }
    });
    const orderedNames = (nextBoard.root?.children || [])
      .map((columnId) => nextBoard[columnId]?.title)
      .filter(Boolean);
    if (orderedNames.length !== normalizeStatuses(demand).length) return;

    const previousDemand = demand;
    const statusesByName = new Map(normalizeStatuses(demand).map((status) => [status.name, status]));
    const nextStatuses = orderedNames.map((name) => statusesByName.get(name)).filter(Boolean);
    const optimisticDemand = { ...demand, task_statuses: nextStatuses };

    boardRef.current = nextBoard;
    setBoard(nextBoard);
    setDemand(optimisticDemand);
    window.TDEMAND_ACTIVE_TASKS_DEMAND = optimisticDemand;
    setColumnOrderSaving(true);
    setNotice('');

    try {
      const response = await api(`/api/demands/${demand.id}/task-status-order`, {
        method: 'PATCH',
        body: JSON.stringify({ statuses: orderedNames })
      });
      const savedDemand = {
        ...optimisticDemand,
        ...(response.data || {}),
        task_statuses: response.data?.task_statuses || nextStatuses
      };
      setDemand(savedDemand);
      window.TDEMAND_ACTIVE_TASKS_DEMAND = savedDemand;
    } catch (error) {
      setBoard((current) => {
        const rollbackBoard = {
          ...current,
          root: {
            ...current.root,
            children: previousColumnOrder
          }
        };
        boardRef.current = rollbackBoard;
        return rollbackBoard;
      });
      setDemand((current) => {
        const rollbackDemand = {
          ...(current || previousDemand),
          task_statuses: previousDemand.task_statuses
        };
        window.TDEMAND_ACTIVE_TASKS_DEMAND = rollbackDemand;
        return rollbackDemand;
      });
      setNotice(error.message || 'Não foi possível salvar a ordem das colunas.');
    } finally {
      setColumnOrderSaving(false);
    }
  }, [canEdit, columnOrderSaving, demand, sharedMode]);

  const handleLoadMore = useCallback(async (columnId) => {
    const currentBoard = boardRef.current;
    const column = currentBoard?.[columnId];
    if ((!demand?.id && !sharedMode) || !column) return;

    const loaded = Number(column.children?.length || 0);
    const total = Number(column.totalChildrenCount || 0);
    if (loaded >= total) return;

    const loadingKey = `${demand?.id || shareToken}:${column.title}:${debouncedSearchTerm}:${activeFiltersParam}`;
    if (loadingMoreColumnsRef.current.has(loadingKey)) return;
    loadingMoreColumnsRef.current.add(loadingKey);

    try {
      const result = sharedMode
        ? await loadSharedTasks({
          view: 'kanban',
          status: column.title,
          limit: TASK_KANBAN_COLUMN_LIMIT,
          offset: loaded,
          search: debouncedSearchTerm,
          filters: activeFiltersParam
        })
        : await loadDemandTasks(demand, {
          view: 'kanban',
          status: column.title,
          limit: TASK_KANBAN_COLUMN_LIMIT,
          offset: loaded,
          search: debouncedSearchTerm,
          filters: activeFiltersParam
        });
      const rows = Array.isArray(result.data) ? result.data : [];
      if (!rows.length) return;
      const nextDemand = result.demand || demand;
      applySharedDemandSnapshot(result.demand);

      setTasks((current) => {
        const seen = new Set(current.map((task) => String(task.id)));
        return [
          ...current,
          ...rows.filter((task) => !seen.has(String(task.id)))
        ];
      });
      setBoard((current) => appendTasksToBoard(current, nextDemand, rows, result.status_counts || {}));
      setTaskStats((current) => ({
        ...current,
        loaded: Math.min(current.total, current.loaded + rows.length),
        truncated: current.loaded + rows.length < current.total
      }));
    } catch (error) {
      setNotice(error.message);
    } finally {
      loadingMoreColumnsRef.current.delete(loadingKey);
    }
  }, [activeFiltersParam, applySharedDemandSnapshot, debouncedSearchTerm, demand, loadDemandTasks, loadSharedTasks, shareToken, sharedMode]);

  const handleTaskOpen = useCallback((task) => {
    window.dispatchEvent(new CustomEvent('tdemand:task-open', {
      detail: {
        task,
        demand,
        shared: sharedMode
      }
    }));
  }, [demand, sharedMode]);

  const handleCardClick = useCallback((event, card) => {
    event.stopPropagation();
    handleTaskOpen(card.content.task);
  }, [handleTaskOpen]);

  const handleKanbanSurfaceContextMenu = useCallback((event) => {
    if (viewMode !== 'kanban' || sharedMode) return;
    if (event.target?.closest?.('[data-task-card-id]')) return;
    const columnNode = event.target?.closest?.('.td-kanban-column');
    if (!columnNode) return;
    const status = String(columnNode.querySelector('.td-column-title-text')?.textContent || '').trim();
    const entry = columnForStatus(status);
    if (!entry) return;

    event.preventDefault();
    event.stopPropagation();
    const menuWidth = 220;
    const menuHeight = canEdit ? 132 : 96;
    setKanbanContextMenu({
      type: 'column',
      columnId: String(entry.columnId),
      status,
      collapsed: collapsedColumns.has(String(entry.columnId)),
      canEdit,
      left: Math.max(8, Math.min(Number(event.clientX || 0), window.innerWidth - menuWidth - 8)),
      top: Math.max(8, Math.min(Number(event.clientY || 0), window.innerHeight - menuHeight - 8))
    });
  }, [canEdit, collapsedColumns, columnForStatus, sharedMode, viewMode]);

  const handleKanbanContextCopy = useCallback(async () => {
    if (kanbanContextMenu?.type !== 'task' || !kanbanContextMenu.task) return;
    const columns = buildTaskTableColumns(demand, [kanbanContextMenu.task]);
    const text = taskTableRowCopyText(kanbanContextMenu.task, columns, demand);
    try {
      await navigator.clipboard.writeText(text);
      setNotice('Task copiada com todos os campos.');
    } catch (error) {
      setNotice(text);
    }
    setKanbanContextMenu(null);
  }, [demand, kanbanContextMenu]);

  const handleKanbanContextDelete = useCallback(() => {
    if (kanbanContextMenu?.type !== 'task' || !kanbanContextMenu.task || !kanbanContextMenu.canDelete) return;
    requestTaskDelete(kanbanContextMenu.task);
  }, [kanbanContextMenu, requestTaskDelete]);

  const handleKanbanContextToggleColumn = useCallback(() => {
    if (kanbanContextMenu?.type !== 'column' || !kanbanContextMenu.columnId) return;
    toggleColumnCollapsed(kanbanContextMenu.columnId);
    setKanbanContextMenu(null);
  }, [kanbanContextMenu, toggleColumnCollapsed]);

  const handleKanbanContextAddTask = useCallback(() => {
    if (kanbanContextMenu?.type !== 'column') return;
    if (!canEdit) {
      setNotice('Sua role User permite apenas visualização.');
      setKanbanContextMenu(null);
      return;
    }
    if (!demand?.id) return;
    window.dispatchEvent(new CustomEvent('tdemand:task-import-open', {
      detail: {
        demand,
        status: kanbanContextMenu.status || ''
      }
    }));
    setKanbanContextMenu(null);
  }, [canEdit, demand, kanbanContextMenu]);

  const handleKanbanContextChangeStatus = useCallback(() => {
    if (!canEdit) {
      setNotice('Sua role User permite apenas visualização.');
      setKanbanContextMenu(null);
      return;
    }
    if (kanbanContextMenu?.type === 'task' && kanbanContextMenu.task) {
      openStatusActionModal({
        scope: 'task',
        task: kanbanContextMenu.task,
        currentStatus: kanbanContextMenu.task.status
      });
    } else if (kanbanContextMenu?.type === 'column' && kanbanContextMenu.columnId) {
      openStatusActionModal({
        scope: 'column',
        columnId: kanbanContextMenu.columnId,
        currentStatus: kanbanContextMenu.status
      });
    }
    setKanbanContextMenu(null);
  }, [canEdit, kanbanContextMenu, openStatusActionModal]);

  const handleCsvDownload = useCallback(async () => {
    if ((!demand?.id && !sharedMode) || csvExporting) return;
    setCsvExporting(true);
    setNotice('Gerando CSV com todas as tasks do filtro atual...');
    try {
      const result = sharedMode
        ? await loadSharedTasks({ view: 'export', search: debouncedSearchTerm, filters: activeFiltersParam })
        : await loadDemandTasks(demand, {
          view: 'export',
          search: debouncedSearchTerm,
          filters: activeFiltersParam
        });
      const rows = Array.isArray(result.data) ? result.data : [];
      if (!rows.length) {
        setNotice('Nenhuma task encontrada para exportar.');
        return;
      }
      const nextDemand = result.demand || demand;
      const csv = writeCsvObjects(buildCsvRows(nextDemand, rows), { delimiter: ',', newline: '\r\n' });
      const filenameBase = slug(nextDemand?.name || nextDemand?.protocol || 'tasks');
      downloadTextFile(`t-demand-${filenameBase}-tasks.csv`, csv, 'text/csv;charset=utf-8');
      const exported = Number(result.count || rows.length).toLocaleString('pt-BR');
      const total = Number(result.total_count || rows.length).toLocaleString('pt-BR');
      setNotice(result.truncated
        ? `CSV gerado com ${exported} de ${total} tasks. Refine a busca para exportar menos registros.`
        : `CSV gerado com ${exported} task(s).`);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setCsvExporting(false);
    }
  }, [activeFiltersParam, csvExporting, debouncedSearchTerm, demand, loadDemandTasks, loadSharedTasks, sharedMode]);

  const handleFilteredStatusUpdate = useCallback(async () => {
    if (!demand?.id || sharedMode) return;
    if (!canEdit) {
      setNotice('Sua role User permite apenas visualização.');
      return;
    }
    const hasFilterScope = Boolean(activeFiltersCount || normalizeSearchQuery(debouncedSearchTerm));
    if (!hasFilterScope) {
      setNotice('Aplique ao menos um filtro ou busca antes do status em lote.');
      return;
    }
    if (!statusOptions.length) {
      setNotice('Nenhum status disponível para atualizar em lote.');
      return;
    }
    setStatusActionModal({
      scope: 'filtered',
      task: null,
      columnId: '',
      currentStatus: '',
      options: statusOptions,
      value: statusOptions[0],
      running: false,
      error: ''
    });
  }, [activeFiltersCount, canEdit, debouncedSearchTerm, demand, sharedMode, statusOptions]);

  const executeStatusAction = useCallback(async () => {
    if (!demand?.id || sharedMode || !statusActionModal || statusActionModal.running) return;
    const nextStatus = String(statusActionModal.value || '').trim();
    const options = Array.isArray(statusActionModal.options) ? statusActionModal.options : [];
    if (!options.includes(nextStatus)) {
      setStatusActionModal((current) => (current ? {
        ...current,
        error: `Status inválido. Opções: ${options.join(', ')}`
      } : current));
      return;
    }

    setStatusActionModal((current) => (current ? { ...current, running: true, error: '' } : current));
    setLoading(true);
    setNotice('Atualizando status...');
    try {
      if (statusActionModal.scope === 'task') {
        const task = statusActionModal.task;
        if (!task?.id) throw new Error('Task não encontrada para alteração de status.');
        const result = await api(`/api/demands/${encodeURIComponent(demand.id)}/tasks/${encodeURIComponent(task.id)}`, {
          method: 'PUT',
          body: JSON.stringify({ status: nextStatus })
        });
        const savedTask = result.data || { ...task, status: nextStatus };
        applySavedTask(savedTask, 'kanban-context');
        setNotice(`Task ${taskTitle(savedTask)} atualizada para ${nextStatus}.`);
      } else {
        const payload = {
          new_status: nextStatus,
          search: debouncedSearchTerm,
          filters: activeFiltersParam,
          limit: TASK_FILTER_BATCH_LIMIT
        };
        if (statusActionModal.scope === 'column') {
          payload.status_filter = statusActionModal.currentStatus;
        }
        const result = await api(`/api/demands/${encodeURIComponent(demand.id)}/tasks/status-filtered`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        const updated = Number(result?.data?.updated || 0);
        const scopeLabel = statusActionModal.scope === 'column'
          ? `na coluna ${statusActionModal.currentStatus}`
          : 'no filtro atual';
        setNotice(`${updated.toLocaleString('pt-BR')} task(s) atualizada(s) para ${nextStatus} (${scopeLabel}).`);
      }

      setStatusActionModal(null);
      setRefreshKey((current) => current + 1);
    } catch (error) {
      setStatusActionModal((current) => (current ? {
        ...current,
        running: false,
        error: error.message || 'Não foi possível atualizar o status.'
      } : current));
    } finally {
      setLoading(false);
    }
  }, [activeFiltersParam, applySavedTask, debouncedSearchTerm, demand, sharedMode, statusActionModal]);

  const hasFilterScope = useCallback(() => (
    Boolean(activeFiltersCount || normalizeSearchQuery(debouncedSearchTerm))
  ), [activeFiltersCount, debouncedSearchTerm]);

  const openDeleteFilteredTasksModal = useCallback((selection = null) => {
    if (!demand?.id || sharedMode) return;
    if (!canEdit) {
      setNotice('Sua role User permite apenas visualização.');
      return;
    }
    const taskIds = [...new Set((Array.isArray(selection?.taskIds) ? selection.taskIds : [])
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item > 0))];
    const selectedScope = taskIds.length > 0;
    if (!selectedScope && !hasFilterScope()) {
      setNotice('Aplique ao menos um filtro ou busca antes de remover tasks filtradas.');
      return;
    }
    setFilterBatchModal({
      type: 'delete',
      scope: selectedScope ? 'selected' : 'filtered',
      taskIds,
      selectedCount: taskIds.length,
      clearSelection: selection?.clearSelection,
      running: false,
      error: ''
    });
  }, [canEdit, demand, hasFilterScope, sharedMode]);

  const openTransferFilteredTasksModal = useCallback(async (mode, selection = null) => {
    if (!demand?.id || sharedMode) return;
    if (!canEdit) {
      setNotice('Sua role User permite apenas visualização.');
      return;
    }
    const taskIds = [...new Set((Array.isArray(selection?.taskIds) ? selection.taskIds : [])
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item > 0))];
    const selectedScope = taskIds.length > 0;
    if (!selectedScope && !hasFilterScope()) {
      setNotice('Aplique ao menos um filtro ou busca antes da ação em lote.');
      return;
    }

    setFilterBatchModal({
      type: 'transfer',
      mode,
      scope: selectedScope ? 'selected' : 'filtered',
      taskIds,
      selectedCount: taskIds.length,
      clearSelection: selection?.clearSelection,
      loading: true,
      running: false,
      options: [],
      selectedTargetId: '',
      error: ''
    });

    try {
      const result = await api('/api/demands');
      const options = (Array.isArray(result?.data) ? result.data : [])
        .filter((item) => String(item?.id) !== String(demand.id))
        .sort((left, right) => demandTitle(left).localeCompare(demandTitle(right), 'pt-BR'));

      if (!options.length) {
        setFilterBatchModal(null);
        setNotice('Não há outra demanda disponível para destino.');
        return;
      }

      setFilterBatchModal({
        type: 'transfer',
        mode,
        scope: selectedScope ? 'selected' : 'filtered',
        taskIds,
        selectedCount: taskIds.length,
        clearSelection: selection?.clearSelection,
        loading: false,
        running: false,
        options,
        selectedTargetId: String(options[0].id),
        error: ''
      });
    } catch (error) {
      setFilterBatchModal(null);
      setNotice(error.message || 'Não foi possível carregar as demandas de destino.');
    }
  }, [canEdit, demand, hasFilterScope, sharedMode]);

  const executeDeleteFilteredTasks = useCallback(async () => {
    if (!demand?.id || filterBatchModal?.type !== 'delete' || filterBatchModal.running) return;
    const selectedScope = filterBatchModal.scope === 'selected';

    setFilterBatchModal((current) => (current?.type === 'delete' ? { ...current, running: true, error: '' } : current));
    setLoading(true);
    setNotice(selectedScope ? 'Removendo tasks selecionadas...' : 'Removendo tasks filtradas...');
    try {
      const result = await api(`/api/demands/${encodeURIComponent(demand.id)}/tasks/filtered`, {
        method: 'DELETE',
        body: JSON.stringify({
          task_ids: selectedScope ? filterBatchModal.taskIds : undefined,
          search: selectedScope ? '' : debouncedSearchTerm,
          filters: selectedScope ? undefined : activeFiltersParam,
          limit: 5000
        })
      });
      const removed = Number(result?.data?.removed || 0);
      setNotice(`${removed.toLocaleString('pt-BR')} task(s) removida(s) ${selectedScope ? 'da seleção' : 'do filtro atual'}.`);
      filterBatchModal.clearSelection?.();
      setRefreshKey((current) => current + 1);
      setFilterBatchModal(null);
    } catch (error) {
      setFilterBatchModal((current) => (
        current?.type === 'delete'
          ? { ...current, running: false, error: error.message || 'Não foi possível remover tasks filtradas.' }
          : current
      ));
      setNotice(error.message || 'Não foi possível remover tasks filtradas.');
    } finally {
      setLoading(false);
    }
  }, [activeFiltersParam, debouncedSearchTerm, demand, filterBatchModal]);

  const executeTransferFilteredTasks = useCallback(async () => {
    if (!demand?.id || filterBatchModal?.type !== 'transfer' || filterBatchModal.loading || filterBatchModal.running) return;
    const target = filterBatchModal.options.find((item) => String(item.id) === String(filterBatchModal.selectedTargetId));
    if (!target) {
      setFilterBatchModal((current) => (
        current?.type === 'transfer' ? { ...current, error: 'Selecione uma demanda de destino válida.' } : current
      ));
      return;
    }

    const mode = filterBatchModal.mode === 'move' ? 'move' : 'copy';
    const selectedScope = filterBatchModal.scope === 'selected';
    const route = mode === 'move' ? 'move-filtered' : 'copy-filtered';
    const verb = mode === 'move' ? 'Movendo' : 'Copiando';

    setFilterBatchModal((current) => (
      current?.type === 'transfer' ? { ...current, running: true, error: '' } : current
    ));
    setLoading(true);
    setNotice(`${verb} tasks ${selectedScope ? 'selecionadas' : 'filtradas'}...`);
    try {
      const result = await api(`/api/demands/${encodeURIComponent(demand.id)}/tasks/${route}`, {
        method: 'POST',
        body: JSON.stringify({
          target_demand_id: target.id,
          task_ids: selectedScope ? filterBatchModal.taskIds : undefined,
          search: selectedScope ? '' : debouncedSearchTerm,
          filters: selectedScope ? undefined : activeFiltersParam,
          limit: TASK_FILTER_BATCH_LIMIT
        })
      });
      const moved = Number(result?.data?.moved || 0);
      const copied = Number(result?.data?.copied || 0);
      const fallback = Number(result?.data?.status_fallback_count || 0);
      const amount = mode === 'move' ? moved : copied;
      const action = mode === 'move' ? 'movida(s)' : 'copiada(s)';
      const suffix = fallback > 0
        ? ` ${fallback.toLocaleString('pt-BR')} task(s) usaram status inicial por incompatibilidade de status.`
        : '';
      setNotice(`${amount.toLocaleString('pt-BR')} task(s) ${action} para ${demandTitle(target)}.${suffix}`);
      filterBatchModal.clearSelection?.();
      setRefreshKey((current) => current + 1);
      setFilterBatchModal(null);
    } catch (error) {
      setFilterBatchModal((current) => (
        current?.type === 'transfer'
          ? { ...current, running: false, error: error.message || 'Não foi possível transferir tasks filtradas.' }
          : current
      ));
      setNotice(error.message || 'Não foi possível transferir tasks filtradas.');
    } finally {
      setLoading(false);
    }
  }, [activeFiltersParam, debouncedSearchTerm, demand, filterBatchModal]);

  const handleDeleteFilteredTasks = useCallback(() => {
    openDeleteFilteredTasksModal();
  }, [openDeleteFilteredTasksModal]);

  const handleMoveFilteredTasks = useCallback(() => {
    openTransferFilteredTasksModal('move');
  }, [openTransferFilteredTasksModal]);

  const handleCopyFilteredTasks = useCallback(() => {
    openTransferFilteredTasksModal('copy');
  }, [openTransferFilteredTasksModal]);

  const handleTTableBulkAction = useCallback((rawAction, taskIds, clearSelection) => {
    const action = String(rawAction || '').trim().toLowerCase();
    const selection = { taskIds, clearSelection };
    if (action === 'move' || action === 'mover') {
      void openTransferFilteredTasksModal('move', selection);
      return;
    }
    if (action === 'copy' || action === 'copiar') {
      void openTransferFilteredTasksModal('copy', selection);
      return;
    }
    if (['remove', 'remover', 'delete', 'apagar'].includes(action)) {
      openDeleteFilteredTasksModal(selection);
    }
  }, [openDeleteFilteredTasksModal, openTransferFilteredTasksModal]);

  useEffect(() => {
    window.addEventListener('tdemand:tasks-download-csv', handleCsvDownload);
    return () => window.removeEventListener('tdemand:tasks-download-csv', handleCsvDownload);
  }, [handleCsvDownload]);

  useEffect(() => {
    window.addEventListener('tdemand:tasks-status-filtered', handleFilteredStatusUpdate);
    window.addEventListener('tdemand:tasks-delete-filtered', handleDeleteFilteredTasks);
    return () => {
      window.removeEventListener('tdemand:tasks-status-filtered', handleFilteredStatusUpdate);
      window.removeEventListener('tdemand:tasks-delete-filtered', handleDeleteFilteredTasks);
    };
  }, [handleDeleteFilteredTasks, handleFilteredStatusUpdate]);

  useEffect(() => {
    if (!filterBatchModal && !statusActionModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (filterBatchModal?.running || statusActionModal?.running) return;
      setFilterBatchModal(null);
      setStatusActionModal(null);
      setKanbanContextMenu(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [filterBatchModal, statusActionModal]);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setTableState((current) => current.page === 1 ? current : { ...current, page: 1 });
  }, []);

  const handleTablePageSizeChange = useCallback((event) => {
    const pageSize = Number(event.target.value || TASK_TABLE_DEFAULT_PAGE_SIZE);
    setTableState((current) => ({
      ...current,
      page: 1,
      pageSize
    }));
  }, []);

  const handleTableSort = useCallback((field, requestedDirection = '') => {
    if (!field) return;
    setTableSort((current) => ({
      field,
      direction: ['asc', 'desc'].includes(requestedDirection)
        ? requestedDirection
        : current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
    setTableState((current) => current.page === 1 ? current : { ...current, page: 1 });
  }, []);

  const handleFiltersToggle = useCallback(() => {
    setDraftFilters((current) => normalizeTaskFilters(current, filterFields));
    setFiltersOpen((current) => !current);
  }, [filterFields]);

  const handleFiltersApply = useCallback(() => {
    const normalized = normalizeTaskFilters(draftFilters, filterFields);
    setDraftFilters(normalized);
    setActiveFilters(normalized);
    setFiltersOpen(false);
    setTableState((current) => current.page === 1 ? current : { ...current, page: 1 });
  }, [draftFilters, filterFields]);

  const handleFiltersClear = useCallback(() => {
    const empty = emptyTaskFilters();
    setDraftFilters(empty);
    setActiveFilters(empty);
    setFilterSaveName('');
    setFiltersOpen(false);
    setTableState((current) => current.page === 1 ? current : { ...current, page: 1 });
  }, []);

  const handleFilterSave = useCallback(() => {
    const serialized = serializeTaskFilters(draftFilters);
    if (!serialized) {
      setNotice('Crie ao menos uma condição válida antes de salvar o filtro.');
      return;
    }
    const name = filterSaveName.trim() || `Filtro ${new Date().toLocaleDateString('pt-BR')}`;
    const nextItem = {
      id: taskFilterConditionId(),
      name,
      filters: serialized,
      createdAt: new Date().toISOString()
    };
    const nextSaved = [
      nextItem,
      ...savedFilters.filter((item) => item.name.toLowerCase() !== name.toLowerCase())
    ].slice(0, 20);
    setSavedFilters(nextSaved);
    writeStoredTaskFilters(filterScope, nextSaved);
    setFilterSaveName('');
    setNotice(`Filtro "${name}" salvo neste navegador.`);
  }, [draftFilters, filterSaveName, filterScope, savedFilters]);

  const handleFilterLoadSaved = useCallback((item) => {
    const normalized = normalizeTaskFilters(item.filters, filterFields);
    setDraftFilters(normalized);
    setActiveFilters(normalized);
    setFiltersOpen(false);
    setTableState((current) => current.page === 1 ? current : { ...current, page: 1 });
    setNotice(`Filtro "${item.name}" aplicado.`);
  }, [filterFields]);

  const handleChartLegendFilter = useCallback(({ field, value, label }) => {
    const filterField = String(field || '').trim();
    const filterValue = String(value ?? label ?? '').trim();
    if (!filterField || !filterValue) return;

    const availableField = filterFields.find((item) => item.value === filterField);
    if (!availableField) {
      setNotice('Não foi possível localizar este atributo nos filtros da demanda.');
      return;
    }

    const baseFilters = normalizeTaskFilters(activeFilters, filterFields);
    const nextFilters = normalizeTaskFilters({
      ...baseFilters,
      conditions: [
        ...baseFilters.conditions,
        {
          id: taskFilterConditionId(),
          field: filterField,
          operator: 'equals',
          value: filterValue
        }
      ]
    }, filterFields);

    setDraftFilters(nextFilters);
    setActiveFilters(nextFilters);
    setFiltersOpen(true);
    setTableState((current) => current.page === 1 ? current : { ...current, page: 1 });
    setNotice(`Filtro adicionado: ${availableField.label} = ${filterValue}`);
  }, [activeFilters, filterFields]);

  const handleFilterDeleteSaved = useCallback((id) => {
    const nextSaved = savedFilters.filter((item) => item.id !== id);
    setSavedFilters(nextSaved);
    writeStoredTaskFilters(filterScope, nextSaved);
  }, [filterScope, savedFilters]);

  const handleSharePasswordSubmit = useCallback((event) => {
    event.preventDefault();
    setSharePassword(sharePasswordDraft);
    setSharePasswordRequired(false);
    setNotice('Validando senha do compartilhamento...');
    setRefreshKey((current) => current + 1);
  }, [sharePasswordDraft]);

  const totalTasks = taskStats.total || 0;
  const loadedTasks = taskStats.loaded || tasks.length;
  const hasSearch = Boolean(normalizeSearchQuery(searchTerm));
  const taskStatLabel = hasSearch ? 'Encontradas' : 'Tasks';
  const taskStatValue = totalTasks.toLocaleString('pt-BR');
  const statusCount = demand ? normalizeStatuses(demand).length : 0;
  const headerControlsRoot = document.getElementById('tasksBoardHeaderControls');
  const headerSummaryRoot = document.getElementById('tasksBoardHeaderSummary');
  const headerControls = (
    <div className="tasks-board-controls">
      <div className="tasks-view-toggle" role="group" aria-label="Modo de visualização das tasks">
        <button type="button" className={viewMode === 'kanban' ? 'is-active' : ''} onClick={() => setViewMode('kanban')}>
          <span className="material-symbols-rounded" aria-hidden="true">view_kanban</span>
          Kanban
        </button>
        <button type="button" className={viewMode === 'table' ? 'is-active' : ''} onClick={() => setViewMode('table')}>
          <span className="material-symbols-rounded" aria-hidden="true">table_view</span>
          Tabela
        </button>
        <button type="button" className={viewMode === 'charts' ? 'is-active' : ''} onClick={() => setViewMode('charts')}>
          <span className="material-symbols-rounded" aria-hidden="true">query_stats</span>
          Gráfico
        </button>
      </div>
    </div>
  );
  const headerSummary = (
    <div className="tasks-board-summary-grid">
      <div className={`tasks-board-search ${searchOpen ? 'is-open' : ''} ${searchTerm ? 'has-value' : ''}`} ref={searchPopoverRef}>
        <button
          type="button"
          className="tasks-board-search-trigger"
          onClick={() => setSearchOpen((current) => !current)}
          disabled={!demand}
          aria-label="Buscar tasks"
          aria-expanded={searchOpen ? 'true' : 'false'}
          aria-haspopup="dialog"
        >
          <span className="material-symbols-rounded" aria-hidden="true">search</span>
        </button>
        {searchOpen && (
          <div className="tasks-board-search-popover" role="dialog" aria-label="Buscar tasks por qualquer campo">
            <label>
              <span className="material-symbols-rounded" aria-hidden="true">search</span>
              <input
                type="search"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Buscar em todas as páginas..."
                autoComplete="off"
                disabled={!demand}
                autoFocus
              />
              {searchTerm && (
                <button type="button" onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearchTerm('');
                  setTableState((current) => current.page === 1 ? current : { ...current, page: 1 });
                }} aria-label="Limpar busca">
                  <span className="material-symbols-rounded" aria-hidden="true">close</span>
                </button>
              )}
            </label>
            <small>Busca em todas as páginas da demanda.</small>
          </div>
        )}
      </div>
      <TaskFilterDropdown
        open={filtersOpen}
        onToggle={handleFiltersToggle}
        fields={filterFields}
        draft={draftFilters}
        activeCount={activeFiltersCount}
        savedFilters={savedFilters}
        saveName={filterSaveName}
        onSaveNameChange={setFilterSaveName}
        onDraftChange={setDraftFilters}
        onApply={handleFiltersApply}
        onClear={handleFiltersClear}
        onSave={handleFilterSave}
        onLoadSaved={handleFilterLoadSaved}
        onDeleteSaved={handleFilterDeleteSaved}
        onMoveFiltered={handleMoveFilteredTasks}
        onCopyFiltered={handleCopyFilteredTasks}
        onDeleteFiltered={handleDeleteFilteredTasks}
        canEdit={canEdit}
        busy={loading}
        disabled={!demand || loading}
      />
      <div className="tasks-board-stat">
        <span>{taskStatLabel}</span>
        <strong>{taskStatValue}</strong>
      </div>
      <div className="tasks-board-stat">
        <span>Status</span>
        <strong>{statusCount}</strong>
      </div>
      {!sharedMode && (
        <TaskPresenceStack
          viewers={presenceViewers}
          open={presenceOpen}
          onToggle={() => setPresenceOpen((current) => !current)}
          onClose={() => setPresenceOpen(false)}
          disabled={!demand}
        />
      )}
    </div>
  );
  const isTransferModal = filterBatchModal?.type === 'transfer';
  const isDeleteModal = filterBatchModal?.type === 'delete';
  const isSelectedBatch = filterBatchModal?.scope === 'selected';
  const selectedBatchCount = Number(filterBatchModal?.selectedCount || filterBatchModal?.taskIds?.length || 0);
  const transferMode = isTransferModal && filterBatchModal.mode === 'move' ? 'move' : 'copy';
  const statusActionOptions = Array.isArray(statusActionModal?.options) ? statusActionModal.options : [];

	  return (
	    <>
	      {headerControlsRoot && createPortal(headerControls, headerControlsRoot)}
	      {headerSummaryRoot && createPortal(headerSummary, headerSummaryRoot)}
		      <section className="td-kanban" ref={kanbanShellRef} onContextMenu={handleKanbanSurfaceContextMenu}>
	        {sharePasswordRequired ? (
	          <form className="td-share-password-card" onSubmit={handleSharePasswordSubmit}>
	            <span className="material-symbols-rounded" aria-hidden="true">lock</span>
	            <strong>Esta demanda está protegida por senha</strong>
	            <p>Informe a senha recebida junto com o link para visualizar Kanban, tabela e dados da demanda.</p>
	            <label>
	              Senha
	              <input
	                type="password"
	                value={sharePasswordDraft}
	                onChange={(event) => setSharePasswordDraft(event.target.value)}
	                autoFocus
	                placeholder="Digite a senha"
	              />
	            </label>
	            <button className="primary-button" type="submit">
	              <span className="material-symbols-rounded" aria-hidden="true">key</span>
	              Acessar compartilhamento
	            </button>
	          </form>
	        ) : (
	        <>
        {kanbanContextMenu && (
          <div
            className="td-canvas-context-menu td-kanban-context-menu"
            style={{ left: `${kanbanContextMenu.left}px`, top: `${kanbanContextMenu.top}px` }}
            onClick={(event) => event.stopPropagation()}
            role="menu"
          >
            {kanbanContextMenu.type === 'column' ? (
              <>
                <button type="button" onClick={handleKanbanContextToggleColumn} role="menuitem">
                  <span className="material-symbols-rounded" aria-hidden="true">
                    {kanbanContextMenu.collapsed ? 'expand_content' : 'collapse_content'}
                  </span>
                  {kanbanContextMenu.collapsed ? 'Expandir' : 'Recolher'}
                </button>
                {kanbanContextMenu.canEdit && (
                  <button type="button" onClick={handleKanbanContextAddTask} role="menuitem">
                    <span className="material-symbols-rounded" aria-hidden="true">add_circle</span>
                    Adicionar
                  </button>
                )}
                {kanbanContextMenu.canEdit && (
                  <button type="button" onClick={handleKanbanContextChangeStatus} role="menuitem">
                    <span className="material-symbols-rounded" aria-hidden="true">swap_horiz</span>
                    Alterar status
                  </button>
                )}
              </>
            ) : (
              <>
                <button type="button" onClick={handleKanbanContextCopy} role="menuitem">
                  <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
                  Copiar
                </button>
                {kanbanContextMenu.canDelete && (
                  <button type="button" onClick={handleKanbanContextChangeStatus} role="menuitem">
                    <span className="material-symbols-rounded" aria-hidden="true">swap_horiz</span>
                    Alterar status
                  </button>
                )}
                {kanbanContextMenu.canDelete && (
                  <button className="is-danger" type="button" onClick={handleKanbanContextDelete} role="menuitem">
                    <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                    Apagar
                  </button>
                )}
              </>
            )}
          </div>
        )}
		        {notice && <div className={`td-kanban-notice ${loading ? 'is-loading' : ''}`}>{notice}</div>}
	        {loading && <div className="td-kanban-notice is-loading">Carregando tasks...</div>}

        {!tasksPageActive && !sharedMode ? (
          <div className="td-kanban-notice">Abra a página de Tasks para carregar o Kanban.</div>
        ) : viewMode === 'kanban' ? (
          <Kanban
            dataSource={kanbanBoard}
            configMap={configMap}
            rootClassName="td-kanban-kit"
            columnWrapperClassName={(column) => `td-kanban-column tone-${column.content?.tone || 'custom'} ${collapsedColumns.has(String(column.id)) ? 'is-collapsed' : ''}`}
            columnHeaderClassName={() => `td-kanban-column-head ${canEdit && !sharedMode ? 'is-reorderable' : ''}`}
            columnListContentClassName={() => 'td-kanban-column-list'}
            cardWrapperClassName="td-kanban-card-wrap"
            renderColumnHeader={(column) => (
              <div className="td-column-header">
                <div className="td-column-header-title">
                  {canEdit && !sharedMode && (
                    <span className="material-symbols-rounded td-column-drag-handle" aria-hidden="true">drag_indicator</span>
                  )}
                  <span className="td-column-title-text">{column.title}</span>
                </div>
                <strong>{column.totalChildrenCount}</strong>
              </div>
            )}
            renderCardDragPreview={(card) => (
              <div className="td-task-card td-task-card-preview">
                <strong>{card.title}</strong>
                <span>{card.content.task.status}</span>
              </div>
            )}
            renderCardDragIndicator={() => <div className="td-card-drop-indicator" />}
            onCardMove={handleCardMove}
            onColumnMove={handleColumnMove}
            onCardClick={handleCardClick}
            loadMore={handleLoadMore}
            allowColumnAdder={false}
            allowColumnDrag={canEdit && !sharedMode && !columnOrderSaving}
            renderColumnDragIndicator={() => <div className="td-column-drop-indicator" />}
            virtualization={true}
            cardsGap={10}
          />
	        ) : viewMode === 'table' ? (
          <div className="td-table-mode">
	            <TaskTTableView
	              demand={demand}
	              tasks={tasks}
	              canEdit={canEdit}
	              onTaskClick={handleTaskOpen}
	              onTaskSaved={handleTaskSavedFromTable}
	              onSort={handleTableSort}
	              onBulkAction={handleTTableBulkAction}
	              bulkTextScope={{
	                search: debouncedSearchTerm,
	                filters: activeFiltersParam
	              }}
	              pagination={{
	                loaded: loadedTasks,
	                total: totalTasks,
	                page: tableState.page,
	                pageCount: tableState.pageCount,
	                pageSize: tableState.pageSize,
	                loading,
	                onPageSizeChange: handleTablePageSizeChange,
	                onPrevious: () => setTableState((current) => ({ ...current, page: Math.max(1, current.page - 1) })),
	                onNext: () => setTableState((current) => ({ ...current, page: Math.min(current.pageCount, current.page + 1) }))
	              }}
	            />
          </div>
	        ) : (
          <TaskChartsDashboard
            demand={demand}
            rows={chartRows}
            loading={chartLoading}
            error={chartError}
            meta={chartMeta}
            onRefresh={() => setChartRefreshKey((current) => current + 1)}
            onLegendFilter={handleChartLegendFilter}
          />
	        )}
	        </>
	        )}
	      </section>
	      {filterBatchModal && (
	        <div
	          className="td-filter-transfer-backdrop"
	          role="presentation"
	          onClick={() => {
	            if (filterBatchModal.running) return;
	            setFilterBatchModal(null);
	          }}
	        >
	          <form
	            className="td-filter-transfer-modal"
	            role="dialog"
	            aria-modal="true"
	            aria-label={isDeleteModal ? 'Confirmar exclusão de tasks' : 'Selecionar demanda de destino'}
	            onClick={(event) => event.stopPropagation()}
	            onSubmit={(event) => {
	              event.preventDefault();
	              if (isDeleteModal) {
	                executeDeleteFilteredTasks();
	                return;
	              }
	              executeTransferFilteredTasks();
	            }}
	          >
	            <header>
	              <div>
	                <span>{isDeleteModal ? 'Ação irreversível' : isSelectedBatch ? `${selectedBatchCount.toLocaleString('pt-BR')} task(s) selecionada(s)` : 'Transferência em lote'}</span>
	                <strong>
	                  {isDeleteModal
	                    ? `Remover ${isSelectedBatch ? 'tasks selecionadas' : 'tasks filtradas'}`
	                    : transferMode === 'move'
	                      ? 'Mover para outra demanda'
	                      : 'Copiar para outra demanda'}
	                </strong>
	                <p>
	                  {isDeleteModal
	                    ? isSelectedBatch
	                      ? `${selectedBatchCount.toLocaleString('pt-BR')} task(s) selecionada(s) serão removidas desta demanda.`
	                      : 'Essa ação remove somente as tasks que correspondem ao filtro atual.'
	                    : transferMode === 'move'
	                      ? `Selecione a demanda de destino. ${isSelectedBatch ? 'Somente as Tasks selecionadas' : 'As Tasks filtradas'} serão removidas da demanda atual.`
	                      : `Selecione uma demanda de destino para copiar ${isSelectedBatch ? 'somente as Tasks selecionadas' : 'as Tasks filtradas'}.`}
	                </p>
	              </div>
	              <button
	                type="button"
	                className="icon-button"
	                onClick={() => setFilterBatchModal(null)}
	                disabled={filterBatchModal.running}
	                aria-label="Fechar"
	              >
	                <span className="material-symbols-rounded" aria-hidden="true">close</span>
	              </button>
	            </header>
	            <div className="td-filter-transfer-body">
	              {isTransferModal ? (
	                filterBatchModal.loading ? (
	                  <div className="td-filter-transfer-loading">
	                    <span className="material-symbols-rounded" aria-hidden="true">sync</span>
	                    Carregando demandas...
	                  </div>
	                ) : (
	                  <div className="td-filter-transfer-list" role="listbox" aria-label="Demandas de destino">
	                    {filterBatchModal.options.map((item) => {
	                      const selected = String(filterBatchModal.selectedTargetId) === String(item.id);
	                      return (
	                        <button
	                          type="button"
	                          className={`td-filter-transfer-item ${selected ? 'is-selected' : ''}`}
	                          key={item.id}
	                          onClick={() => setFilterBatchModal((current) => (
	                            current?.type === 'transfer'
	                              ? { ...current, selectedTargetId: String(item.id), error: '' }
	                              : current
	                          ))}
	                          disabled={filterBatchModal.running}
	                          role="option"
	                          aria-selected={selected}
	                        >
	                          <div>
	                            <strong>{demandTitle(item)}</strong>
	                            <span>{item.protocol || `Demanda ${item.id}`}</span>
	                          </div>
	                          <small>{Number(item.task_count || 0).toLocaleString('pt-BR')} task(s)</small>
	                        </button>
	                      );
	                    })}
	                  </div>
	                )
	              ) : (
	                <div className="td-filter-transfer-danger">
	                  <span className="material-symbols-rounded" aria-hidden="true">warning</span>
	                  <p>
	                    {isSelectedBatch
	                      ? `Confirme para apagar as ${selectedBatchCount.toLocaleString('pt-BR')} Tasks selecionadas. Esta ação não pode ser desfeita.`
	                      : 'Confirme para apagar somente as Tasks do filtro atual. Esta ação não pode ser desfeita.'}
	                  </p>
	                </div>
	              )}
	              {filterBatchModal.error && <div className="td-filter-transfer-error">{filterBatchModal.error}</div>}
	            </div>
	            <footer>
	              <button type="button" className="secondary-button" onClick={() => setFilterBatchModal(null)} disabled={filterBatchModal.running}>
	                Cancelar
	              </button>
	              <button type="submit" className={`primary-button ${isDeleteModal ? 'is-danger' : ''}`} disabled={filterBatchModal.running || (isTransferModal && filterBatchModal.loading)}>
	                <span className="material-symbols-rounded" aria-hidden="true">
	                  {filterBatchModal.running
	                    ? 'sync'
	                    : isDeleteModal
	                      ? 'delete'
	                      : transferMode === 'move'
	                        ? 'drive_file_move'
	                        : 'content_copy'}
	                </span>
	                {filterBatchModal.running
	                  ? 'Aplicando...'
	                  : isDeleteModal
	                    ? 'Confirmar remoção'
	                    : transferMode === 'move'
	                      ? 'Confirmar mover'
	                      : 'Confirmar cópia'}
	              </button>
	            </footer>
	          </form>
	        </div>
	      )}
	      {statusActionModal && (
	        <div
	          className="td-filter-transfer-backdrop"
	          role="presentation"
	          onClick={() => {
	            if (statusActionModal.running) return;
	            setStatusActionModal(null);
	          }}
	        >
	          <form
	            className="td-filter-transfer-modal td-filter-status-modal"
	            role="dialog"
	            aria-modal="true"
	            aria-label="Alterar status"
	            onClick={(event) => event.stopPropagation()}
	            onSubmit={(event) => {
	              event.preventDefault();
	              executeStatusAction();
	            }}
	          >
	            <header>
	              <div>
	                <span>
	                  {statusActionModal.scope === 'task'
	                    ? 'Task'
	                    : statusActionModal.scope === 'column'
	                      ? 'Coluna'
	                      : 'Status em lote'}
	                </span>
	                <strong>
	                  {statusActionModal.scope === 'task'
	                    ? `Alterar status de ${taskTitle(statusActionModal.task || {})}`
	                    : statusActionModal.scope === 'column'
	                      ? `Atualizar tasks da coluna ${statusActionModal.currentStatus}`
	                      : 'Atualizar tasks filtradas'}
	                </strong>
	                <p>
	                  {statusActionModal.scope === 'task'
	                    ? 'Escolha o novo status da task selecionada.'
	                    : statusActionModal.scope === 'column'
	                      ? 'Escolha o status de destino para as tasks desta coluna dentro da busca atual.'
	                      : 'Escolha o novo status para todas as tasks que atendem ao filtro atual.'}
	                </p>
	              </div>
	              <button
	                type="button"
	                className="icon-button"
	                onClick={() => setStatusActionModal(null)}
	                disabled={statusActionModal.running}
	                aria-label="Fechar"
	              >
	                <span className="material-symbols-rounded" aria-hidden="true">close</span>
	              </button>
	            </header>
	            <div className="td-filter-transfer-body td-filter-status-body">
	              <label className="modal-field modal-field-wide">
	                <span>Novo status</span>
	                <select
	                  value={statusActionModal.value || ''}
	                  onChange={(event) => setStatusActionModal((current) => (
	                    current ? { ...current, value: event.target.value, error: '' } : current
	                  ))}
	                  disabled={statusActionModal.running}
	                >
	                  {statusActionOptions.map((status) => (
	                    <option key={status} value={status}>{status}</option>
	                  ))}
	                </select>
	              </label>
	              {statusActionModal.error && <div className="td-filter-transfer-error">{statusActionModal.error}</div>}
	            </div>
	            <footer>
	              <button type="button" className="secondary-button" onClick={() => setStatusActionModal(null)} disabled={statusActionModal.running}>
	                Cancelar
	              </button>
	              <button type="submit" className="primary-button" disabled={statusActionModal.running}>
	                <span className="material-symbols-rounded" aria-hidden="true">
	                  {statusActionModal.running ? 'sync' : 'playlist_add_check'}
	                </span>
	                {statusActionModal.running ? 'Aplicando...' : 'Confirmar status'}
	              </button>
	            </footer>
	          </form>
	        </div>
	      )}
	    </>
	  );
};

const root = document.getElementById('tasksKanbanRoot');
if (root) {
  createRoot(root).render(<TasksKanbanApp />);
}
